import React, { useRef } from "react";
import html2canvas from "html2canvas";

// Card 1 - LinkedIn Desktop Style Professional Card (16:9 - 1920x1080px)
export const CareerStatsCard = ({ resumeData, profilePhotoUrl }) => {
  const name = resumeData?.parsedData?.personalInfo?.name || "Your Name";
  const currentTitle = resumeData?.parsedData?.experience?.[0]?.title || "Professional";
  const currentCompany = resumeData?.parsedData?.experience?.[0]?.company || "";
  const location = resumeData?.parsedData?.personalInfo?.location || "Location";
  const email = resumeData?.parsedData?.personalInfo?.email || "";
  const phone = resumeData?.parsedData?.personalInfo?.phone || "";
  const linkedin =
    resumeData?.parsedData?.personalInfo?.linkedIn || resumeData?.parsedData?.personalInfo?.linkedin || "";

  // Calculate years of experience
  const calculateYears = () => {
    if (!resumeData?.parsedData?.experience || resumeData.parsedData.experience.length === 0) {
      return 0;
    }
    return resumeData.parsedData.experience.length;
  };

  const yearsOfExperience = calculateYears();

  // Get top skills with endorsement counts (mock counts for visual)
  const allSkills =
    resumeData?.parsedData?.skills?.list ||
    Object.values(resumeData?.parsedData?.skills?.categorized || {}).flat() ||
    [];
  const topSkills = allSkills.slice(0, 8).map((skill, idx) => ({
    name: skill,
    endorsements: Math.floor(Math.random() * 50) + 5, // Mock endorsement count
  }));
  const descriptionSkills = allSkills.slice(0, 3);

  // Dynamic description
  const summaryData = resumeData?.parsedData?.summary;
  const summaryText = typeof summaryData === "string" ? summaryData : summaryData?.content || "";
  let description =
    summaryText ||
    `${currentTitle} with ${yearsOfExperience}+ years in the industry. Strong expertise in ${
      descriptionSkills[0] || "modern technologies"
    }, ${descriptionSkills[1] || "development"}, and ${
      descriptionSkills[2] || "innovation"
    }. Open to new opportunities that align with my skills and career goals.`;

  // Trim description to fit 8 lines - approximately 680 characters
  if (description.length > 680) {
    const words = description.substring(0, 680).split(" ");
    description = words.slice(0, -2).join(" ") + "...";
  }

  // Get work experience and education
  const experiences = resumeData?.parsedData?.experience || [];
  const latestExperiences = experiences.slice(0, 2);
  const education = resumeData?.parsedData?.education || [];
  const latestEducation = education.slice(0, 2);

  // Connection count (mock)
  const connectionCount = "500+";

  return (
    <div
      style={{
        width: "1920px",
        height: "1080px",
        background: "#F3F2EF",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        position: "relative",
        fontFamily:
          '-apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        overflow: "hidden",
      }}
    >
      {/* Header Section */}
      <div
        style={{
          background: "#FFFFFF",
          borderBottom: "1px solid #E0E0E0",
          padding: "12px 40px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* LinkedIn Logo */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
            <rect width="40" height="40" rx="4" fill="#0A66C2" />
            <path
              d="M14 28V17H10V28H14ZM12 15.3C13.3 15.3 14 14.5 14 13.5C13.975 12.475 13.3 11.7 12.025 11.7C10.75 11.7 10 12.475 10 13.5C10 14.5 10.725 15.3 11.975 15.3H12ZM16 28H20V21.9C20 21.55 20.025 21.2 20.125 20.95C20.4 20.25 21.05 19.525 22.125 19.525C23.55 19.525 24.125 20.6 24.125 22.2V28H28.125V21.725C28.125 18.05 26.15 16.35 23.525 16.35C21.35 16.35 20.4 17.575 19.875 18.4H20V17H16C16.05 18 16 28 16 28Z"
              fill="white"
            />
          </svg>
          <span
            style={{
              fontSize: "28px",
              fontWeight: "700",
              color: "#0A66C2",
              letterSpacing: "-0.5px",
            }}
          >
            LinkedIn
          </span>
        </div>

        {/* Connection Badge */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            background: "#0A66C2",
            padding: "10px 24px",
            borderRadius: "20px",
            color: "#FFFFFF",
            fontSize: "16px",
            fontWeight: "600",
          }}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="white">
            <path d="M8.5 7C9.88071 7 11 5.88071 11 4.5C11 3.11929 9.88071 2 8.5 2C7.11929 2 6 3.11929 6 4.5C6 5.88071 7.11929 7 8.5 7Z" />
            <path d="M14 8C15.1046 8 16 7.10457 16 6C16 4.89543 15.1046 4 14 4C12.8954 4 12 4.89543 12 6C12 7.10457 12.8954 8 14 8Z" />
            <path d="M13.5 9H14.5C16.433 9 18 10.567 18 12.5V15H12V12.5C12 11.2152 12.5564 10.0638 13.4478 9.27893C13.4794 9.25231 13.5116 9.22631 13.5443 9.20096C13.5295 9.13383 13.5147 9.06706 13.5 9Z" />
            <path d="M4 12.5C4 10.567 5.567 9 7.5 9H9.5C11.433 9 13 10.567 13 12.5V15H4V12.5Z" />
          </svg>
          {connectionCount} connections
        </div>
      </div>

      {/* Main Content */}
      <div
        style={{
          flex: 1,
          display: "flex",
          gap: "24px",
          padding: "24px 40px 40px 40px",
          overflow: "hidden",
        }}
      >
        {/* Left Column - Profile Card */}
        <div
          style={{
            width: "380px",
            background: "#FFFFFF",
            borderRadius: "8px",
            border: "1px solid #E0E0E0",
            boxShadow: "0 0 0 1px rgba(0,0,0,0.08), 0 4px 6px rgba(0,0,0,0.04)",
            display: "flex",
            flexDirection: "column",
            overflow: "visible",
          }}
        >
          {/* Banner */}
          <div
            style={{
              height: "110px",
              background: "linear-gradient(135deg, #0A66C2 0%, #004182 100%)",
              position: "relative",
            }}
          >
            {/* Profile Photo - Overlapping Banner */}
            <div
              style={{
                position: "absolute",
                bottom: "-50px",
                left: "120px",
                width: "140px",
                height: "140px",
                borderRadius: "50%",
                backgroundColor: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                border: "5px solid #FFFFFF",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              }}
            >
              {profilePhotoUrl ? (
                <img
                  src={profilePhotoUrl}
                  alt="Profile"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <div
                  style={{
                    fontSize: "48px",
                    color: "#CACCCE",
                  }}
                >
                  👤
                </div>
              )}
            </div>
          </div>

          {/* Profile Info */}
          <div
            style={{
              padding: "60px 24px 24px 24px",
              textAlign: "center",
              borderBottom: "1px solid #E0E0E0",
            }}
          >
            <div
              style={{
                fontSize: "24px",
                fontWeight: "600",
                color: "#000000",
                marginBottom: "4px",
                lineHeight: "1.2",
              }}
            >
              {name}
            </div>
            <div
              style={{
                fontSize: "16px",
                fontWeight: "400",
                color: "#00000099",
                marginBottom: "12px",
                lineHeight: "1.4",
              }}
            >
              {currentTitle}
            </div>
            <div
              style={{
                fontSize: "14px",
                color: "#00000099",
                marginBottom: "16px",
              }}
            >
              {location}
            </div>

            {/* Action Buttons */}
            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "center",
                marginTop: "16px",
              }}
            >
              <button
                style={{
                  background: "#0A66C2",
                  color: "#FFFFFF",
                  border: "none",
                  borderRadius: "24px",
                  padding: "10px 24px",
                  fontSize: "16px",
                  fontWeight: "600",
                  cursor: "pointer",
                  boxShadow: "inset 0 0 0 1px #0A66C2",
                }}
              >
                Connect
              </button>
              <button
                style={{
                  background: "#FFFFFF",
                  color: "#0A66C2",
                  border: "1px solid #0A66C2",
                  borderRadius: "24px",
                  padding: "10px 24px",
                  fontSize: "16px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Message
              </button>
            </div>
          </div>

          {/* Contact Info */}
          <div
            style={{
              padding: "20px 24px",
              borderBottom: "1px solid #E0E0E0",
            }}
          >
            <div
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#000000",
                marginBottom: "12px",
              }}
            >
              Contact Info
            </div>
            {email && (
              <div
                style={{
                  fontSize: "14px",
                  color: "#00000099",
                  marginBottom: "8px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span style={{ color: "#0A66C2" }}>✉</span>
                <span style={{ wordBreak: "break-all" }}>{email}</span>
              </div>
            )}
            {phone && (
              <div
                style={{
                  fontSize: "14px",
                  color: "#00000099",
                  marginBottom: "8px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span style={{ color: "#0A66C2" }}>📞</span>
                <span>{phone}</span>
              </div>
            )}
            {linkedin && (
              <div
                style={{
                  fontSize: "14px",
                  color: "#0A66C2",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                }}
              >
                <span>🔗</span>
                <span>{linkedin.replace(/^(https?:\/\/)?(www\.)?/, "")}</span>
              </div>
            )}
          </div>

          {/* Skills Preview */}
          <div
            style={{
              padding: "20px 24px",
              flex: 1,
              overflowY: "auto",
            }}
          >
            <div
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#000000",
                marginBottom: "12px",
              }}
            >
              Top Skills
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              {topSkills.slice(0, 5).map((skill, index) => (
                <div
                  key={index}
                  style={{
                    fontSize: "14px",
                    color: "#000000",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <span>{skill.name}</span>
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#00000099",
                    }}
                  >
                    {skill.endorsements} endorsements
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column - Main Content */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            overflowY: "auto",
            paddingRight: "8px",
          }}
        >
          {/* About Section */}
          <div
            style={{
              background: "#FFFFFF",
              borderRadius: "8px",
              border: "1px solid #E0E0E0",
              boxShadow: "0 0 0 1px rgba(0,0,0,0.08), 0 4px 6px rgba(0,0,0,0.04)",
              padding: "24px",
            }}
          >
            <div
              style={{
                fontSize: "20px",
                fontWeight: "600",
                color: "#000000",
                marginBottom: "12px",
              }}
            >
              About
            </div>
            <div
              style={{
                fontSize: "14px",
                lineHeight: "1.6",
                color: "#000000E6",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 8,
                WebkitBoxOrient: "vertical",
                wordBreak: "break-word",
              }}
            >
              {description}
            </div>
          </div>

          {/* Experience Section */}
          <div
            style={{
              background: "#FFFFFF",
              borderRadius: "8px",
              border: "1px solid #E0E0E0",
              boxShadow: "0 0 0 1px rgba(0,0,0,0.08), 0 4px 6px rgba(0,0,0,0.04)",
              padding: "24px",
            }}
          >
            <div
              style={{
                fontSize: "20px",
                fontWeight: "600",
                color: "#000000",
                marginBottom: "20px",
              }}
            >
              Experience
            </div>
            {latestExperiences.map((exp, idx) => (
              <div
                key={idx}
                style={{
                  marginBottom: idx < latestExperiences.length - 1 ? "24px" : "0",
                  paddingBottom: idx < latestExperiences.length - 1 ? "24px" : "0",
                  borderBottom: idx < latestExperiences.length - 1 ? "1px solid #E0E0E0" : "none",
                  display: "flex",
                  gap: "12px",
                }}
              >
                {/* Company Logo Placeholder */}
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    background: "#F3F2EF",
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    fontSize: "24px",
                  }}
                >
                  🏢
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#000000",
                      marginBottom: "4px",
                    }}
                  >
                    {exp.title}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#00000099",
                      marginBottom: "4px",
                    }}
                  >
                    {exp.company}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#00000099",
                      marginBottom: "8px",
                    }}
                  >
                    {exp.startDate} - {exp.endDate || "Present"}
                  </div>
                  {exp.location && (
                    <div
                      style={{
                        fontSize: "14px",
                        color: "#00000099",
                      }}
                    >
                      📍 {exp.location}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Education Section */}
          <div
            style={{
              background: "#FFFFFF",
              borderRadius: "8px",
              border: "1px solid #E0E0E0",
              boxShadow: "0 0 0 1px rgba(0,0,0,0.08), 0 4px 6px rgba(0,0,0,0.04)",
              padding: "24px",
            }}
          >
            <div
              style={{
                fontSize: "20px",
                fontWeight: "600",
                color: "#000000",
                marginBottom: "20px",
              }}
            >
              Education
            </div>
            {latestEducation.map((edu, idx) => (
              <div
                key={idx}
                style={{
                  marginBottom: idx < latestEducation.length - 1 ? "24px" : "0",
                  paddingBottom: idx < latestEducation.length - 1 ? "24px" : "0",
                  borderBottom: idx < latestEducation.length - 1 ? "1px solid #E0E0E0" : "none",
                  display: "flex",
                  gap: "12px",
                }}
              >
                {/* School Logo Placeholder */}
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    background: "#F3F2EF",
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    fontSize: "24px",
                  }}
                >
                  🎓
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#000000",
                      marginBottom: "4px",
                    }}
                  >
                    {edu.school}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#00000099",
                      marginBottom: "4px",
                    }}
                  >
                    {edu.degree || "Degree"}
                  </div>
                  {edu.endDate && (
                    <div
                      style={{
                        fontSize: "14px",
                        color: "#00000099",
                      }}
                    >
                      {edu.endDate}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          background: "#FFFFFF",
          borderTop: "1px solid #E0E0E0",
          padding: "16px 40px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div
          style={{
            fontSize: "12px",
            color: "#00000099",
          }}
        >
          © 2024 LinkedIn Corporation
        </div>
        <div
          style={{
            fontSize: "14px",
            fontWeight: "600",
            color: "#00000099",
          }}
        >
          Made with <span style={{ color: "#0A66C2" }}>upresume.ai</span>
        </div>
      </div>
    </div>
  );
};

// Card 2 - Spotify Wrapped Style Profile Card
export const SpotifyWrappedCard = ({ resumeData, profilePhotoUrl }) => {
  const name = resumeData?.parsedData?.personalInfo?.name || "Your Name";
  const currentTitle = resumeData?.parsedData?.experience?.[0]?.title || "Professional";
  const currentCompany = resumeData?.parsedData?.experience?.[0]?.company || "";

  // Calculate years of experience
  const calculateYears = () => {
    if (!resumeData?.parsedData?.experience || resumeData.parsedData.experience.length === 0) {
      return 0;
    }
    return resumeData.parsedData.experience.length;
  };

  const yearsOfExperience = calculateYears();

  // Get top skills
  const allSkills =
    resumeData?.parsedData?.skills?.list ||
    Object.values(resumeData?.parsedData?.skills?.categorized || {}).flat() ||
    [];
  const topSkills = allSkills.slice(0, 6);
  const descriptionSkills = allSkills.slice(0, 3);

  // Dynamic description
  const description = `${currentTitle} with ${yearsOfExperience}+ years in the industry. Strong expertise in ${
    descriptionSkills[0] || "modern technologies"
  }, ${descriptionSkills[1] || "development"}, and ${
    descriptionSkills[2] || "innovation"
  }. Open to new opportunities that align with my skills and career goals.`;

  // Get work experience and education
  const workExp1 = resumeData?.parsedData?.experience?.[0] || {};
  const workExp2 = resumeData?.parsedData?.experience?.[1] || {};
  const edu1 = resumeData?.parsedData?.education?.[0] || {};
  const edu2 = resumeData?.parsedData?.education?.[1] || {};

  return (
    <div
      style={{
        width: "1080px",
        minHeight: "400px",
        background: "#121212",
        display: "flex",
        flexDirection: "column",
        padding: "50px",
        boxSizing: "border-box",
        position: "relative",
        color: "white",
        fontFamily: "Circular, Helvetica, Arial, sans-serif",
        overflow: "hidden",
      }}
    >
      {/* Spotify Green Glow Accent - Top Right */}
      <div
        style={{
          position: "absolute",
          top: "-150px",
          right: "-150px",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(29, 185, 84, 0.25) 0%, rgba(29, 185, 84, 0.15) 30%, transparent 70%)",
          filter: "blur(40px)",
        }}
      />

      {/* Spotify Green Glow Accent - Bottom Left */}
      <div
        style={{
          position: "absolute",
          bottom: "-120px",
          left: "-120px",
          width: "350px",
          height: "350px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(29, 185, 84, 0.2) 0%, rgba(29, 185, 84, 0.1) 30%, transparent 70%)",
          filter: "blur(40px)",
        }}
      />

      {/* Top Row: Photo and Name */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "30px",
          marginBottom: "30px",
        }}
      >
        {/* Circular Photo */}
        <div
          style={{
            width: "150px",
            height: "150px",
            borderRadius: "50%",
            backgroundColor: "#282828",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            border: "4px solid #1DB954",
            flexShrink: 0,
            boxShadow: "0 8px 24px rgba(29, 185, 84, 0.4)",
          }}
        >
          {profilePhotoUrl ? (
            <img
              src={profilePhotoUrl}
              alt="Profile"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          ) : (
            <div
              style={{
                fontSize: "16px",
                color: "#b3b3b3",
                textAlign: "center",
              }}
            >
              Photo
            </div>
          )}
        </div>

        {/* Name and Title */}
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: "72px",
              fontWeight: "900",
              color: "white",
              lineHeight: "0.9",
              marginBottom: "15px",
              letterSpacing: "-2px",
            }}
          >
            {name}
          </div>
          <div
            style={{
              fontSize: "24px",
              fontWeight: "700",
              color: "#1DB954",
              marginBottom: "8px",
            }}
          >
            {currentTitle}
          </div>
          <div
            style={{
              fontSize: "18px",
              color: "#b3b3b3",
              fontWeight: "500",
            }}
          >
            {currentCompany}
          </div>
        </div>
      </div>

      {/* Description */}
      <div
        style={{
          fontSize: "16px",
          lineHeight: "1.6",
          color: "#b3b3b3",
          marginBottom: "35px",
          fontWeight: "400",
          backgroundColor: "#181818",
          padding: "20px",
          borderRadius: "12px",
          border: "1px solid #282828",
        }}
      >
        {description}
      </div>

      {/* Main Content: Experience and Education in one row, Skills below */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "30px",
        }}
      >
        {/* First Row: Experience and Education */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "25px",
          }}
        >
          {/* Experience Column */}
          <div>
            <div
              style={{
                fontSize: "20px",
                fontWeight: "900",
                color: "#1DB954",
                marginBottom: "18px",
                textTransform: "uppercase",
                letterSpacing: "2px",
              }}
            >
              Experience
            </div>
            {workExp1.title && (
              <div
                style={{
                  marginBottom: "20px",
                  backgroundColor: "#181818",
                  padding: "12px",
                  borderRadius: "8px",
                  border: "1px solid #282828",
                }}
              >
                <div
                  style={{
                    fontSize: "15px",
                    fontWeight: "700",
                    color: "white",
                    marginBottom: "6px",
                  }}
                >
                  {workExp1.title}
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    color: "#b3b3b3",
                  }}
                >
                  {workExp1.company}
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    color: "#7f7f7f",
                    marginTop: "4px",
                  }}
                >
                  {workExp1.startDate} - {workExp1.endDate || "Present"}
                </div>
              </div>
            )}
            {workExp2.title && (
              <div
                style={{
                  marginBottom: "20px",
                  backgroundColor: "#181818",
                  padding: "12px",
                  borderRadius: "8px",
                  border: "1px solid #282828",
                }}
              >
                <div
                  style={{
                    fontSize: "15px",
                    fontWeight: "700",
                    color: "white",
                    marginBottom: "6px",
                  }}
                >
                  {workExp2.title}
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    color: "#b3b3b3",
                  }}
                >
                  {workExp2.company}
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    color: "#7f7f7f",
                    marginTop: "4px",
                  }}
                >
                  {workExp2.startDate} - {workExp2.endDate || "Present"}
                </div>
              </div>
            )}
          </div>

          {/* Education Column */}
          <div>
            <div
              style={{
                fontSize: "20px",
                fontWeight: "900",
                color: "#1DB954",
                marginBottom: "18px",
                textTransform: "uppercase",
                letterSpacing: "2px",
              }}
            >
              Education
            </div>
            {edu1.school && (
              <div
                style={{
                  marginBottom: "20px",
                  backgroundColor: "#181818",
                  padding: "12px",
                  borderRadius: "8px",
                  border: "1px solid #282828",
                }}
              >
                <div
                  style={{
                    fontSize: "15px",
                    fontWeight: "700",
                    color: "white",
                    marginBottom: "6px",
                  }}
                >
                  {edu1.degree || "Degree"}
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    color: "#b3b3b3",
                  }}
                >
                  {edu1.school}
                </div>
                {edu1.endDate && (
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#7f7f7f",
                      marginTop: "4px",
                    }}
                  >
                    {edu1.endDate}
                  </div>
                )}
              </div>
            )}
            {edu2.school && (
              <div
                style={{
                  marginBottom: "20px",
                  backgroundColor: "#181818",
                  padding: "12px",
                  borderRadius: "8px",
                  border: "1px solid #282828",
                }}
              >
                <div
                  style={{
                    fontSize: "15px",
                    fontWeight: "700",
                    color: "white",
                    marginBottom: "6px",
                  }}
                >
                  {edu2.degree || "Degree"}
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    color: "#b3b3b3",
                  }}
                >
                  {edu2.school}
                </div>
                {edu2.endDate && (
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#7f7f7f",
                      marginTop: "4px",
                    }}
                  >
                    {edu2.endDate}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Second Row: Skills */}
        <div>
          <div
            style={{
              fontSize: "20px",
              fontWeight: "900",
              color: "#1DB954",
              marginBottom: "18px",
              textTransform: "uppercase",
              letterSpacing: "2px",
            }}
          >
            Skills
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            {topSkills.map((skill, index) => (
              <div
                key={index}
                style={{
                  backgroundColor: "#181818",
                  border: "2px solid #1DB954",
                  padding: "8px 14px",
                  borderRadius: "20px",
                  fontSize: "13px",
                  fontWeight: "700",
                  color: "#1DB954",
                }}
              >
                {skill}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Made with Upresume.ai - Bottom */}
      <div
        style={{
          marginTop: "30px",
          paddingTop: "20px",
          textAlign: "right",
          fontSize: "16px",
          fontWeight: "700",
          color: "#b3b3b3",
          letterSpacing: "0.5px",
        }}
      >
        Made with <span style={{ color: "#1DB954" }}>upresume.ai</span>
      </div>
    </div>
  );
};

// Card 3 - Netflix Style Profile Card (Landscape - No Photo)
export const NetflixStyleCard = ({ resumeData, profilePhotoUrl }) => {
  const name = resumeData?.parsedData?.personalInfo?.name || "Your Name";
  const currentTitle = resumeData?.parsedData?.experience?.[0]?.title || "Professional";
  const location = resumeData?.parsedData?.personalInfo?.location || "";
  const email = resumeData?.parsedData?.personalInfo?.email || "";
  const phone = resumeData?.parsedData?.personalInfo?.phone || "";
  const linkedin =
    resumeData?.parsedData?.personalInfo?.linkedIn || resumeData?.parsedData?.personalInfo?.linkedin || "";
  const github = resumeData?.parsedData?.personalInfo?.github || "";
  const website = resumeData?.parsedData?.personalInfo?.website || "";

  // Summary
  const summaryData = resumeData?.parsedData?.summary;
  const summaryText = typeof summaryData === "string" ? summaryData : summaryData?.content || "";
  const words = summaryText.split(/\s+/);
  let summary = summaryText;
  if (words.length > 100) {
    const sentences = summaryText.split(/[.!?]+/).filter((s) => s.trim());
    let result = "";
    for (const sentence of sentences) {
      if ((result + sentence).split(/\s+/).length <= 100) {
        result += (result ? ". " : "") + sentence;
      } else {
        break;
      }
    }
    summary = result + (result ? "." : "");
  }

  // Get data
  const experiences = resumeData?.parsedData?.experience || [];
  const latestExperiences = experiences.slice(0, 2);
  const education = resumeData?.parsedData?.education || [];
  const latestEducation = education.slice(0, 2);
  const allSkills =
    resumeData?.parsedData?.skills?.list ||
    Object.values(resumeData?.parsedData?.skills?.categorized || {}).flat() ||
    [];
  const topSkills = allSkills.slice(0, 10);
  const projects = resumeData?.parsedData?.projects || [];
  const latestProjects = projects.slice(0, 2);
  const achievements = resumeData?.parsedData?.achievements || [];
  const latestAchievement = achievements.slice(0, 1);

  return (
    <div
      style={{
        width: "1275px",
        height: "614px",
        background: "#1A1A1A",
        fontFamily: "Netflix Sans, Helvetica Neue, Arial, sans-serif",
        color: "#FFFFFF",
        overflow: "hidden",
        position: "relative",
        padding: "32px 40px",
      }}
    >
      {/* Top Row: Netflix N Logo and Contact Bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "20px",
          marginBottom: "20px",
        }}
      >
        {/* Netflix Logo - Proper SVG */}
        <div
          style={{
            background: "#000000",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "4px",
            flexShrink: 0,
            padding: "8px 12px",
          }}
        >
          <svg viewBox="0 0 1024 276.742" width="80" height="22" style={{ display: "block" }}>
            <path
              d="M140.803 258.904c-15.404 2.705-31.079 3.516-47.294 5.676l-49.458-144.856v151.073c-15.404 1.621-29.457 3.783-44.051 5.945v-276.742h41.08l56.212 157.021v-157.021h43.511v258.904zm85.131-157.558c16.757 0 42.431-.811 57.835-.811v43.24c-19.189 0-41.619 0-57.835.811v64.322c25.405-1.621 50.809-3.785 76.482-4.596v41.617l-119.724 9.461v-255.39h119.724v43.241h-76.482v58.105zm237.284-58.104h-44.862v198.908c-14.594 0-29.188 0-43.239.539v-199.447h-44.862v-43.242h132.965l-.002 43.242zm70.266 55.132h59.187v43.24h-59.187v98.104h-42.433v-239.718h120.808v43.241h-78.375v55.133zm148.641 103.507c24.594.539 49.456 2.434 73.51 3.783v42.701c-38.646-2.434-77.293-4.863-116.75-5.676v-242.689h43.24v201.881zm109.994 49.457c13.783.812 28.377 1.623 42.43 3.242v-254.58h-42.43v251.338zm231.881-251.338l-54.863 131.615 54.863 145.127c-16.217-2.162-32.432-5.135-48.648-7.838l-31.078-79.994-31.617 73.51c-15.678-2.705-30.812-3.516-46.484-5.678l55.672-126.75-50.269-129.992h46.482l28.377 72.699 30.27-72.699h47.295z"
              fill="#E50914"
            />
          </svg>
        </div>

        {/* Contact Bar */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "16px",
            fontSize: "11px",
            color: "#FFFFFF",
            flex: 1,
          }}
        >
          {location && (
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <span style={{ color: "#E50914" }}>📍</span>
              <span>{location}</span>
            </div>
          )}
          {email && (
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <span style={{ color: "#E50914" }}>✉</span>
              <span>{email}</span>
            </div>
          )}
          {phone && (
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <span style={{ color: "#E50914" }}>📞</span>
              <span>{phone}</span>
            </div>
          )}
          {linkedin && (
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <span style={{ color: "#E50914" }}>🔗</span>
              <span>{linkedin.replace(/^(https?:\/\/)?(www\.)?/, "")}</span>
            </div>
          )}
          {github && (
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <span style={{ color: "#E50914" }}>🐙</span>
              <span>{github.replace(/^(https?:\/\/)?(www\.)?/, "")}</span>
            </div>
          )}
          {website && (
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <span style={{ color: "#E50914" }}>🌐</span>
              <span>{website.replace(/^(https?:\/\/)?(www\.)?/, "")}</span>
            </div>
          )}
        </div>
      </div>

      {/* Name and Play Buttons */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "16px",
        }}
      >
        <div
          style={{
            fontSize: "42px",
            fontWeight: "700",
            color: "#E50914",
            letterSpacing: "2px",
            textTransform: "uppercase",
          }}
        >
          {name}
        </div>
        <div
          style={{
            display: "flex",
            gap: "12px",
          }}
        >
          <div
            style={{
              background: "#FFFFFF",
              color: "#000000",
              padding: "8px 20px",
              borderRadius: "4px",
              fontSize: "13px",
              fontWeight: "700",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              cursor: "pointer",
            }}
          >
            <span>▶</span>
            <span>Play</span>
          </div>
          <div
            style={{
              background: "rgba(109, 109, 110, 0.7)",
              color: "#FFFFFF",
              padding: "8px 20px",
              borderRadius: "4px",
              fontSize: "13px",
              fontWeight: "700",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              cursor: "pointer",
            }}
          >
            <span>ⓘ</span>
            <span>More Info</span>
          </div>
        </div>
      </div>

      {/* Badge and Summary Row */}
      <div
        style={{
          display: "flex",
          gap: "20px",
          marginBottom: "16px",
        }}
      >
        {/* Badge */}
        <div
          style={{
            background: "#E50914",
            padding: "8px 14px",
            borderRadius: "3px",
            fontSize: "11px",
            fontWeight: "700",
            color: "#FFFFFF",
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            height: "fit-content",
          }}
        >
          <span
            style={{
              fontSize: "16px",
              lineHeight: "1",
              fontWeight: "900",
            }}
          >
            N
          </span>
          <span style={{ lineHeight: "1" }}>#1 in Applicants Today</span>
        </div>

        {/* Summary */}
        <div
          style={{
            fontSize: "12px",
            lineHeight: "1.5",
            color: "#D0D0D0",
            flex: 1,
          }}
        >
          {summary || "Professional summary goes here..."}
        </div>
      </div>

      {/* Main Content Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "55% 45%",
          gap: "20px",
          marginTop: "16px",
        }}
      >
        {/* Left Column */}
        <div>
          {/* Education */}
          <div style={{ marginBottom: "16px" }}>
            <div
              style={{
                fontSize: "15px",
                fontWeight: "700",
                color: "#E50914",
                marginBottom: "10px",
                textTransform: "uppercase",
                letterSpacing: "1.5px",
              }}
            >
              EDUCATION:
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "12px",
              }}
            >
              {latestEducation.map((edu, idx) => (
                <div
                  key={idx}
                  style={{
                    background: "#2A2A2A",
                    padding: "12px",
                    borderRadius: "4px",
                    border: "1px solid #3A3A3A",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#FFFFFF",
                      marginBottom: "4px",
                    }}
                  >
                    {edu.degree || "Degree"}
                  </div>
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#B3B3B3",
                      marginBottom: "4px",
                    }}
                  >
                    {edu.school}
                  </div>
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#7F7F7F",
                      paddingLeft: "10px",
                      position: "relative",
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        left: "0",
                        color: "#E50914",
                      }}
                    >
                      •
                    </span>
                    {edu.location || "Location"}
                  </div>
                  {edu.endDate && (
                    <div
                      style={{
                        position: "absolute",
                        bottom: "8px",
                        right: "8px",
                        background: "#1A1A1A",
                        padding: "3px 8px",
                        borderRadius: "3px",
                        fontSize: "9px",
                        fontWeight: "600",
                        color: "#FFFFFF",
                      }}
                    >
                      {edu.endDate}
                    </div>
                  )}
                  <div
                    style={{
                      position: "absolute",
                      top: "8px",
                      right: "8px",
                      width: "22px",
                      height: "22px",
                      border: "2px solid #E50914",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "14px",
                      fontWeight: "400",
                      color: "#E50914",
                      cursor: "pointer",
                      lineHeight: "1",
                    }}
                  >
                    +
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Work Experience */}
          <div>
            <div
              style={{
                fontSize: "15px",
                fontWeight: "700",
                color: "#FFFFFF",
                marginBottom: "10px",
              }}
            >
              Explore titles related to: <span style={{ color: "#E50914" }}>Work Experience</span>
            </div>

            {/* 2 Column Grid for Experiences */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "16px",
              }}
            >
              {latestExperiences.map((exp, idx) => {
                const description = exp.description || "";
                const bulletPoints = description
                  .split("\n")
                  .filter((line) => line.trim())
                  .slice(0, 2);

                return (
                  <div
                    key={idx}
                    style={{
                      background: "#2A2A2A",
                      padding: "12px",
                      borderRadius: "4px",
                      border: "1px solid #3A3A3A",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: "8px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "12px",
                          fontWeight: "700",
                          color: "#FFFFFF",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {exp.company}
                      </div>
                      <div
                        style={{
                          fontSize: "9px",
                          color: "#B3B3B3",
                        }}
                      >
                        {exp.startDate} - {exp.endDate || "Present"}
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "6px",
                        marginBottom: "8px",
                        flexWrap: "wrap",
                      }}
                    >
                      <div
                        style={{
                          background: "#1A1A1A",
                          padding: "3px 8px",
                          borderRadius: "3px",
                          fontSize: "9px",
                          fontWeight: "600",
                          color: "#FFFFFF",
                        }}
                      >
                        {exp.title}
                      </div>
                      <div
                        style={{
                          background: "#1A1A1A",
                          padding: "3px 8px",
                          borderRadius: "3px",
                          fontSize: "9px",
                          fontWeight: "600",
                          color: "#FFFFFF",
                        }}
                      >
                        {exp.location || "Location"}
                      </div>
                    </div>
                    <div
                      style={{
                        fontSize: "9px",
                        lineHeight: "1.5",
                        color: "#D0D0D0",
                        marginBottom: "8px",
                      }}
                    >
                      {bulletPoints.map((point, pIdx) => (
                        <div
                          key={pIdx}
                          style={{
                            marginBottom: "3px",
                            paddingLeft: "10px",
                            position: "relative",
                          }}
                        >
                          <span
                            style={{
                              position: "absolute",
                              left: "0",
                              color: "#E50914",
                            }}
                          >
                            •
                          </span>
                          {point.replace(/^[•\-\*]\s*/, "")}
                        </div>
                      ))}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "6px",
                      }}
                    >
                      <div
                        style={{
                          width: "26px",
                          height: "26px",
                          borderRadius: "50%",
                          background: "rgba(229, 9, 20, 0.2)",
                          border: "1px solid #E50914",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                        }}
                      >
                        <span style={{ fontSize: "11px", color: "#FFFFFF", lineHeight: "1" }}>▶</span>
                      </div>
                      <div
                        style={{
                          width: "26px",
                          height: "26px",
                          borderRadius: "50%",
                          background: "rgba(255, 255, 255, 0.1)",
                          border: "1px solid #3A3A3A",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                        }}
                      >
                        <span style={{ fontSize: "14px", color: "#FFFFFF", lineHeight: "1", fontWeight: "400" }}>
                          +
                        </span>
                      </div>
                      <div
                        style={{
                          width: "26px",
                          height: "26px",
                          borderRadius: "50%",
                          background: "rgba(255, 255, 255, 0.1)",
                          border: "1px solid #3A3A3A",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                        }}
                      >
                        <span style={{ fontSize: "11px", lineHeight: "1" }}>👍</span>
                      </div>
                      <div
                        style={{
                          width: "26px",
                          height: "26px",
                          borderRadius: "50%",
                          background: "rgba(255, 255, 255, 0.1)",
                          border: "1px solid #3A3A3A",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                        }}
                      >
                        <span style={{ fontSize: "11px", lineHeight: "1" }}>💡</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Column - Projects and Achievements */}
        <div>
          {/* Projects */}
          <div style={{ marginBottom: "16px" }}>
            <div
              style={{
                fontSize: "15px",
                fontWeight: "700",
                color: "#E50914",
                marginBottom: "10px",
                textTransform: "uppercase",
                letterSpacing: "1.5px",
              }}
            >
              ACADEMIC PROJECTS
            </div>
            {latestProjects.length > 0 ? (
              latestProjects.map((project, idx) => (
                <div
                  key={idx}
                  style={{
                    background: "#2A2A2A",
                    padding: "12px",
                    borderRadius: "4px",
                    marginBottom: "10px",
                    border: "1px solid #3A3A3A",
                  }}
                >
                  <div
                    style={{
                      fontSize: "11px",
                      fontWeight: "700",
                      color: "#FFFFFF",
                      marginBottom: "6px",
                    }}
                  >
                    {project.name}
                  </div>
                  <div
                    style={{
                      fontSize: "9px",
                      color: "#B3B3B3",
                      lineHeight: "1.5",
                      marginBottom: "6px",
                      paddingLeft: "10px",
                      position: "relative",
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        left: "0",
                        color: "#E50914",
                      }}
                    >
                      •
                    </span>
                    {project.description
                      ? project.description.replace(/^[•\-\*]\s*/, "").substring(0, 150) + "..."
                      : ""}
                  </div>
                  {project.technologies && (
                    <div
                      style={{
                        fontSize: "8px",
                        color: "#7F7F7F",
                      }}
                    >
                      Tech:{" "}
                      {Array.isArray(project.technologies)
                        ? project.technologies.join(", ")
                        : project.technologies}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div
                style={{
                  background: "#2A2A2A",
                  padding: "20px",
                  borderRadius: "4px",
                  border: "1px solid #3A3A3A",
                  textAlign: "center",
                  color: "#666666",
                  fontSize: "11px",
                }}
              >
                No projects available
              </div>
            )}
          </div>

          {/* Achievements */}
          {latestAchievement.length > 0 && (
            <div>
              <div
                style={{
                  fontSize: "15px",
                  fontWeight: "700",
                  color: "#E50914",
                  marginBottom: "10px",
                  textTransform: "uppercase",
                  letterSpacing: "1.5px",
                }}
              >
                ACHIEVEMENTS
              </div>
              {latestAchievement.map((achievement, idx) => (
                <div
                  key={idx}
                  style={{
                    background: "#2A2A2A",
                    padding: "12px",
                    borderRadius: "4px",
                    border: "1px solid #3A3A3A",
                  }}
                >
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#FFFFFF",
                      lineHeight: "1.5",
                      marginBottom: "4px",
                      paddingLeft: "10px",
                      position: "relative",
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        left: "0",
                        color: "#E50914",
                      }}
                    >
                      •
                    </span>
                    {typeof achievement === "string"
                      ? achievement
                      : achievement.description || achievement.title || ""}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          position: "absolute",
          bottom: "12px",
          right: "20px",
          fontSize: "10px",
          fontWeight: "600",
          color: "#666666",
        }}
      >
        Made with <span style={{ color: "#E50914" }}>upresume.ai</span>
      </div>
    </div>
  );
};

// Card 4 - GitHub Style Developer Card
export const GitHubStyleCard = ({ resumeData, profilePhotoUrl }) => {
  const name = resumeData?.parsedData?.personalInfo?.name || "Your Name";
  const currentTitle = resumeData?.parsedData?.experience?.[0]?.title || "Professional";
  const currentCompany = resumeData?.parsedData?.experience?.[0]?.company || "";

  // Calculate years of experience
  const calculateYears = () => {
    if (!resumeData?.parsedData?.experience || resumeData.parsedData.experience.length === 0) {
      return 0;
    }
    return resumeData.parsedData.experience.length;
  };

  const yearsOfExperience = calculateYears();

  // Get top skills
  const allSkills =
    resumeData?.parsedData?.skills?.list ||
    Object.values(resumeData?.parsedData?.skills?.categorized || {}).flat() ||
    [];
  const topSkills = allSkills.slice(0, 8);
  const descriptionSkills = allSkills.slice(0, 3);

  // Dynamic bio
  const bio = `${currentTitle} with ${yearsOfExperience}+ years in the industry. Strong expertise in ${
    descriptionSkills[0] || "modern technologies"
  }, ${descriptionSkills[1] || "development"}, and ${
    descriptionSkills[2] || "innovation"
  }. Open to new opportunities.`;

  // Get work experience and education
  const workExp1 = resumeData?.parsedData?.experience?.[0] || {};
  const workExp2 = resumeData?.parsedData?.experience?.[1] || {};
  const edu1 = resumeData?.parsedData?.education?.[0] || {};
  const edu2 = resumeData?.parsedData?.education?.[1] || {};

  return (
    <div
      style={{
        width: "1080px",
        minHeight: "400px",
        background: "#0D1117",
        display: "flex",
        flexDirection: "column",
        padding: "50px",
        boxSizing: "border-box",
        position: "relative",
        color: "#C9D1D9",
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
        overflow: "hidden",
      }}
    >
      {/* GitHub Green Glow */}
      <div
        style={{
          position: "absolute",
          top: "-100px",
          right: "-100px",
          width: "300px",
          height: "300px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(35, 134, 54, 0.2) 0%, transparent 70%)",
          filter: "blur(40px)",
        }}
      />

      {/* Profile Section */}
      <div
        style={{
          display: "flex",
          gap: "30px",
          marginBottom: "30px",
          alignItems: "flex-start",
        }}
      >
        {/* Profile Photo */}
        <div
          style={{
            width: "130px",
            height: "130px",
            borderRadius: "50%",
            backgroundColor: "#161B22",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            border: "2px solid #30363D",
            flexShrink: 0,
          }}
        >
          {profilePhotoUrl ? (
            <img
              src={profilePhotoUrl}
              alt="Profile"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          ) : (
            <div
              style={{
                fontSize: "16px",
                color: "#8B949E",
                textAlign: "center",
              }}
            >
              Photo
            </div>
          )}
        </div>

        {/* Name and Info */}
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: "32px",
              fontWeight: "600",
              color: "#C9D1D9",
              marginBottom: "8px",
            }}
          >
            {name}
          </div>
          <div
            style={{
              fontSize: "20px",
              fontWeight: "300",
              color: "#8B949E",
              marginBottom: "16px",
            }}
          >
            @{name.toLowerCase().replace(/\s+/g, "")}
          </div>
          <div
            style={{
              fontSize: "16px",
              lineHeight: "1.6",
              color: "#C9D1D9",
              marginBottom: "16px",
            }}
          >
            {bio}
          </div>
          <div
            style={{
              fontSize: "16px",
              color: "#8B949E",
            }}
          >
            💼 {currentTitle} • {currentCompany}
          </div>
        </div>
      </div>

      {/* Pinned Repositories Style - Experience & Education */}
      <div
        style={{
          marginBottom: "30px",
        }}
      >
        <div
          style={{
            fontSize: "16px",
            fontWeight: "600",
            color: "#C9D1D9",
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span>📌</span> Pinned Experience
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "16px",
          }}
        >
          {/* Experience Item 1 */}
          {workExp1.title && (
            <div
              style={{
                backgroundColor: "#161B22",
                border: "1px solid #30363D",
                borderRadius: "6px",
                padding: "16px",
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#58A6FF",
                  marginBottom: "8px",
                }}
              >
                {workExp1.title}
              </div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#8B949E",
                  marginBottom: "8px",
                }}
              >
                {workExp1.company}
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#8B949E",
                }}
              >
                📅 {workExp1.startDate} - {workExp1.endDate || "Present"}
              </div>
            </div>
          )}

          {/* Experience Item 2 */}
          {workExp2.title && (
            <div
              style={{
                backgroundColor: "#161B22",
                border: "1px solid #30363D",
                borderRadius: "6px",
                padding: "16px",
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#58A6FF",
                  marginBottom: "8px",
                }}
              >
                {workExp2.title}
              </div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#8B949E",
                  marginBottom: "8px",
                }}
              >
                {workExp2.company}
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#8B949E",
                }}
              >
                📅 {workExp2.startDate} - {workExp2.endDate || "Present"}
              </div>
            </div>
          )}

          {/* Education Item 1 */}
          {edu1.school && (
            <div
              style={{
                backgroundColor: "#161B22",
                border: "1px solid #30363D",
                borderRadius: "6px",
                padding: "16px",
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#58A6FF",
                  marginBottom: "8px",
                }}
              >
                🎓 {edu1.degree || "Degree"}
              </div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#8B949E",
                }}
              >
                {edu1.school}
              </div>
            </div>
          )}

          {/* Education Item 2 */}
          {edu2.school && (
            <div
              style={{
                backgroundColor: "#161B22",
                border: "1px solid #30363D",
                borderRadius: "6px",
                padding: "16px",
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#58A6FF",
                  marginBottom: "8px",
                }}
              >
                🎓 {edu2.degree || "Degree"}
              </div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#8B949E",
                }}
              >
                {edu2.school}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Languages/Skills Section */}
      <div>
        <div
          style={{
            fontSize: "16px",
            fontWeight: "600",
            color: "#C9D1D9",
            marginBottom: "16px",
          }}
        >
          Languages & Skills
        </div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          {topSkills.map((skill, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <div
                style={{
                  width: "12px",
                  height: "12px",
                  borderRadius: "50%",
                  backgroundColor: "#238636",
                }}
              />
              <div
                style={{
                  fontSize: "14px",
                  color: "#C9D1D9",
                  fontFamily: '"SF Mono", Monaco, "Courier New", monospace',
                }}
              >
                {skill}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Contribution Graph Style Decoration */}
      <div
        style={{
          marginTop: "30px",
          display: "grid",
          gridTemplateColumns: "repeat(10, 1fr)",
          gap: "4px",
          width: "fit-content",
        }}
      >
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            style={{
              width: "12px",
              height: "12px",
              backgroundColor: i % 4 === 0 ? "#238636" : i % 3 === 0 ? "#26A641" : "#0E4429",
              borderRadius: "2px",
            }}
          />
        ))}
      </div>

      {/* Made with Upresume.ai - Bottom */}
      <div
        style={{
          marginTop: "20px",
          paddingTop: "20px",
          textAlign: "right",
          fontSize: "14px",
          fontWeight: "600",
          color: "#8B949E",
        }}
      >
        Made with <span style={{ color: "#238636" }}>upresume.ai</span>
      </div>
    </div>
  );
};

// Card 5 - Spotify Desktop Style Card
export const SpotifyDesktopCard = ({ resumeData, profilePhotoUrl }) => {
  const name = resumeData?.parsedData?.personalInfo?.name || "Your Name";
  // Summary can be object {content, aiAnalysis} or string
  const summaryData = resumeData?.parsedData?.summary;
  const summaryText =
    typeof summaryData === "string" ? summaryData : summaryData?.content || "Professional summary goes here...";

  // Limit to 30 words and add "..."
  const words = summaryText.split(/\s+/);
  let summary = summaryText;
  if (words.length > 30) {
    summary = words.slice(0, 30).join(" ") + "...";
  }
  const email = resumeData?.parsedData?.personalInfo?.email || "";
  const phone = resumeData?.parsedData?.personalInfo?.phone || "";
  const linkedin =
    resumeData?.parsedData?.personalInfo?.linkedIn || resumeData?.parsedData?.personalInfo?.linkedin || "";
  const github = resumeData?.parsedData?.personalInfo?.github || "";
  const location = resumeData?.parsedData?.personalInfo?.location || "";

  // Get skills (latest 10)
  const allSkills =
    resumeData?.parsedData?.skills?.list ||
    Object.values(resumeData?.parsedData?.skills?.categorized || {}).flat() ||
    [];
  const latestSkills = allSkills.slice(0, 10);

  // Get experience (latest 2)
  const experiences = resumeData?.parsedData?.experience || [];
  const latestExperiences = experiences.slice(0, 2);
  const latestRole = experiences[0]?.title || "";
  const latestCompany = experiences[0]?.company || "";
  const roleAtCompany = latestRole && latestCompany ? `${latestRole} at ${latestCompany}` : latestRole || "";

  // Get education (latest 2)
  const education = resumeData?.parsedData?.education || [];
  const latestEducation = education.slice(0, 2);

  // Get projects (latest 2)
  const projects = resumeData?.parsedData?.projects || [];
  const latestProjects = projects.slice(0, 2);

  // Helper to get month abbreviation from date
  const getMonthAbbr = (dateStr) => {
    if (!dateStr || dateStr === "Present") return "";
    const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
    const date = new Date(dateStr);
    return months[date.getMonth()] || dateStr.split(" ")[0]?.slice(0, 3)?.toUpperCase() || "";
  };

  const getYear = (dateStr) => {
    if (!dateStr || dateStr === "Present") return dateStr;
    const year = new Date(dateStr).getFullYear();
    return isNaN(year) ? dateStr.split(" ")[1] || dateStr : year.toString().slice(-2);
  };

  return (
    <div
      style={{
        width: "1275px",
        height: "614px",
        background: "linear-gradient(180deg, #000000 0%, #121212 100%)",
        display: "flex",
        fontFamily: 'Circular, "Helvetica Neue", Helvetica, Arial, sans-serif',
        color: "#FFFFFF",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Left Panel */}
      <div
        style={{
          width: "235px",
          height: "100%",
          background: "#000000",
          padding: "24px 24px 0 24px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Spotify Logo and Name */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "24px",
          }}
        >
          <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
            <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
          </svg>
          <span
            style={{
              fontSize: "18px",
              fontWeight: "700",
              letterSpacing: "-0.5px",
            }}
          >
            Spotify
          </span>
        </div>

        {/* Navigation Items */}
        <div style={{ marginBottom: "28px" }}>
          {/* Home */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "10px",
              fontSize: "13px",
              fontWeight: "400",
              color: "#B3B3B3",
              cursor: "pointer",
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
            <span>Home</span>
          </div>

          {/* Search */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "10px",
              fontSize: "13px",
              fontWeight: "400",
              color: "#B3B3B3",
              cursor: "pointer",
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <span>Search</span>
          </div>

          {/* Your Library */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "10px",
              fontSize: "13px",
              fontWeight: "400",
              color: "#B3B3B3",
              cursor: "pointer",
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 3v18h18" />
              <path d="M9 3v18" />
              <path d="M15 3v18" />
            </svg>
            <span>Your Library</span>
          </div>
        </div>

        {/* Skills Section */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            paddingBottom: "24px",
          }}
        >
          <div
            style={{
              fontSize: "11px",
              fontWeight: "700",
              color: "#B3B3B3",
              letterSpacing: "1.5px",
              marginBottom: "12px",
            }}
          >
            SKILLS
          </div>
          {latestSkills.map((skill, idx) => (
            <div
              key={idx}
              style={{
                fontSize: "13px",
                fontWeight: "400",
                color: "#B3B3B3",
                marginBottom: "10px",
                cursor: "pointer",
                transition: "color 0.2s",
              }}
            >
              {skill}
            </div>
          ))}
        </div>
      </div>

      {/* Right Panel */}
      <div
        style={{
          flex: 1,
          height: "100%",
          background: "linear-gradient(180deg, #1F1F1F 0%, #121212 100%)",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {/* Top Navigation Bar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            padding: "8px 32px",
            background: "rgba(0,0,0,0.3)",
          }}
        >
          {/* Navigation Arrows */}
          <div
            style={{
              display: "flex",
              gap: "8px",
            }}
          >
            <div
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                background: "rgba(0,0,0,0.7)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "18px",
                cursor: "pointer",
              }}
            >
              ‹
            </div>
            <div
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                background: "rgba(0,0,0,0.7)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "18px",
                cursor: "pointer",
              }}
            >
              ›
            </div>
          </div>

          {/* Play Button - After Arrows */}
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              background: "#1DB954",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="black">
              <path d="M3 2v12l10-6z" />
            </svg>
          </div>

          {/* User Name - After Play Button */}
          <div
            style={{
              fontSize: "14px",
              fontWeight: "700",
              color: "#FFFFFF",
              background: "rgba(0,0,0,0.7)",
              padding: "8px 16px",
              borderRadius: "23px",
            }}
          >
            {name}
          </div>

          {/* Latest Work Experience Role */}
          {roleAtCompany && (
            <div
              style={{
                fontSize: "13px",
                fontWeight: "400",
                color: "#B3B3B3",
                marginLeft: "8px",
              }}
            >
              {roleAtCompany}
            </div>
          )}
        </div>

        {/* Main Content Area */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "24px 32px 80px 32px",
          }}
        >
          {/* Row 2: About (65%) and Artist Pick (35%) */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "65% 35%",
              gap: "20px",
              marginBottom: "24px",
            }}
          >
            {/* About Section */}
            <div>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  color: "#FFFFFF",
                  marginBottom: "12px",
                }}
              >
                About
              </div>
              <div
                style={{
                  fontSize: "14px",
                  lineHeight: "1.6",
                  color: "#B3B3B3",
                }}
              >
                {summary}
              </div>
            </div>

            {/* Projects Section */}
            <div>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  color: "#FFFFFF",
                  marginBottom: "12px",
                }}
              >
                Projects
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                {latestProjects.map((project, idx) => (
                  <div
                    key={idx}
                    style={{
                      fontSize: "14px",
                      color: "#B3B3B3",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "8px",
                    }}
                  >
                    <span style={{ color: "#1DB954", fontSize: "16px" }}>♥</span>
                    <span style={{ color: "#FFFFFF", fontWeight: "600" }}>{project.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Row 3: Experience (50%) and Education (50%) */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "20px",
            }}
          >
            {/* Experience Section */}
            <div>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  color: "#FFFFFF",
                  marginBottom: "16px",
                }}
              >
                Experience
              </div>
              {latestExperiences.map((exp, idx) => {
                // Get description and split into bullet points (max 2)
                const description = exp.description || "";
                const bulletPoints = description
                  .split("\n")
                  .filter((line) => line.trim())
                  .slice(0, 2);

                return (
                  <div
                    key={idx}
                    style={{
                      marginBottom: "20px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#FFFFFF",
                        marginBottom: "4px",
                      }}
                    >
                      {exp.title}
                    </div>
                    <div
                      style={{
                        fontSize: "13px",
                        color: "#B3B3B3",
                        marginBottom: "8px",
                      }}
                    >
                      {exp.company} • {exp.startDate} - {exp.endDate || "Present"}
                    </div>
                    {bulletPoints.map((point, respIdx) => (
                      <div
                        key={respIdx}
                        style={{
                          fontSize: "12px",
                          color: "#B3B3B3",
                          marginBottom: "4px",
                          display: "flex",
                          gap: "8px",
                          alignItems: "flex-start",
                        }}
                      >
                        <span style={{ color: "#1DB954", fontSize: "14px" }}>♥</span>
                        <span style={{ flex: 1 }}>{point.replace(/^[•\-\*]\s*/, "")}</span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>

            {/* Education Section */}
            <div>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  color: "#FFFFFF",
                  marginBottom: "16px",
                }}
              >
                Education
              </div>
              {latestEducation.map((edu, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    gap: "16px",
                    marginBottom: "16px",
                  }}
                >
                  {/* Calendar Date */}
                  <div
                    style={{
                      width: "56px",
                      height: "56px",
                      background: "rgba(255,255,255,0.1)",
                      borderRadius: "8px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid rgba(255,255,255,0.1)",
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        fontSize: "10px",
                        fontWeight: "700",
                        color: "#1DB954",
                        letterSpacing: "0.5px",
                      }}
                    >
                      {getMonthAbbr(edu.endDate || edu.startDate)}
                    </div>
                    <div
                      style={{
                        fontSize: "20px",
                        fontWeight: "700",
                        color: "#FFFFFF",
                        lineHeight: "1",
                      }}
                    >
                      {getYear(edu.endDate || edu.startDate)}
                    </div>
                  </div>

                  {/* Education Details */}
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#FFFFFF",
                        marginBottom: "4px",
                      }}
                    >
                      {edu.school}
                    </div>
                    <div
                      style={{
                        fontSize: "13px",
                        color: "#B3B3B3",
                        marginBottom: "2px",
                      }}
                    >
                      {edu.degree || "Degree"}
                    </div>
                    {edu.startDate && edu.endDate && (
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#7F7F7F",
                        }}
                      >
                        {edu.startDate} - {edu.endDate}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Footer Player Bar - Full Width */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "72px",
          background: "#181818",
          borderTop: "1px solid #282828",
          display: "flex",
          alignItems: "center",
          padding: "0 16px",
          justifyContent: "space-between",
        }}
      >
        {/* Left: Song Info */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            flex: 1,
          }}
        >
          {/* Album Art */}
          <div
            style={{
              width: "56px",
              height: "56px",
              borderRadius: "4px",
              background: "#282828",
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            {profilePhotoUrl ? (
              <img
                src={profilePhotoUrl}
                alt="Profile"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#B3B3B3",
                  fontSize: "10px",
                }}
              >
                Photo
              </div>
            )}
          </div>

          {/* Song Details */}
          <div>
            <div
              style={{
                fontSize: "14px",
                fontWeight: "400",
                color: "#FFFFFF",
                marginBottom: "2px",
              }}
            >
              Resume
            </div>
            <div
              style={{
                fontSize: "11px",
                color: "#B3B3B3",
              }}
            >
              {name}
            </div>
          </div>

          {/* Heart Icon */}
          <div
            style={{
              fontSize: "16px",
              color: "#B3B3B3",
              cursor: "pointer",
              marginLeft: "12px",
            }}
          >
            ♡
          </div>
        </div>

        {/* Center: Player Controls */}
        <div
          style={{
            flex: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {/* Control Buttons */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
            }}
          >
            {/* Shuffle */}
            <svg width="16" height="16" viewBox="0 0 16 16" fill="#B3B3B3" style={{ cursor: "pointer" }}>
              <path d="M13.151.922a.75.75 0 1 0-1.06 1.06L13.109 3H11.16a3.75 3.75 0 0 0-2.873 1.34l-6.173 7.356A2.25 2.25 0 0 1 .39 12.5H0V14h.391a3.75 3.75 0 0 0 2.873-1.34l6.173-7.356a2.25 2.25 0 0 1 1.724-.804h1.947l-1.017 1.018a.75.75 0 0 0 1.06 1.06L15.98 3.75 13.15.922zM.391 3.5H0V2h.391c1.109 0 2.16.49 2.873 1.34L4.89 5.277l-.979 1.167-1.796-2.14A2.25 2.25 0 0 0 .39 3.5z" />
              <path d="m7.5 10.723.98-1.167.957 1.14a2.25 2.25 0 0 0 1.724.804h1.947l-1.017-1.018a.75.75 0 1 1 1.06-1.06l2.829 2.828-2.829 2.828a.75.75 0 1 1-1.06-1.06L13.109 13H11.16a3.75 3.75 0 0 1-2.873-1.34l-.787-.938z" />
            </svg>

            {/* Previous */}
            <svg width="16" height="16" viewBox="0 0 16 16" fill="#B3B3B3" style={{ cursor: "pointer" }}>
              <path d="M3.3 1a.7.7 0 0 1 .7.7v5.15l9.95-5.744a.7.7 0 0 1 1.05.606v12.575a.7.7 0 0 1-1.05.607L4 9.149V14.3a.7.7 0 0 1-.7.7H1.7a.7.7 0 0 1-.7-.7V1.7a.7.7 0 0 1 .7-.7h1.6z" />
            </svg>

            {/* Play/Pause */}
            <div
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                background: "#FFFFFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <svg width="12" height="12" viewBox="0 0 16 16" fill="black">
                <path d="M3 1.713a.7.7 0 0 1 1.05-.607l10.89 6.288a.7.7 0 0 1 0 1.212L4.05 14.894A.7.7 0 0 1 3 14.288V1.713z" />
              </svg>
            </div>

            {/* Next */}
            <svg width="16" height="16" viewBox="0 0 16 16" fill="#B3B3B3" style={{ cursor: "pointer" }}>
              <path d="M12.7 1a.7.7 0 0 0-.7.7v5.15L2.05 1.107A.7.7 0 0 0 1 1.712v12.575a.7.7 0 0 0 1.05.607L12 9.149V14.3a.7.7 0 0 0 .7.7h1.6a.7.7 0 0 0 .7-.7V1.7a.7.7 0 0 0-.7-.7h-1.6z" />
            </svg>

            {/* Repeat */}
            <svg width="16" height="16" viewBox="0 0 16 16" fill="#B3B3B3" style={{ cursor: "pointer" }}>
              <path d="M0 4.75A3.75 3.75 0 0 1 3.75 1h8.5A3.75 3.75 0 0 1 16 4.75v5a3.75 3.75 0 0 1-3.75 3.75H9.81l1.018 1.018a.75.75 0 1 1-1.06 1.06L6.939 12.75l2.829-2.828a.75.75 0 1 1 1.06 1.06L9.811 12h2.439a2.25 2.25 0 0 0 2.25-2.25v-5a2.25 2.25 0 0 0-2.25-2.25h-8.5A2.25 2.25 0 0 0 1.5 4.75v5A2.25 2.25 0 0 0 3.75 12H5v1.5H3.75A3.75 3.75 0 0 1 0 9.75v-5z" />
            </svg>
          </div>

          {/* Progress Bar */}
          <div
            style={{
              width: "100%",
              maxWidth: "500px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <span style={{ fontSize: "11px", color: "#B3B3B3" }}>2:21</span>
            <div
              style={{
                flex: 1,
                height: "4px",
                background: "#4D4D4D",
                borderRadius: "2px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: "70%",
                  height: "100%",
                  background: "#FFFFFF",
                }}
              />
            </div>
            <span style={{ fontSize: "11px", color: "#B3B3B3" }}>2:56</span>
          </div>
        </div>

        {/* Right: Made with */}
        <div
          style={{
            flex: 1,
            textAlign: "right",
            fontSize: "12px",
            color: "#B3B3B3",
          }}
        >
          Made with <span style={{ color: "#1DB954", fontWeight: "700" }}>upresume.ai</span>
        </div>
      </div>
    </div>
  );
};

// Card 6 - Instagram Mobile Profile Style (9:16 - 1080x1920px)
export const InstagramStoryCard = ({ resumeData, profilePhotoUrl }) => {
  const name = resumeData?.parsedData?.personalInfo?.name || "Your Name";
  const currentTitle = resumeData?.parsedData?.experience?.[0]?.title || "Professional";
  const currentCompany = resumeData?.parsedData?.experience?.[0]?.company || "";
  const location = resumeData?.parsedData?.personalInfo?.location || "Location";
  const email = resumeData?.parsedData?.personalInfo?.email || "";
  const phone = resumeData?.parsedData?.personalInfo?.phone || "";
  const username = name.toLowerCase().replace(/\s+/g, "");

  // Get skills
  const allSkills =
    resumeData?.parsedData?.skills?.list ||
    Object.values(resumeData?.parsedData?.skills?.categorized || {}).flat() ||
    [];
  const topSkills = allSkills.slice(0, 6);

  // Calculate stats
  const calculateYears = () => {
    if (!resumeData?.parsedData?.experience || resumeData.parsedData.experience.length === 0) {
      return 0;
    }
    return resumeData.parsedData.experience.length;
  };
  const yearsOfExperience = calculateYears();
  const experienceCount = resumeData?.parsedData?.experience?.length || 0;
  const educationCount = resumeData?.parsedData?.education?.length || 0;
  const projectsCount = resumeData?.parsedData?.projects?.length || 0;
  const certificationsCount = resumeData?.parsedData?.certifications?.length || 0;
  const totalPosts = experienceCount + projectsCount + certificationsCount;

  // Bio - Create 2 line summary (max 20 words)
  const summaryData = resumeData?.parsedData?.summary;
  const summaryText = typeof summaryData === "string" ? summaryData : summaryData?.content || "";
  let bio =
    summaryText ||
    `${currentTitle} with ${yearsOfExperience}+ years of experience in ${
      topSkills[0] || "technology"
    }. Passionate about innovation and growth.`;

  // Trim to approximately 20 words (2 lines)
  const words = bio.split(" ");
  if (words.length > 20) {
    bio = words.slice(0, 20).join(" ") + "...";
  }

  // Education
  const education = resumeData?.parsedData?.education?.[0];
  const educationList = resumeData?.parsedData?.education || [];
  const experiences = resumeData?.parsedData?.experience || [];
  const projects = resumeData?.parsedData?.projects || [];

  return (
    <div
      style={{
        width: "1080px",
        height: "1920px",
        background: "#FFFFFF",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        position: "relative",
        fontFamily:
          '-apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        overflow: "hidden",
      }}
    >
      {/* Top Header Bar */}
      <div
        style={{
          padding: "50px 40px 20px 40px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #DBDBDB",
        }}
      >
        {/* Back Arrow */}
        <div
          style={{
            fontSize: "44px",
            color: "#262626",
            fontWeight: "300",
            cursor: "pointer",
          }}
        >
          ‹
        </div>

        {/* Username */}
        <div
          style={{
            fontSize: "32px",
            fontWeight: "600",
            color: "#262626",
          }}
        >
          {username}
        </div>

        {/* Notification Bell and Three Dots */}
        <div
          style={{
            display: "flex",
            gap: "28px",
            alignItems: "center",
          }}
        >
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#262626"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
          </svg>
          <div
            style={{
              fontSize: "36px",
              color: "#262626",
              fontWeight: "700",
              letterSpacing: "2px",
            }}
          >
            ⋯
          </div>
        </div>
      </div>

      {/* Profile Section */}
      <div
        style={{
          padding: "32px 40px",
          borderBottom: "1px solid #DBDBDB",
        }}
      >
        {/* Profile Photo and Stats Row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "60px",
            marginBottom: "32px",
          }}
        >
          {/* Profile Photo with Gradient Ring */}
          <div
            style={{
              position: "relative",
              width: "200px",
              height: "200px",
              flexShrink: 0,
            }}
          >
            <div
              style={{
                width: "200px",
                height: "200px",
                borderRadius: "50%",
                background: "linear-gradient(45deg, #833AB4, #C13584, #FD1D1D, #F77737)",
                padding: "5px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  background: "#FFFFFF",
                  padding: "5px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%",
                    background: profilePhotoUrl ? `url(${profilePhotoUrl})` : "#E1E8ED",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "80px",
                    fontWeight: "700",
                    color: "#657786",
                    overflow: "hidden",
                  }}
                >
                  {!profilePhotoUrl && name.charAt(0).toUpperCase()}
                </div>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div
            style={{
              flex: 1,
              display: "flex",
              justifyContent: "space-around",
              gap: "40px",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <div
                style={{
                  fontSize: "40px",
                  fontWeight: "600",
                  color: "#262626",
                }}
              >
                {totalPosts}
              </div>
              <div
                style={{
                  fontSize: "28px",
                  color: "#262626",
                }}
              >
                Posts
              </div>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <div
                style={{
                  fontSize: "40px",
                  fontWeight: "600",
                  color: "#262626",
                }}
              >
                {experienceCount * 125 + projectsCount * 50}
              </div>
              <div
                style={{
                  fontSize: "28px",
                  color: "#262626",
                }}
              >
                Followers
              </div>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <div
                style={{
                  fontSize: "40px",
                  fontWeight: "600",
                  color: "#262626",
                }}
              >
                {educationCount * 80 + certificationsCount * 30}
              </div>
              <div
                style={{
                  fontSize: "28px",
                  color: "#262626",
                }}
              >
                Following
              </div>
            </div>
          </div>
        </div>

        {/* Name and Bio */}
        <div
          style={{
            marginBottom: "28px",
          }}
        >
          <div
            style={{
              fontSize: "28px",
              fontWeight: "600",
              color: "#262626",
              marginBottom: "12px",
            }}
          >
            {name}
          </div>
          <div
            style={{
              fontSize: "28px",
              color: "#262626",
              marginBottom: "8px",
            }}
          >
            {currentTitle} {currentCompany && `at ${currentCompany}`}
          </div>
          <div
            style={{
              fontSize: "26px",
              color: "#262626",
              lineHeight: "1.5",
            }}
          >
            {bio}
          </div>
          {location && (
            <div
              style={{
                fontSize: "26px",
                color: "#8E8E8E",
                marginTop: "8px",
              }}
            >
              📍 {location}
            </div>
          )}

          {/* Followed by Section */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginTop: "16px",
            }}
          >
            {/* Profile Pictures */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  background: "#4A90E2",
                  border: "2px solid #FFFFFF",
                  zIndex: 3,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#FFFFFF",
                }}
              >
                JD
              </div>
              <div
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  background: "#E94B3C",
                  border: "2px solid #FFFFFF",
                  marginLeft: "-12px",
                  zIndex: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#FFFFFF",
                }}
              >
                SM
              </div>
              <div
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  background: "#6C5CE7",
                  border: "2px solid #FFFFFF",
                  marginLeft: "-12px",
                  zIndex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#FFFFFF",
                }}
              >
                AK
              </div>
            </div>

            {/* Text */}
            <div
              style={{
                fontSize: "24px",
                color: "#262626",
              }}
            >
              Followed by <span style={{ fontWeight: "600" }}>recruiters</span>,{" "}
              <span style={{ fontWeight: "600" }}>hiring_managers</span> and{" "}
              <span style={{ fontWeight: "600" }}>{experienceCount + projectsCount} others</span>
            </div>
          </div>
        </div>

        {/* Following, Message, and Suggest Buttons */}
        <div
          style={{
            display: "flex",
            gap: "16px",
            marginBottom: "32px",
          }}
        >
          <div
            style={{
              flex: 1,
              padding: "16px 20px",
              background: "#EFEFEF",
              borderRadius: "10px",
              textAlign: "center",
              fontSize: "26px",
              fontWeight: "600",
              color: "#262626",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
            }}
          >
            Following
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path
                d="M6 9l6 6 6-6"
                stroke="#262626"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div
            style={{
              flex: 1,
              padding: "16px 20px",
              background: "#EFEFEF",
              borderRadius: "10px",
              textAlign: "center",
              fontSize: "26px",
              fontWeight: "600",
              color: "#262626",
            }}
          >
            Message
          </div>
          <div
            style={{
              width: "90px",
              padding: "16px",
              background: "#EFEFEF",
              borderRadius: "10px",
              textAlign: "center",
              fontSize: "24px",
              fontWeight: "600",
              color: "#262626",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#262626"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="8.5" cy="7" r="4"></circle>
              <line x1="20" y1="8" x2="20" y2="14"></line>
              <line x1="23" y1="11" x2="17" y2="11"></line>
            </svg>
          </div>
        </div>

        {/* Highlights */}
        <div
          style={{
            display: "flex",
            gap: "28px",
            overflowX: "auto",
            paddingBottom: "8px",
          }}
        >
          {/* Experience Highlight - Only show if yearsOfExperience > 0 */}
          {yearsOfExperience > 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "10px",
                minWidth: "140px",
              }}
            >
              <div
                style={{
                  width: "140px",
                  height: "140px",
                  borderRadius: "50%",
                  border: "3px solid #E0E0E0",
                  background: "#F5E6D3",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "12px",
                }}
              >
                <div
                  style={{
                    fontSize: "40px",
                    marginBottom: "4px",
                  }}
                >
                  💼
                </div>
                <div
                  style={{
                    fontSize: "19px",
                    fontWeight: "700",
                    color: "#8B4513",
                    textAlign: "center",
                    lineHeight: "1.2",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {yearsOfExperience}+ YEARS
                </div>
              </div>
              <div
                style={{
                  fontSize: "22px",
                  color: "#262626",
                  textAlign: "center",
                  fontWeight: "400",
                }}
              >
                Experience
              </div>
            </div>
          )}

          {/* Skills Highlight - Only show if topSkills.length > 0 */}
          {topSkills.length > 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "10px",
                minWidth: "140px",
              }}
            >
              <div
                style={{
                  width: "140px",
                  height: "140px",
                  borderRadius: "50%",
                  border: "3px solid #E0E0E0",
                  background: "#E8F5E9",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "12px",
                }}
              >
                <div
                  style={{
                    fontSize: "40px",
                    marginBottom: "4px",
                  }}
                >
                  ⚡
                </div>
                <div
                  style={{
                    fontSize: "36px",
                    fontWeight: "700",
                    color: "#2E7D32",
                    textAlign: "center",
                    lineHeight: "1.2",
                  }}
                >
                  {topSkills.length}
                </div>
              </div>
              <div
                style={{
                  fontSize: "22px",
                  color: "#262626",
                  textAlign: "center",
                  fontWeight: "400",
                }}
              >
                Skills
              </div>
            </div>
          )}

          {/* Projects Highlight - Only show if projectsCount > 0 */}
          {projectsCount > 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "10px",
                minWidth: "140px",
              }}
            >
              <div
                style={{
                  width: "140px",
                  height: "140px",
                  borderRadius: "50%",
                  border: "3px solid #E0E0E0",
                  background: "#FFF3E0",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "12px",
                }}
              >
                <div
                  style={{
                    fontSize: "40px",
                    marginBottom: "4px",
                  }}
                >
                  🚀
                </div>
                <div
                  style={{
                    fontSize: "36px",
                    fontWeight: "700",
                    color: "#E65100",
                    textAlign: "center",
                    lineHeight: "1.2",
                  }}
                >
                  {projectsCount}
                </div>
              </div>
              <div
                style={{
                  fontSize: "22px",
                  color: "#262626",
                  textAlign: "center",
                  fontWeight: "400",
                }}
              >
                Projects
              </div>
            </div>
          )}

          {/* Certificates Highlight - Only show if certificationsCount > 0 */}
          {certificationsCount > 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "10px",
                minWidth: "140px",
              }}
            >
              <div
                style={{
                  width: "140px",
                  height: "140px",
                  borderRadius: "50%",
                  border: "3px solid #E0E0E0",
                  background: "#E3F2FD",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "12px",
                }}
              >
                <div
                  style={{
                    fontSize: "40px",
                    marginBottom: "4px",
                  }}
                >
                  🎓
                </div>
                <div
                  style={{
                    fontSize: "36px",
                    fontWeight: "700",
                    color: "#1565C0",
                    textAlign: "center",
                    lineHeight: "1.2",
                  }}
                >
                  {certificationsCount}
                </div>
              </div>
              <div
                style={{
                  fontSize: "22px",
                  color: "#262626",
                  textAlign: "center",
                  fontWeight: "400",
                }}
              >
                Certifications
              </div>
            </div>
          )}

          {/* Achievements Highlight - Only show if exists and count > 0 */}
          {resumeData?.parsedData?.achievements && resumeData.parsedData.achievements.length > 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "10px",
                minWidth: "140px",
              }}
            >
              <div
                style={{
                  width: "140px",
                  height: "140px",
                  borderRadius: "50%",
                  border: "3px solid #E0E0E0",
                  background: "#FCE4EC",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "12px",
                }}
              >
                <div
                  style={{
                    fontSize: "40px",
                    marginBottom: "4px",
                  }}
                >
                  🏆
                </div>
                <div
                  style={{
                    fontSize: "36px",
                    fontWeight: "700",
                    color: "#C2185B",
                    textAlign: "center",
                    lineHeight: "1.2",
                  }}
                >
                  {resumeData.parsedData.achievements.length}
                </div>
              </div>
              <div
                style={{
                  fontSize: "22px",
                  color: "#262626",
                  textAlign: "center",
                  fontWeight: "400",
                }}
              >
                Achievements
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div
        style={{
          display: "flex",
          borderBottom: "1px solid #DBDBDB",
        }}
      >
        {/* Posts Tab - 3x3 Grid (Active) */}
        <div
          style={{
            flex: 1,
            padding: "18px",
            textAlign: "center",
            borderBottom: "2px solid #262626",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="3" width="5" height="5" stroke="#262626" strokeWidth="2" fill="none"></rect>
            <rect x="9.5" y="3" width="5" height="5" stroke="#262626" strokeWidth="2" fill="none"></rect>
            <rect x="16" y="3" width="5" height="5" stroke="#262626" strokeWidth="2" fill="none"></rect>
            <rect x="3" y="9.5" width="5" height="5" stroke="#262626" strokeWidth="2" fill="none"></rect>
            <rect x="9.5" y="9.5" width="5" height="5" stroke="#262626" strokeWidth="2" fill="none"></rect>
            <rect x="16" y="9.5" width="5" height="5" stroke="#262626" strokeWidth="2" fill="none"></rect>
            <rect x="3" y="16" width="5" height="5" stroke="#262626" strokeWidth="2" fill="none"></rect>
            <rect x="9.5" y="16" width="5" height="5" stroke="#262626" strokeWidth="2" fill="none"></rect>
            <rect x="16" y="16" width="5" height="5" stroke="#262626" strokeWidth="2" fill="none"></rect>
          </svg>
        </div>

        {/* Reels Tab - Video/Clapperboard Icon */}
        <div
          style={{
            flex: 1,
            padding: "18px",
            textAlign: "center",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#8E8E8E" strokeWidth="2">
            <rect x="2" y="7" width="20" height="15" rx="2" ry="2"></rect>
            <path d="M16 2 L16 7"></path>
            <path d="M8 2 L8 7"></path>
            <path d="M2 12 L22 12"></path>
            <circle cx="12" cy="15" r="2" fill="#8E8E8E"></circle>
          </svg>
        </div>

        {/* Guides Tab - Map/Guide Icon */}
        <div
          style={{
            flex: 1,
            padding: "18px",
            textAlign: "center",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#8E8E8E" strokeWidth="2">
            <path d="M3 6 L3 20 L9 17 L15 20 L21 17 L21 3 L15 6 L9 3 L3 6 Z"></path>
            <path d="M9 3 L9 17"></path>
            <path d="M15 6 L15 20"></path>
          </svg>
        </div>

        {/* Tagged Tab - Photo with Person Icon */}
        <div
          style={{
            flex: 1,
            padding: "18px",
            textAlign: "center",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#8E8E8E" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            <circle cx="12" cy="10" r="3"></circle>
            <path d="M9 21 C9 18 10.5 16 12 16 C13.5 16 15 18 15 21"></path>
          </svg>
        </div>
      </div>

      {/* Posts Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "3px",
          padding: "0",
        }}
      >
        {/* Experience Post 1 */}
        {experiences[0] && (
          <div
            style={{
              aspectRatio: "1/1",
              background: "linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Decorative background pattern */}
            <div
              style={{
                position: "absolute",
                top: "-20px",
                right: "-20px",
                width: "100px",
                height: "100px",
                borderRadius: "50%",
                background: "rgba(255,255,255,0.1)",
                zIndex: 0,
              }}
            ></div>

            {/* Top right pin icon */}
            <div
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                opacity: 0.9,
                transform: "rotate(45deg)",
                zIndex: 2,
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M16 9V4h1c.55 0 1-.45 1-1s-.45-1-1-1H7c-.55 0-1 .45-1 1s.45 1 1 1h1v5c0 1.66-1.34 3-3 3v2h5.97v7l1 1 1-1v-7H19v-2c-1.66 0-3-1.34-3-3z"></path>
              </svg>
            </div>

            <div
              style={{
                fontSize: "40px",
                marginBottom: "8px",
                zIndex: 1,
              }}
            >
              💼
            </div>
            <div
              style={{
                fontSize: "18px",
                fontWeight: "700",
                color: "#FFFFFF",
                marginBottom: "4px",
                zIndex: 1,
                textShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              {experiences[0].title}
            </div>
            <div
              style={{
                fontSize: "15px",
                color: "#FFFFFF",
                opacity: 0.95,
                marginBottom: "3px",
                zIndex: 1,
              }}
            >
              {experiences[0].company}
            </div>
            <div
              style={{
                fontSize: "13px",
                color: "#FFFFFF",
                opacity: 0.9,
                marginBottom: "3px",
                zIndex: 1,
              }}
            >
              {experiences[0].location}
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "#FFFFFF",
                opacity: 0.85,
                zIndex: 1,
              }}
            >
              {experiences[0].startDate} - {experiences[0].endDate}
            </div>
          </div>
        )}

        {/* Project Post 1 */}
        {projects[0] && (
          <div
            style={{
              aspectRatio: "1/1",
              background: "linear-gradient(135deg, #667EEA 0%, #764BA2 100%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Decorative background pattern */}
            <div
              style={{
                position: "absolute",
                bottom: "-30px",
                left: "-30px",
                width: "120px",
                height: "120px",
                borderRadius: "50%",
                background: "rgba(255,255,255,0.1)",
              }}
            ></div>

            {/* Top right copy icon */}
            <div
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                opacity: 0.9,
                transform: "scaleX(-1)",
                zIndex: 2,
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"></path>
              </svg>
            </div>

            <div
              style={{
                fontSize: "42px",
                marginBottom: "12px",
                position: "relative",
                zIndex: 1,
              }}
            >
              🚀
            </div>
            <div
              style={{
                fontSize: "16px",
                fontWeight: "700",
                color: "#FFFFFF",
                marginBottom: "4px",
                position: "relative",
                zIndex: 1,
                textShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              Projects
            </div>
            <div
              style={{
                fontSize: "14px",
                color: "#FFFFFF",
                opacity: 0.95,
                position: "relative",
                zIndex: 1,
              }}
            >
              {projects[0]?.name}
            </div>
            {projects[1] && (
              <div
                style={{
                  fontSize: "14px",
                  color: "#FFFFFF",
                  opacity: 0.95,
                  marginTop: "2px",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                {projects[1].name}
              </div>
            )}
          </div>
        )}

        {/* Education Post */}
        {education && (
          <div
            style={{
              aspectRatio: "1/1",
              background: "linear-gradient(135deg, #38B2AC 0%, #2C7A7B 100%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Decorative background pattern */}
            <div
              style={{
                position: "absolute",
                top: "-25px",
                right: "-25px",
                width: "100px",
                height: "100px",
                borderRadius: "50%",
                background: "rgba(255,255,255,0.12)",
              }}
            ></div>
            <div
              style={{
                position: "absolute",
                bottom: "-35px",
                right: "20px",
                width: "90px",
                height: "90px",
                borderRadius: "50%",
                background: "rgba(255,255,255,0.08)",
              }}
            ></div>

            {/* Top right copy icon */}
            <div
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                opacity: 0.9,
                transform: "scaleX(-1)",
                zIndex: 2,
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"></path>
              </svg>
            </div>

            <div
              style={{
                fontSize: "40px",
                marginBottom: "8px",
                position: "relative",
                zIndex: 1,
              }}
            >
              🎓
            </div>
            <div
              style={{
                fontSize: "16px",
                fontWeight: "700",
                color: "#FFFFFF",
                marginBottom: "4px",
                position: "relative",
                zIndex: 1,
                textShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              {education.degree}
            </div>
            <div
              style={{
                fontSize: "14px",
                color: "#FFFFFF",
                opacity: 0.95,
                marginBottom: "3px",
                position: "relative",
                zIndex: 1,
              }}
            >
              {education.school}
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "#FFFFFF",
                opacity: 0.9,
                marginBottom: "3px",
                position: "relative",
                zIndex: 1,
              }}
            >
              {education.location}
            </div>
            <div
              style={{
                fontSize: "11px",
                color: "#FFFFFF",
                opacity: 0.85,
                position: "relative",
                zIndex: 1,
              }}
            >
              {education.startDate} - {education.endDate}
            </div>
          </div>
        )}

        {/* Experience Post 2 */}
        {experiences[1] && (
          <div
            style={{
              aspectRatio: "1/1",
              background: "linear-gradient(135deg, #F093FB 0%, #F5576C 100%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Decorative background pattern */}
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "150px",
                height: "150px",
                borderRadius: "50%",
                background: "rgba(255,255,255,0.1)",
                zIndex: 0,
              }}
            ></div>

            {/* Top right copy icon */}
            <div
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                opacity: 0.9,
                transform: "scaleX(-1)",
                zIndex: 2,
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"></path>
              </svg>
            </div>

            <div
              style={{
                fontSize: "40px",
                marginBottom: "8px",
                position: "relative",
                zIndex: 1,
              }}
            >
              💼
            </div>
            <div
              style={{
                fontSize: "18px",
                fontWeight: "700",
                color: "#FFFFFF",
                marginBottom: "4px",
                position: "relative",
                zIndex: 1,
                textShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              {experiences[1].title}
            </div>
            <div
              style={{
                fontSize: "15px",
                color: "#FFFFFF",
                opacity: 0.95,
                marginBottom: "3px",
                position: "relative",
                zIndex: 1,
              }}
            >
              {experiences[1].company}
            </div>
            <div
              style={{
                fontSize: "13px",
                color: "#FFFFFF",
                opacity: 0.9,
                marginBottom: "3px",
                position: "relative",
                zIndex: 1,
              }}
            >
              {experiences[1].location}
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "#FFFFFF",
                opacity: 0.85,
                position: "relative",
                zIndex: 1,
              }}
            >
              {experiences[1].startDate} - {experiences[1].endDate}
            </div>
          </div>
        )}

        {/* Skills Post */}
        <div
          style={{
            aspectRatio: "1/1",
            background: "linear-gradient(135deg, #4A00E0 0%, #8E2DE2 100%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
            textAlign: "center",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Decorative background pattern */}
          <div
            style={{
              position: "absolute",
              top: "-40px",
              left: "-40px",
              width: "130px",
              height: "130px",
              borderRadius: "50%",
              background: "rgba(255,255,255,0.08)",
            }}
          ></div>
          <div
            style={{
              position: "absolute",
              bottom: "-50px",
              right: "-50px",
              width: "150px",
              height: "150px",
              borderRadius: "50%",
              background: "rgba(255,255,255,0.06)",
            }}
          ></div>

          {/* Top right copy icon */}
          <div
            style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              opacity: 0.9,
              transform: "scaleX(-1)",
              zIndex: 2,
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
              <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"></path>
            </svg>
          </div>

          <div
            style={{
              fontSize: "42px",
              marginBottom: "10px",
              position: "relative",
              zIndex: 1,
            }}
          >
            ⚡
          </div>
          <div
            style={{
              fontSize: "18px",
              fontWeight: "700",
              color: "#FFFFFF",
              marginBottom: "8px",
              position: "relative",
              zIndex: 1,
              textShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            Top Skills
          </div>
          <div
            style={{
              fontSize: "13px",
              color: "#FFFFFF",
              opacity: 0.95,
              position: "relative",
              zIndex: 1,
              lineHeight: "1.4",
            }}
          >
            {topSkills.slice(0, 5).join(" • ")}
          </div>
        </div>

        {/* Education Post 2 */}
        {educationList[1] && (
          <div
            style={{
              aspectRatio: "1/1",
              background: "linear-gradient(135deg, #11998E 0%, #38EF7D 100%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Decorative background pattern */}
            <div
              style={{
                position: "absolute",
                top: "10px",
                left: "10px",
                width: "110px",
                height: "110px",
                borderRadius: "50%",
                background: "rgba(255,255,255,0.15)",
              }}
            ></div>
            <div
              style={{
                position: "absolute",
                bottom: "-20px",
                left: "50%",
                transform: "translateX(-50%)",
                width: "100px",
                height: "100px",
                borderRadius: "50%",
                background: "rgba(255,255,255,0.1)",
              }}
            ></div>

            {/* Top right copy icon */}
            <div
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                opacity: 0.9,
                transform: "scaleX(-1)",
                zIndex: 2,
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"></path>
              </svg>
            </div>

            <div
              style={{
                fontSize: "40px",
                marginBottom: "8px",
                position: "relative",
                zIndex: 1,
              }}
            >
              🎓
            </div>
            <div
              style={{
                fontSize: "16px",
                fontWeight: "700",
                color: "#FFFFFF",
                marginBottom: "4px",
                position: "relative",
                zIndex: 1,
                textShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              {educationList[1].degree}
            </div>
            <div
              style={{
                fontSize: "14px",
                color: "#FFFFFF",
                opacity: 0.95,
                marginBottom: "3px",
                position: "relative",
                zIndex: 1,
              }}
            >
              {educationList[1].school}
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "#FFFFFF",
                opacity: 0.9,
                marginBottom: "3px",
                position: "relative",
                zIndex: 1,
              }}
            >
              {educationList[1].location}
            </div>
            <div
              style={{
                fontSize: "11px",
                color: "#FFFFFF",
                opacity: 0.85,
                position: "relative",
                zIndex: 1,
              }}
            >
              {educationList[1].startDate} - {educationList[1].endDate}
            </div>
          </div>
        )}
      </div>

      {/* Bottom Navigation Bar */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "100px",
          background: "#FFFFFF",
          borderTop: "1px solid #DBDBDB",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-around",
          padding: "0 40px",
        }}
      >
        {/* Home Icon */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg width="32" height="32" viewBox="0 0 24 24" fill="#262626">
            <path d="M9.005 16.545a2.997 2.997 0 0 1 2.997-2.997A2.997 2.997 0 0 1 15 16.545V22h7V11.543L12 2 2 11.543V22h7.005z"></path>
          </svg>
        </div>

        {/* Search Icon */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#262626" strokeWidth="2.5">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
        </div>

        {/* Add/Create Icon */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#262626" strokeWidth="2.5">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="12" y1="8" x2="12" y2="16"></line>
            <line x1="8" y1="12" x2="16" y2="12"></line>
          </svg>
        </div>

        {/* Reels Icon */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#262626" strokeWidth="2.5">
            <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"></rect>
            <line x1="7" y1="2" x2="7" y2="22"></line>
            <line x1="17" y1="2" x2="17" y2="22"></line>
            <line x1="2" y1="12" x2="22" y2="12"></line>
            <line x1="2" y1="7" x2="7" y2="7"></line>
            <line x1="2" y1="17" x2="7" y2="17"></line>
            <line x1="17" y1="17" x2="22" y2="17"></line>
            <line x1="17" y1="7" x2="22" y2="7"></line>
          </svg>
        </div>

        {/* Profile Icon with Avatar and Active Dot */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <div
            style={{
              width: "36px",
              height: "36px",
              borderRadius: "50%",
              background: profilePhotoUrl
                ? `url(${profilePhotoUrl})`
                : "linear-gradient(135deg, #667eea, #764ba2)",
              backgroundSize: "cover",
              backgroundPosition: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "16px",
              fontWeight: "700",
              color: "#FFFFFF",
              overflow: "hidden",
            }}
          >
            {!profilePhotoUrl && name.charAt(0).toUpperCase()}
          </div>
          {/* Active red dot indicator */}
          <div
            style={{
              position: "absolute",
              bottom: "-8px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              background: "#ED4956",
            }}
          ></div>
        </div>
      </div>
    </div>
  );
};

// Card 7 - Duolingo Style Mobile Portrait Card (9:16 - 1080x1920px)
export const DuolingoCard = ({ resumeData, profilePhotoUrl }) => {
  const name = resumeData?.parsedData?.personalInfo?.name || "Your Name";
  const currentTitle = resumeData?.parsedData?.experience?.[0]?.title || "Professional";
  const currentCompany = resumeData?.parsedData?.experience?.[0]?.company || "";
  const location = resumeData?.parsedData?.personalInfo?.location || "Location";

  // Calculate years of experience (streak equivalent)
  const calculateYears = () => {
    if (!resumeData?.parsedData?.experience || resumeData.parsedData.experience.length === 0) {
      return 0;
    }
    // Calculate total months of experience for more accurate representation
    let totalMonths = 0;
    resumeData.parsedData.experience.forEach((exp) => {
      if (exp.startDate) {
        const start = new Date(exp.startDate);
        const end = exp.endDate ? new Date(exp.endDate) : new Date();
        const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
        totalMonths += months;
      }
    });
    return Math.floor(totalMonths / 12);
  };

  const yearsOfExperience = calculateYears();
  const workStreak = yearsOfExperience > 0 ? yearsOfExperience * 365 : 365; // Convert to days

  // Get top skills with crown levels (4 skills for 2-column layout)
  const allSkills =
    resumeData?.parsedData?.skills?.list ||
    Object.values(resumeData?.parsedData?.skills?.categorized || {}).flat() ||
    [];
  const topSkills = allSkills.slice(0, 4).map((skill, idx) => ({
    name: skill,
    crowns: idx < 2 ? 3 : idx < 3 ? 2 : 1, // Top skills get more crowns (max 3)
  }));

  // Get experience and education for achievements
  const experiences = resumeData?.parsedData?.experience || [];
  const education = resumeData?.parsedData?.education || [];
  const certifications = resumeData?.parsedData?.certifications || [];
  const projects = resumeData?.parsedData?.projects || [];

  // Calculate XP (mock calculation based on experience)
  const totalXP =
    experiences.length * 1000 + education.length * 500 + certifications.length * 250 + projects.length * 150;

  // Calculate level based on XP
  const level = Math.floor(totalXP / 1000) + 1;

  // Create achievement badges
  const achievements = [
    { icon: "🎓", label: "Education", count: education.length, color: "#FFC800" },
    { icon: "💼", label: "Experience", count: experiences.length, color: "#1CB0F6" },
    { icon: "🏆", label: "Certifications", count: certifications.length, color: "#CE82FF" },
    { icon: "🚀", label: "Projects", count: projects.length, color: "#FF4B4B" },
  ].filter((badge) => badge.count > 0);

  // Duo owl SVG component
  const DuoOwl = ({ expression = "happy", size = 200 }) => (
    <svg width={size} height={size} viewBox="0 0 200 200" fill="none">
      {/* Body */}
      <ellipse cx="100" cy="120" rx="70" ry="80" fill="#58CC02" />
      {/* Belly */}
      <ellipse cx="100" cy="135" rx="45" ry="50" fill="#89E219" />
      {/* Head */}
      <circle cx="100" cy="70" r="55" fill="#58CC02" />
      {/* Eyes - White background */}
      <ellipse cx="80" cy="65" rx="18" ry="22" fill="white" />
      <ellipse cx="120" cy="65" rx="18" ry="22" fill="white" />
      {/* Pupils */}
      <circle cx="80" cy="68" r="10" fill="#1E1E1E" />
      <circle cx="120" cy="68" r="10" fill="#1E1E1E" />
      {/* Eye shine */}
      <circle cx="83" cy="65" r="4" fill="white" />
      <circle cx="123" cy="65" r="4" fill="white" />
      {/* Beak */}
      <path d="M 100 80 L 95 88 L 105 88 Z" fill="#FF9900" />
      {/* Happy expression - smile */}
      <path d="M 85 90 Q 100 100 115 90" stroke="#1E1E1E" strokeWidth="3" fill="none" strokeLinecap="round" />
      {/* Wings */}
      <ellipse cx="45" cy="120" rx="20" ry="35" fill="#58CC02" transform="rotate(-20 45 120)" />
      <ellipse cx="155" cy="120" rx="20" ry="35" fill="#58CC02" transform="rotate(20 155 120)" />
      {/* Feet */}
      <ellipse cx="85" cy="195" rx="12" ry="8" fill="#FF9900" />
      <ellipse cx="115" cy="195" rx="12" ry="8" fill="#FF9900" />
    </svg>
  );

  return (
    <div
      style={{
        width: "1080px",
        height: "1920px",
        background: "linear-gradient(180deg, #58CC02 0%, #89E219 15%, #FFFFFF 15%)",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        position: "relative",
        fontFamily: '"Nunito", "Montserrat", -apple-system, system-ui, sans-serif',
        overflow: "hidden",
      }}
    >
      {/* Header with Duo and Streak */}
      <div
        style={{
          padding: "40px 50px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "relative",
          zIndex: 2,
        }}
      >
        {/* Duo Mini Icon */}
        <div
          style={{
            width: "80px",
            height: "80px",
            borderRadius: "50%",
            background: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          <div style={{ fontSize: "50px" }}>🦉</div>
        </div>

        {/* Streak Display */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "15px",
            background: "white",
            padding: "15px 30px",
            borderRadius: "50px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          <div style={{ fontSize: "45px" }}>🔥</div>
          <div
            style={{
              fontSize: "42px",
              fontWeight: "900",
              color: "#FF4B4B",
            }}
          >
            {workStreak}
          </div>
        </div>
      </div>

      {/* Main Duo Character */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "-20px",
          marginBottom: "25px",
          position: "relative",
          zIndex: 2,
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: "50%",
            padding: "15px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
          }}
        >
          <DuoOwl size={240} />
        </div>
      </div>

      {/* Congratulations Message */}
      <div
        style={{
          padding: "0 50px",
          marginBottom: "25px",
          position: "relative",
          zIndex: 2,
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: "24px",
            padding: "30px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
            border: "4px solid #58CC02",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: "48px",
              fontWeight: "900",
              color: "#58CC02",
              marginBottom: "12px",
              textTransform: "uppercase",
              letterSpacing: "1px",
            }}
          >
            GREAT JOB!
          </div>
          <div
            style={{
              fontSize: "38px",
              fontWeight: "800",
              color: "#1E1E1E",
              marginBottom: "8px",
            }}
          >
            {name}
          </div>
          <div
            style={{
              fontSize: "26px",
              color: "#666",
              fontWeight: "700",
              marginBottom: "4px",
            }}
          >
            {currentTitle}
          </div>
          {currentCompany && (
            <div
              style={{
                fontSize: "22px",
                color: "#999",
                fontWeight: "600",
              }}
            >
              {currentCompany}
            </div>
          )}
        </div>
      </div>

      {/* XP and Level Display */}
      <div
        style={{
          padding: "0 50px",
          marginBottom: "25px",
          position: "relative",
          zIndex: 2,
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: "20px",
            padding: "30px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
            }}
          >
            <div
              style={{
                fontSize: "32px",
                fontWeight: "800",
                color: "#58CC02",
              }}
            >
              +{totalXP} XP
            </div>
            <div
              style={{
                fontSize: "28px",
                fontWeight: "800",
                color: "#1CB0F6",
              }}
            >
              Level {level}
            </div>
          </div>
          {/* Progress Bar */}
          <div
            style={{
              width: "100%",
              height: "24px",
              background: "#E5E5E5",
              borderRadius: "12px",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <div
              style={{
                width: `${((totalXP % 1000) / 1000) * 100}%`,
                height: "100%",
                background: "linear-gradient(90deg, #58CC02 0%, #89E219 100%)",
                borderRadius: "12px",
                transition: "width 0.3s ease",
              }}
            />
          </div>
          <div
            style={{
              textAlign: "center",
              marginTop: "15px",
              fontSize: "22px",
              fontWeight: "700",
              color: "#666",
            }}
          >
            {workStreak}-day work streak! 🔥
          </div>
        </div>
      </div>

      {/* Achievement Badges */}
      <div
        style={{
          padding: "0 50px",
          marginBottom: "25px",
          position: "relative",
          zIndex: 2,
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: "20px",
            padding: "28px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          }}
        >
          <div
            style={{
              fontSize: "30px",
              fontWeight: "900",
              color: "#58CC02",
              marginBottom: "20px",
              textAlign: "center",
              textTransform: "uppercase",
              letterSpacing: "1px",
            }}
          >
            Achievements
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: achievements.length > 2 ? "1fr 1fr" : "1fr",
              gap: "15px",
            }}
          >
            {achievements.map((badge, index) => (
              <div
                key={index}
                style={{
                  background: "#F7F7F7",
                  borderRadius: "14px",
                  padding: "20px",
                  display: "flex",
                  alignItems: "center",
                  gap: "15px",
                  border: `3px solid ${badge.color}`,
                  boxShadow: `0 4px 12px ${badge.color}33`,
                }}
              >
                <div
                  style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "50%",
                    background: badge.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "35px",
                    flexShrink: 0,
                    boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                  }}
                >
                  {badge.icon}
                </div>
                <div>
                  <div
                    style={{
                      fontSize: "20px",
                      fontWeight: "800",
                      color: "#1E1E1E",
                      marginBottom: "4px",
                    }}
                  >
                    {badge.label}
                  </div>
                  <div
                    style={{
                      fontSize: "32px",
                      fontWeight: "900",
                      color: badge.color,
                    }}
                  >
                    {badge.count}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Skills as Lesson Progress */}
      <div
        style={{
          padding: "0 50px",
          marginBottom: "25px",
          position: "relative",
          zIndex: 2,
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: "20px",
            padding: "28px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          }}
        >
          <div
            style={{
              fontSize: "30px",
              fontWeight: "900",
              color: "#58CC02",
              marginBottom: "20px",
              textAlign: "center",
              textTransform: "uppercase",
              letterSpacing: "1px",
            }}
          >
            Skill Mastery
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "15px",
            }}
          >
            {topSkills.map((skill, index) => (
              <div
                key={index}
                style={{
                  background: "#F7F7F7",
                  borderRadius: "14px",
                  padding: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "2px solid #58CC02",
                  boxShadow: "0 2px 8px rgba(88, 204, 2, 0.1)",
                }}
              >
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: "800",
                    color: "#1E1E1E",
                    textAlign: "center",
                  }}
                >
                  {skill.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Call to Action - Big Green Button */}
      <div
        style={{
          padding: "0 50px",
          marginBottom: "30px",
          position: "relative",
          zIndex: 2,
        }}
      >
        <div
          style={{
            background: "#58CC02",
            borderRadius: "24px",
            padding: "28px",
            textAlign: "center",
            boxShadow: "0 8px 24px rgba(88, 204, 2, 0.4)",
            border: "4px solid #89E219",
            cursor: "pointer",
            transition: "transform 0.2s ease",
          }}
        >
          <div
            style={{
              fontSize: "44px",
              fontWeight: "900",
              color: "white",
              textTransform: "uppercase",
              letterSpacing: "2px",
              textShadow: "0 2px 4px rgba(0,0,0,0.2)",
            }}
          >
            HIRE ME!
          </div>
        </div>
      </div>

      {/* Footer - Made with upresume.ai */}
      <div
        style={{
          padding: "0 50px 40px",
          textAlign: "center",
          marginTop: "auto",
          position: "relative",
          zIndex: 2,
        }}
      >
        <div
          style={{
            fontSize: "24px",
            fontWeight: "700",
            color: "#666",
          }}
        >
          Made with <span style={{ color: "#58CC02", fontWeight: "900" }}>upresume.ai</span>
        </div>
      </div>

      {/* Decorative confetti elements */}
      <div
        style={{
          position: "absolute",
          top: "15%",
          left: "10%",
          fontSize: "40px",
          opacity: 0.3,
          transform: "rotate(-15deg)",
          zIndex: 1,
        }}
      >
        🎉
      </div>
      <div
        style={{
          position: "absolute",
          top: "25%",
          right: "12%",
          fontSize: "35px",
          opacity: 0.3,
          transform: "rotate(20deg)",
          zIndex: 1,
        }}
      >
        ⭐
      </div>
      <div
        style={{
          position: "absolute",
          top: "45%",
          left: "8%",
          fontSize: "38px",
          opacity: 0.25,
          transform: "rotate(10deg)",
          zIndex: 1,
        }}
      >
        🎊
      </div>
      <div
        style={{
          position: "absolute",
          top: "65%",
          right: "10%",
          fontSize: "42px",
          opacity: 0.3,
          transform: "rotate(-10deg)",
          zIndex: 1,
        }}
      >
        ✨
      </div>
    </div>
  );
};

// Card 8 - Spotify Wrapped Mobile Version (9:16 - 1080x1920px)
export const SpotifyWrappedMobileCard = ({ resumeData, profilePhotoUrl, cardIndex = 0 }) => {
  const name = resumeData?.parsedData?.personalInfo?.name || "Your Name";
  const email = resumeData?.parsedData?.personalInfo?.email || "";
  const phone = resumeData?.parsedData?.personalInfo?.phone || "";
  const linkedin =
    resumeData?.parsedData?.personalInfo?.linkedIn || resumeData?.parsedData?.personalInfo?.linkedin || "";

  // Calculate statistics
  const calculateStats = () => {
    const experience = resumeData?.parsedData?.experience || [];
    const totalMonths = experience.reduce((acc, exp) => {
      if (exp.startDate && exp.endDate) {
        const start = new Date(exp.startDate);
        const end = exp.endDate === "Present" ? new Date() : new Date(exp.endDate);
        const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
        return acc + months;
      }
      return acc;
    }, 0);

    return {
      totalMinutes: totalMonths * 30 * 8 * 60, // Approx working minutes
      totalProjects: experience.length + (resumeData?.parsedData?.projects?.length || 0),
      totalSkills:
        (resumeData?.parsedData?.skills?.list?.length || 0) +
        (Object.values(resumeData?.parsedData?.skills?.categorized || {}).flat().length || 0),
      yearsOfExperience: Math.floor(totalMonths / 12),
    };
  };

  const stats = calculateStats();

  // Get top 5 skills
  const allSkills =
    resumeData?.parsedData?.skills?.list ||
    Object.values(resumeData?.parsedData?.skills?.categorized || {}).flat() ||
    [];
  const topSkills = allSkills.slice(0, 5);

  // Developer personality traits
  const getPersonality = () => {
    const exp = resumeData?.parsedData?.experience || [];
    const traits = [];

    if (exp.length >= 5) traits.push({ emoji: "🌙", name: "NIGHT OWL" });
    else traits.push({ emoji: "☀️", name: "DAY STARTER" });

    if (topSkills.some((s) => s.toLowerCase().includes("ai") || s.toLowerCase().includes("ml"))) {
      traits.push({ emoji: "🔮", name: "INNOVATOR" });
    } else {
      traits.push({ emoji: "⚙️", name: "BUILDER" });
    }

    if (stats.totalProjects > 10) traits.push({ emoji: "⚡", name: "SPEED DEMON" });
    else traits.push({ emoji: "🎯", name: "FOCUSED" });

    return traits;
  };

  const personality = getPersonality();

  // Card configurations
  const cards = [
    // Card 1: Welcome
    {
      gradient: "linear-gradient(135deg, #FF6B9D 0%, #C66FBC 25%, #7C4DFF 50%, #1E3264 100%)",
      content: (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            padding: "60px",
            textAlign: "center",
            position: "relative",
            zIndex: 2,
          }}
        >
          <div
            style={{
              fontSize: "28px",
              fontWeight: "800",
              color: "#FFFFFF",
              marginBottom: "60px",
              letterSpacing: "2px",
              textTransform: "uppercase",
            }}
          >
            YOUR 2024 WRAPPED
          </div>

          {profilePhotoUrl && (
            <div
              style={{
                width: "240px",
                height: "240px",
                borderRadius: "50%",
                border: "6px solid rgba(255, 255, 255, 0.3)",
                overflow: "hidden",
                marginBottom: "40px",
                boxShadow: "0 20px 60px rgba(124, 77, 255, 0.4), 0 10px 20px rgba(0, 0, 0, 0.3)",
                background: "#FFFFFF",
              }}
            >
              <img
                src={profilePhotoUrl}
                alt={name}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </div>
          )}

          <div
            style={{
              fontSize: "48px",
              fontWeight: "900",
              color: "#FFFFFF",
              marginBottom: "24px",
              lineHeight: "1.2",
            }}
          >
            {name.split(" ")[0]}
          </div>

          <div
            style={{
              fontSize: "32px",
              fontWeight: "300",
              color: "#F0F0F0",
              marginBottom: "80px",
            }}
          >
            Ready to see your
            <br />
            professional year in review?
          </div>

          <div
            style={{
              fontSize: "20px",
              color: "#FFFFFF",
              opacity: 0.8,
              animation: "bounce 2s infinite",
            }}
          >
            ↓ Swipe to explore ↓
          </div>
        </div>
      ),
    },
    // Card 2: Total Impact
    {
      gradient: "linear-gradient(135deg, #C66FBC 0%, #7C4DFF 50%, #1E3264 100%)",
      content: (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            padding: "60px",
            textAlign: "center",
            position: "relative",
            zIndex: 2,
          }}
        >
          <div
            style={{
              fontSize: "28px",
              fontWeight: "300",
              color: "#F0F0F0",
              marginBottom: "40px",
              letterSpacing: "1px",
            }}
          >
            YOU CODED FOR
          </div>

          <div
            style={{
              background: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              borderRadius: "30px",
              padding: "60px 80px",
              marginBottom: "60px",
              boxShadow: "0 8px 32px rgba(31, 38, 135, 0.37)",
            }}
          >
            <div
              style={{
                fontSize: "120px",
                fontWeight: "900",
                color: "#FFFFFF",
                lineHeight: "1",
                marginBottom: "20px",
                letterSpacing: "-2px",
              }}
            >
              {stats.totalMinutes.toLocaleString()}
            </div>
            <div
              style={{
                fontSize: "36px",
                fontWeight: "700",
                color: "#F0F0F0",
                letterSpacing: "4px",
              }}
            >
              MINUTES
            </div>
          </div>

          <div
            style={{
              fontSize: "24px",
              fontWeight: "300",
              color: "#FFFFFF",
              marginBottom: "16px",
            }}
          >
            That's more than <span style={{ fontWeight: "700", color: "#1ED760" }}>92%</span>
          </div>
          <div
            style={{
              fontSize: "24px",
              fontWeight: "300",
              color: "#FFFFFF",
              marginBottom: "60px",
            }}
          >
            of developers
          </div>

          <div
            style={{
              fontSize: "28px",
              fontWeight: "300",
              color: "#F0F0F0",
              opacity: 0.8,
            }}
          >
            = {Math.floor(stats.totalMinutes / (60 * 24)).toLocaleString()} days non-stop
          </div>
        </div>
      ),
    },
    // Card 3: Top Skills
    {
      gradient: "linear-gradient(135deg, #7C4DFF 0%, #1E3264 50%, #FF6B9D 100%)",
      content: (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            padding: "60px",
            position: "relative",
            zIndex: 2,
          }}
        >
          <div
            style={{
              fontSize: "32px",
              fontWeight: "800",
              color: "#FFFFFF",
              marginBottom: "80px",
              letterSpacing: "2px",
              textAlign: "center",
            }}
          >
            YOUR TOP 5 SKILLS
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "40px",
              flex: 1,
            }}
          >
            {topSkills.map((skill, idx) => {
              const percentage = 100 - idx * 7;
              return (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "28px",
                        fontWeight: "700",
                        color: "#FFFFFF",
                      }}
                    >
                      {idx + 1}. {skill}
                    </div>
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "700",
                        color: "#1ED760",
                      }}
                    >
                      {percentage}%
                    </div>
                  </div>
                  <div
                    style={{
                      width: "100%",
                      height: "12px",
                      background: "rgba(255, 255, 255, 0.1)",
                      borderRadius: "6px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${percentage}%`,
                        height: "100%",
                        background: "linear-gradient(90deg, #1ED760 0%, #1DB954 100%)",
                        borderRadius: "6px",
                        transition: "width 0.8s ease-out",
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ),
    },
    // Card 4: Developer Personality
    {
      gradient: "linear-gradient(135deg, #1E3264 0%, #7C4DFF 50%, #C66FBC 100%)",
      content: (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            padding: "60px",
            textAlign: "center",
            position: "relative",
            zIndex: 2,
          }}
        >
          <div
            style={{
              fontSize: "28px",
              fontWeight: "300",
              color: "#F0F0F0",
              marginBottom: "60px",
              letterSpacing: "2px",
            }}
          >
            YOUR DEVELOPER AURA IS
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "40px",
              marginBottom: "80px",
            }}
          >
            {personality.map((trait, idx) => (
              <div
                key={idx}
                style={{
                  background: "rgba(255, 255, 255, 0.1)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  borderRadius: "20px",
                  padding: "30px 60px",
                  boxShadow: "0 8px 32px rgba(31, 38, 135, 0.37)",
                }}
              >
                <div
                  style={{
                    fontSize: "64px",
                    marginBottom: "16px",
                  }}
                >
                  {trait.emoji}
                </div>
                <div
                  style={{
                    fontSize: "36px",
                    fontWeight: "900",
                    color: "#FFFFFF",
                    letterSpacing: "4px",
                  }}
                >
                  {trait.name}
                </div>
              </div>
            ))}
          </div>

          <div
            style={{
              fontSize: "24px",
              fontWeight: "300",
              color: "#F0F0F0",
              lineHeight: "1.6",
            }}
          >
            You're in rare company -<br />
            only <span style={{ fontWeight: "700", color: "#1ED760" }}>3%</span> share your combo
          </div>
        </div>
      ),
    },
    // Card 5: Achievements
    {
      gradient: "linear-gradient(135deg, #FF6B9D 0%, #C66FBC 50%, #7C4DFF 100%)",
      content: (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            padding: "60px",
            position: "relative",
            zIndex: 2,
          }}
        >
          <div
            style={{
              fontSize: "32px",
              fontWeight: "800",
              color: "#FFFFFF",
              marginBottom: "80px",
              letterSpacing: "2px",
              textAlign: "center",
            }}
          >
            2024 HIGHLIGHTS
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "50px",
              flex: 1,
              justifyContent: "center",
            }}
          >
            {[
              { emoji: "🏆", text: `Shipped ${stats.totalProjects} projects` },
              { emoji: "🎯", text: `Mastered ${stats.totalSkills} skills` },
              { emoji: "📚", text: `${stats.yearsOfExperience} years of expertise` },
              { emoji: "☕", text: "Consumed 1,847 coffees" },
              { emoji: "✨", text: "100% deadline success" },
            ].map((achievement, idx) => (
              <div
                key={idx}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "30px",
                  background: "rgba(255, 255, 255, 0.1)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  borderRadius: "20px",
                  padding: "24px 30px",
                  boxShadow: "0 8px 32px rgba(31, 38, 135, 0.37)",
                }}
              >
                <div
                  style={{
                    fontSize: "48px",
                    minWidth: "60px",
                    textAlign: "center",
                  }}
                >
                  {achievement.emoji}
                </div>
                <div
                  style={{
                    fontSize: "28px",
                    fontWeight: "600",
                    color: "#FFFFFF",
                  }}
                >
                  {achievement.text}
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    // Card 6: Hire Me CTA
    {
      gradient: "linear-gradient(135deg, #1E3264 0%, #7C4DFF 25%, #C66FBC 50%, #FF6B9D 100%)",
      content: (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            padding: "60px",
            textAlign: "center",
            position: "relative",
            zIndex: 2,
          }}
        >
          <div
            style={{
              fontSize: "56px",
              fontWeight: "900",
              color: "#FFFFFF",
              marginBottom: "40px",
              letterSpacing: "2px",
            }}
          >
            THAT'S A WRAP!
          </div>

          {/* Spotify Code Visual */}
          <div
            style={{
              width: "320px",
              height: "120px",
              background: "rgba(255, 255, 255, 0.95)",
              borderRadius: "16px",
              marginBottom: "60px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "4px",
              padding: "20px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
            }}
          >
            {[30, 45, 60, 35, 50, 70, 40, 55, 65, 45, 50, 60, 35, 70, 45].map((height, idx) => (
              <div
                key={idx}
                style={{
                  width: "12px",
                  height: `${height}px`,
                  background: "#121212",
                  borderRadius: "2px",
                }}
              />
            ))}
          </div>

          <div
            style={{
              fontSize: "28px",
              fontWeight: "300",
              color: "#F0F0F0",
              marginBottom: "80px",
              lineHeight: "1.6",
            }}
          >
            "Ready to add me to
            <br />
            your team's playlist?"
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              marginBottom: "60px",
              width: "100%",
            }}
          >
            {email && (
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "600",
                  color: "#FFFFFF",
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  justifyContent: "center",
                }}
              >
                <span>📧</span> {email}
              </div>
            )}
            {linkedin && (
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "600",
                  color: "#FFFFFF",
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  justifyContent: "center",
                }}
              >
                <span>🔗</span> {linkedin.replace("https://", "")}
              </div>
            )}
            {phone && (
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "600",
                  color: "#FFFFFF",
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  justifyContent: "center",
                }}
              >
                <span>📱</span> {phone}
              </div>
            )}
          </div>

          <div
            style={{
              background: "#1ED760",
              color: "#000000",
              fontSize: "32px",
              fontWeight: "900",
              padding: "24px 80px",
              borderRadius: "50px",
              cursor: "pointer",
              boxShadow: "0 10px 40px rgba(30, 215, 96, 0.4)",
              letterSpacing: "2px",
              transition: "transform 0.2s",
              ":hover": {
                transform: "scale(1.05)",
              },
            }}
          >
            HIRE ME
          </div>
        </div>
      ),
    },
  ];

  const currentCard = cards[cardIndex % cards.length];

  return (
    <div
      style={{
        width: "1080px",
        height: "1920px",
        background: currentCard.gradient,
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        position: "relative",
        fontFamily: "'Montserrat', 'Plus Jakarta Sans', 'Inter', -apple-system, system-ui, sans-serif",
        overflow: "hidden",
      }}
    >
      {/* Spotify Wrapped Header (Small) */}
      <div
        style={{
          position: "absolute",
          top: "30px",
          left: "60px",
          fontSize: "18px",
          fontWeight: "600",
          color: "rgba(255, 255, 255, 0.7)",
          letterSpacing: "1px",
          zIndex: 3,
        }}
      >
        SPOTIFY WRAPPED
      </div>

      {/* Progress Dots */}
      <div
        style={{
          position: "absolute",
          bottom: "60px",
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          gap: "16px",
          zIndex: 3,
        }}
      >
        {cards.map((_, idx) => (
          <div
            key={idx}
            style={{
              width: idx === cardIndex ? "40px" : "12px",
              height: "12px",
              borderRadius: "6px",
              background: idx === cardIndex ? "#FFFFFF" : "rgba(255, 255, 255, 0.3)",
              transition: "all 0.3s ease",
            }}
          />
        ))}
      </div>

      {/* Glassmorphism Background Effects */}
      <div
        style={{
          position: "absolute",
          top: "-100px",
          left: "-100px",
          width: "400px",
          height: "400px",
          background: "rgba(255, 255, 255, 0.1)",
          borderRadius: "50%",
          filter: "blur(80px)",
          zIndex: 1,
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "-150px",
          right: "-150px",
          width: "500px",
          height: "500px",
          background: "rgba(255, 107, 157, 0.2)",
          borderRadius: "50%",
          filter: "blur(100px)",
          zIndex: 1,
        }}
      />

      {/* Card Content */}
      {currentCard.content}

      {/* Bounce Animation */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
          @keyframes bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-10px); }
          }
        `,
        }}
      />
    </div>
  );
};

// Card 9 - Spotify Wrapped Mobile Summary (Single Page - 9:16 - 1080x1920px)
export const SpotifyWrappedMobileSummaryCard = ({ resumeData, profilePhotoUrl }) => {
  const name = resumeData?.parsedData?.personalInfo?.name || "Your Name";
  const email = resumeData?.parsedData?.personalInfo?.email || "";
  const phone = resumeData?.parsedData?.personalInfo?.phone || "";
  const linkedin =
    resumeData?.parsedData?.personalInfo?.linkedIn || resumeData?.parsedData?.personalInfo?.linkedin || "";

  // Calculate statistics
  const calculateStats = () => {
    const experience = resumeData?.parsedData?.experience || [];
    const totalMonths = experience.reduce((acc, exp) => {
      if (exp.startDate && exp.endDate) {
        const start = new Date(exp.startDate);
        const end = exp.endDate === "Present" ? new Date() : new Date(exp.endDate);
        const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
        return acc + months;
      }
      return acc;
    }, 0);

    return {
      totalMinutes: totalMonths * 30 * 8 * 60,
      totalProjects: experience.length + (resumeData?.parsedData?.projects?.length || 0),
      totalSkills:
        (resumeData?.parsedData?.skills?.list?.length || 0) +
        (Object.values(resumeData?.parsedData?.skills?.categorized || {}).flat().length || 0),
      yearsOfExperience: Math.floor(totalMonths / 12),
    };
  };

  const stats = calculateStats();

  // Get top 5 skills
  const allSkills =
    resumeData?.parsedData?.skills?.list ||
    Object.values(resumeData?.parsedData?.skills?.categorized || {}).flat() ||
    [];
  const topSkills = allSkills.slice(0, 5);

  // Developer personality traits
  const getPersonality = () => {
    const exp = resumeData?.parsedData?.experience || [];
    const traits = [];

    if (exp.length >= 5) traits.push({ emoji: "🌙", name: "NIGHT OWL" });
    else traits.push({ emoji: "☀️", name: "DAY STARTER" });

    if (topSkills.some((s) => s.toLowerCase().includes("ai") || s.toLowerCase().includes("ml"))) {
      traits.push({ emoji: "🔮", name: "INNOVATOR" });
    } else {
      traits.push({ emoji: "⚙️", name: "BUILDER" });
    }

    if (stats.totalProjects > 10) traits.push({ emoji: "⚡", name: "SPEED DEMON" });
    else traits.push({ emoji: "🎯", name: "FOCUSED" });

    return traits;
  };

  const personality = getPersonality();

  return (
    <div
      style={{
        width: "1080px",
        height: "1920px",
        background: "linear-gradient(135deg, #FF6B9D 0%, #C66FBC 25%, #7C4DFF 50%, #1E3264 100%)",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        position: "relative",
        fontFamily: "'Montserrat', 'Plus Jakarta Sans', 'Inter', -apple-system, system-ui, sans-serif",
        overflow: "hidden",
        padding: "60px 50px",
      }}
    >
      {/* Spotify Wrapped Header */}
      <div
        style={{
          position: "absolute",
          top: "30px",
          left: "60px",
          fontSize: "18px",
          fontWeight: "600",
          color: "rgba(255, 255, 255, 0.7)",
          letterSpacing: "1px",
          zIndex: 3,
        }}
      >
        SPOTIFY WRAPPED
      </div>

      {/* Glassmorphism Background Effects */}
      <div
        style={{
          position: "absolute",
          top: "-100px",
          left: "-100px",
          width: "400px",
          height: "400px",
          background: "rgba(255, 255, 255, 0.1)",
          borderRadius: "50%",
          filter: "blur(80px)",
          zIndex: 1,
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "-150px",
          right: "-150px",
          width: "500px",
          height: "500px",
          background: "rgba(255, 107, 157, 0.2)",
          borderRadius: "50%",
          filter: "blur(100px)",
          zIndex: 1,
        }}
      />

      {/* Content Container */}
      <div
        style={{
          position: "relative",
          zIndex: 2,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          gap: "40px",
        }}
      >
        {/* Row 1: Profile Photo + Name | Minutes Coded */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "30px",
            alignItems: "center",
          }}
        >
          {/* Left Column: Photo + Name */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "20px",
            }}
          >
            {profilePhotoUrl && (
              <div
                style={{
                  width: "180px",
                  height: "180px",
                  borderRadius: "50%",
                  border: "5px solid rgba(255, 255, 255, 0.3)",
                  overflow: "hidden",
                  boxShadow: "0 20px 60px rgba(124, 77, 255, 0.4), 0 10px 20px rgba(0, 0, 0, 0.3)",
                  background: "#FFFFFF",
                }}
              >
                <img
                  src={profilePhotoUrl}
                  alt={name}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </div>
            )}
            <div
              style={{
                fontSize: "40px",
                fontWeight: "900",
                color: "#FFFFFF",
                textAlign: "center",
                lineHeight: "1.2",
              }}
            >
              {name}
            </div>
          </div>

          {/* Right Column: Minutes Coded */}
          <div
            style={{
              background: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              borderRadius: "25px",
              padding: "40px 30px",
              boxShadow: "0 8px 32px rgba(31, 38, 135, 0.37)",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: "22px",
                fontWeight: "300",
                color: "#F0F0F0",
                marginBottom: "20px",
                letterSpacing: "1px",
              }}
            >
              YOU CODED FOR
            </div>
            <div
              style={{
                fontSize: "80px",
                fontWeight: "900",
                color: "#FFFFFF",
                lineHeight: "1",
                marginBottom: "10px",
                letterSpacing: "-2px",
              }}
            >
              {stats.totalMinutes.toLocaleString()}
            </div>
            <div
              style={{
                fontSize: "28px",
                fontWeight: "700",
                color: "#F0F0F0",
                letterSpacing: "3px",
              }}
            >
              MINUTES
            </div>
            <div
              style={{
                fontSize: "18px",
                fontWeight: "300",
                color: "#FFFFFF",
                marginTop: "15px",
              }}
            >
              More than <span style={{ fontWeight: "700", color: "#1ED760" }}>92%</span> of developers
            </div>
          </div>
        </div>

        {/* Row 2: Developer Aura - 3 Cards Horizontal */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "20px",
          }}
        >
          {personality.map((trait, idx) => (
            <div
              key={idx}
              style={{
                background: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                borderRadius: "20px",
                padding: "25px 40px",
                boxShadow: "0 8px 32px rgba(31, 38, 135, 0.37)",
                flex: 1,
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: "50px",
                  marginBottom: "12px",
                }}
              >
                {trait.emoji}
              </div>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "900",
                  color: "#FFFFFF",
                  letterSpacing: "2px",
                }}
              >
                {trait.name}
              </div>
            </div>
          ))}
        </div>

        {/* Row 3: Top 5 Skills */}
        <div
          style={{
            background: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            borderRadius: "25px",
            padding: "35px 40px",
            boxShadow: "0 8px 32px rgba(31, 38, 135, 0.37)",
          }}
        >
          <div
            style={{
              fontSize: "28px",
              fontWeight: "800",
              color: "#FFFFFF",
              marginBottom: "30px",
              letterSpacing: "2px",
              textAlign: "center",
            }}
          >
            YOUR TOP 5 SKILLS
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "25px",
            }}
          >
            {topSkills.map((skill, idx) => {
              const percentage = 100 - idx * 7;
              return (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "22px",
                        fontWeight: "700",
                        color: "#FFFFFF",
                      }}
                    >
                      {idx + 1}. {skill}
                    </div>
                    <div
                      style={{
                        fontSize: "20px",
                        fontWeight: "700",
                        color: "#1ED760",
                      }}
                    >
                      {percentage}%
                    </div>
                  </div>
                  <div
                    style={{
                      width: "100%",
                      height: "10px",
                      background: "rgba(255, 255, 255, 0.1)",
                      borderRadius: "5px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${percentage}%`,
                        height: "100%",
                        background: "linear-gradient(90deg, #1ED760 0%, #1DB954 100%)",
                        borderRadius: "5px",
                        transition: "width 0.8s ease-out",
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Row 4: That's A Wrap - Contact Info */}
        <div
          style={{
            background: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            borderRadius: "25px",
            padding: "35px 40px",
            boxShadow: "0 8px 32px rgba(31, 38, 135, 0.37)",
            textAlign: "center",
            marginTop: "auto",
          }}
        >
          <div
            style={{
              fontSize: "48px",
              fontWeight: "900",
              color: "#FFFFFF",
              marginBottom: "25px",
              letterSpacing: "2px",
            }}
          >
            THAT'S A WRAP!
          </div>

          {/* Spotify Code Visual */}
          <div
            style={{
              width: "280px",
              height: "100px",
              background: "rgba(255, 255, 255, 0.95)",
              borderRadius: "14px",
              margin: "0 auto 25px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "3px",
              padding: "15px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
            }}
          >
            {[30, 45, 60, 35, 50, 70, 40, 55, 65, 45, 50, 60, 35, 70, 45].map((height, idx) => (
              <div
                key={idx}
                style={{
                  width: "10px",
                  height: `${height}px`,
                  background: "#121212",
                  borderRadius: "2px",
                }}
              />
            ))}
          </div>

          <div
            style={{
              fontSize: "22px",
              fontWeight: "300",
              color: "#F0F0F0",
              marginBottom: "25px",
              lineHeight: "1.5",
            }}
          >
            "Ready to add me to your team's playlist?"
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              marginBottom: "25px",
            }}
          >
            {email && (
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#FFFFFF",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  justifyContent: "center",
                }}
              >
                <span>📧</span> {email}
              </div>
            )}
            {linkedin && (
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#FFFFFF",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  justifyContent: "center",
                }}
              >
                <span>🔗</span> {linkedin.replace("https://", "")}
              </div>
            )}
            {phone && (
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#FFFFFF",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  justifyContent: "center",
                }}
              >
                <span>📱</span> {phone}
              </div>
            )}
          </div>

          <div
            style={{
              background: "#1ED760",
              color: "#000000",
              fontSize: "26px",
              fontWeight: "900",
              padding: "18px 60px",
              borderRadius: "50px",
              display: "inline-block",
              boxShadow: "0 10px 40px rgba(30, 215, 96, 0.4)",
              letterSpacing: "2px",
            }}
          >
            HIRE ME
          </div>
        </div>
      </div>
    </div>
  );
};

// Card 10 - Apple Music Desktop Style (16:9 - 1920x1080px)
export const AppleMusicDesktopCard = ({ resumeData, profilePhotoUrl }) => {
  const name = resumeData?.parsedData?.personalInfo?.name || "Your Name";
  const currentTitle = resumeData?.parsedData?.experience?.[0]?.title || "Professional Developer";
  const email = resumeData?.parsedData?.personalInfo?.email || "";
  const phone = resumeData?.parsedData?.personalInfo?.phone || "";
  const location = resumeData?.parsedData?.personalInfo?.location || "";

  // Summary can be object {content, aiAnalysis} or string
  const summaryData = resumeData?.parsedData?.summary;
  const summary = typeof summaryData === "string" ? summaryData : summaryData?.content || "";

  // Get experience and skills
  const experience = resumeData?.parsedData?.experience || [];
  const education = resumeData?.parsedData?.education || [];
  const projects = resumeData?.parsedData?.projects || [];
  const achievements = resumeData?.parsedData?.achievements || [];
  const allSkills =
    resumeData?.parsedData?.skills?.list ||
    Object.values(resumeData?.parsedData?.skills?.categorized || {}).flat() ||
    [];

  // Calculate stats
  const totalMonths = experience.reduce((acc, exp) => {
    if (exp.startDate && exp.endDate) {
      const start = new Date(exp.startDate);
      const end = exp.endDate === "Present" ? new Date() : new Date(exp.endDate);
      const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
      return acc + months;
    }
    return acc;
  }, 0);
  const yearsOfExperience = Math.floor(totalMonths / 12);
  const totalProjects = experience.length + (resumeData?.parsedData?.projects?.length || 0);

  return (
    <div
      style={{
        width: "1920px",
        height: "1080px",
        background: "#1C1C1E",
        display: "flex",
        flexDirection: "column",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif',
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Main Content Area */}
      <div
        style={{
          display: "flex",
          flex: 1,
          position: "relative",
        }}
      >
        {/* Sidebar Navigation */}
        <div
          style={{
            width: "250px",
            background: "rgba(60, 60, 67, 0.65)",
            backdropFilter: "blur(50px)",
            WebkitBackdropFilter: "blur(50px)",
            borderRight: "1px solid rgba(255, 255, 255, 0.1)",
            padding: "24px 16px",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          {/* Logo */}
          <div
            style={{
              fontSize: "24px",
              fontWeight: "700",
              color: "#FFFFFF",
              marginBottom: "32px",
              letterSpacing: "-0.02em",
            }}
          >
            🎵 Apple Music
          </div>

          {/* Navigation Items */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            {[
              { icon: "🎧", label: "Listen Now", active: false },
              { icon: "📻", label: "Browse", active: false },
              { icon: "📡", label: "Radio", active: false },
              { icon: "📚", label: "Library", active: true },
            ].map((item, idx) => (
              <div
                key={idx}
                style={{
                  padding: "8px 12px",
                  borderRadius: "8px",
                  background: item.active ? "rgba(255, 255, 255, 0.1)" : "transparent",
                  color: item.active ? "#FFFFFF" : "#C7C7CC",
                  fontSize: "14px",
                  fontWeight: item.active ? "600" : "500",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <span style={{ fontSize: "16px" }}>{item.icon}</span>
                <span>{item.label}</span>
              </div>
            ))}
          </div>

          {/* Playlists Section */}
          <div
            style={{
              marginTop: "32px",
            }}
          >
            <div
              style={{
                fontSize: "11px",
                fontWeight: "700",
                color: "#8E8E93",
                letterSpacing: "0.06em",
                marginBottom: "12px",
                textTransform: "uppercase",
              }}
            >
              Playlists
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              {["Career Hits 2024", "Top Skills", "Work Experience", "Projects Mix"].map((playlist, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: "8px 12px",
                    borderRadius: "6px",
                    color: "#C7C7CC",
                    fontSize: "13px",
                    fontWeight: "500",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  {playlist}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Top Bar */}
          <div
            style={{
              padding: "20px 40px",
              display: "flex",
              alignItems: "center",
              gap: "16px",
              borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
            }}
          >
            <div
              style={{
                fontSize: "11px",
                fontWeight: "700",
                color: "#8E8E93",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}
            >
              Now Playing
            </div>
          </div>

          {/* Main Content Area */}
          <div
            style={{
              flex: 1,
              display: "flex",
              padding: "32px 40px",
              gap: "32px",
              overflow: "hidden",
            }}
          >
            {/* Left: Album Art + Now Playing */}
            <div
              style={{
                width: "380px",
                display: "flex",
                flexDirection: "column",
                gap: "24px",
              }}
            >
              {/* Album Artwork */}
              {profilePhotoUrl ? (
                <div
                  style={{
                    width: "380px",
                    height: "380px",
                    borderRadius: "8px",
                    overflow: "hidden",
                    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.4)",
                    background: "#2C2C2E",
                  }}
                >
                  <img
                    src={profilePhotoUrl}
                    alt={name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </div>
              ) : (
                <div
                  style={{
                    width: "380px",
                    height: "380px",
                    borderRadius: "8px",
                    background: "linear-gradient(135deg, #FA243C 0%, #FA793E 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "96px",
                    color: "#FFFFFF",
                    fontWeight: "700",
                    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.4)",
                  }}
                >
                  {name.charAt(0)}
                </div>
              )}

              {/* Track Info */}
              <div>
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: "700",
                    color: "#FFFFFF",
                    lineHeight: "1.2",
                    letterSpacing: "-0.02em",
                    marginBottom: "6px",
                  }}
                >
                  {name}
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#8E8E93",
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    marginBottom: "4px",
                  }}
                >
                  {currentTitle}
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    fontWeight: "400",
                    color: "#8E8E93",
                  }}
                >
                  {yearsOfExperience} Years • {totalProjects} Projects
                </div>
              </div>

              {/* Playback Controls */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "20px",
                  justifyContent: "center",
                }}
              >
                {/* Previous */}
                <div
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#8E8E93"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polygon points="19 20 9 12 19 4 19 20" />
                    <line x1="5" y1="19" x2="5" y2="5" />
                  </svg>
                </div>

                {/* Play Button */}
                <div
                  style={{
                    width: "56px",
                    height: "56px",
                    borderRadius: "50%",
                    background: "#FA243C",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    boxShadow: "0 4px 12px rgba(250, 36, 60, 0.4)",
                    transition: "transform 0.2s ease",
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#FFFFFF">
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                </div>

                {/* Next */}
                <div
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#8E8E93"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polygon points="5 4 15 12 5 20 5 4" />
                    <line x1="19" y1="5" x2="19" y2="19" />
                  </svg>
                </div>
              </div>

              {/* Additional Controls */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  paddingTop: "12px",
                }}
              >
                {/* Shuffle */}
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#8E8E93"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="16 3 21 3 21 8" />
                    <line x1="4" y1="20" x2="21" y2="3" />
                    <polyline points="21 16 21 21 16 21" />
                    <line x1="15" y1="15" x2="21" y2="21" />
                    <line x1="4" y1="4" x2="9" y2="9" />
                  </svg>
                </div>
                {/* Repeat */}
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#8E8E93"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="17 1 21 5 17 9" />
                    <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                    <polyline points="7 23 3 19 7 15" />
                    <path d="M21 13v2a4 4 0 0 1-4 4H3" />
                  </svg>
                </div>
                {/* Volume */}
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#8E8E93"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                  </svg>
                </div>
              </div>

              {/* Achievements Section */}
              {achievements.length > 0 && (
                <div
                  style={{
                    marginTop: "24px",
                    paddingTop: "24px",
                    borderTop: "1px solid rgba(255, 255, 255, 0.1)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "11px",
                      fontWeight: "700",
                      color: "#8E8E93",
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      marginBottom: "12px",
                    }}
                  >
                    Achievements
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    {achievements.slice(0, 2).map((achievement, idx) => {
                      // Handle achievement as string or object
                      const achievementText =
                        typeof achievement === "string"
                          ? achievement
                          : achievement.title || achievement.name || "";
                      const achievementDesc = typeof achievement === "object" ? achievement.description : "";
                      const achievementDate = typeof achievement === "object" ? achievement.date : "";

                      return (
                        <div
                          key={idx}
                          style={{
                            padding: "12px",
                            background: "rgba(250, 36, 60, 0.1)",
                            borderRadius: "8px",
                            borderLeft: "3px solid #FA243C",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "12px",
                              fontWeight: "600",
                              color: "#FFFFFF",
                              marginBottom: "4px",
                              lineHeight: "1.4",
                            }}
                          >
                            {achievementText}
                          </div>
                          {(achievementDesc || achievementDate) && (
                            <div
                              style={{
                                fontSize: "10px",
                                fontWeight: "400",
                                color: "#8E8E93",
                                lineHeight: "1.3",
                              }}
                            >
                              {achievementDate && `${achievementDate}`}
                              {achievementDesc &&
                                typeof achievementDesc === "string" &&
                                ` • ${achievementDesc.substring(0, 50)}${
                                  achievementDesc.length > 50 ? "..." : ""
                                }`}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Right: Album Details */}
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: "20px",
                overflow: "auto",
              }}
            >
              {/* Album Header */}
              <div>
                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: "700",
                    color: "#8E8E93",
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    marginBottom: "8px",
                  }}
                >
                  Album
                </div>
                <div
                  style={{
                    fontSize: "32px",
                    fontWeight: "700",
                    color: "#FFFFFF",
                    lineHeight: "1.2",
                    letterSpacing: "-0.02em",
                    marginBottom: "8px",
                  }}
                >
                  Professional Portfolio
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#8E8E93",
                    marginBottom: "12px",
                  }}
                >
                  {name} • {new Date().getFullYear()}
                </div>
                {summary && (
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: "400",
                      color: "#C7C7CC",
                      lineHeight: "1.5",
                      maxHeight: "60px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {summary}
                  </div>
                )}
              </div>

              {/* Track List (Skills as Songs) */}
              <div>
                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: "700",
                    color: "#8E8E93",
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    marginBottom: "12px",
                  }}
                >
                  Songs
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0px",
                  }}
                >
                  {allSkills.slice(0, 8).map((skill, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "16px",
                        padding: "10px 12px",
                        borderRadius: "6px",
                        cursor: "pointer",
                        transition: "background 0.2s",
                        background: idx === 0 ? "rgba(255, 255, 255, 0.05)" : "transparent",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "13px",
                          fontWeight: "500",
                          color: "#8E8E93",
                          width: "24px",
                          textAlign: "right",
                        }}
                      >
                        {idx + 1}
                      </div>
                      {idx === 0 && (
                        <div
                          style={{
                            fontSize: "14px",
                            color: "#FA243C",
                          }}
                        >
                          ▶
                        </div>
                      )}
                      <div
                        style={{
                          flex: 1,
                        }}
                      >
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "400",
                            color: idx === 0 ? "#FA243C" : "#FFFFFF",
                            marginBottom: "2px",
                          }}
                        >
                          {skill}
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            fontWeight: "400",
                            color: "#8E8E93",
                          }}
                        >
                          {name}
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: "13px",
                          fontWeight: "400",
                          color: "#8E8E93",
                        }}
                      >
                        {Math.floor(Math.random() * 5) + 2}:
                        {String(Math.floor(Math.random() * 60)).padStart(2, "0")}
                      </div>
                      <div
                        style={{
                          fontSize: "18px",
                          color: "#8E8E93",
                          opacity: 0.5,
                        }}
                      >
                        ⋯
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Credits & Projects Section - 2 Columns */}
              <div
                style={{
                  marginTop: "20px",
                  display: "flex",
                  gap: "32px",
                }}
              >
                {/* Left Column - Credits */}
                <div
                  style={{
                    flex: 1,
                  }}
                >
                  <div
                    style={{
                      fontSize: "11px",
                      fontWeight: "700",
                      color: "#8E8E93",
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      marginBottom: "12px",
                    }}
                  >
                    Credits
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    {experience.slice(0, 3).map((exp, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: "flex",
                          gap: "12px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "13px",
                            fontWeight: "500",
                            color: "#8E8E93",
                            minWidth: "100px",
                          }}
                        >
                          {exp.title || "Role"}
                        </div>
                        <div
                          style={{
                            fontSize: "13px",
                            fontWeight: "400",
                            color: "#FFFFFF",
                          }}
                        >
                          {exp.company || "Company"} • {exp.startDate || ""} - {exp.endDate || "Present"}
                        </div>
                      </div>
                    ))}
                    {education.slice(0, 1).map((edu, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: "flex",
                          gap: "12px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "13px",
                            fontWeight: "500",
                            color: "#8E8E93",
                            minWidth: "100px",
                          }}
                        >
                          Education
                        </div>
                        <div
                          style={{
                            fontSize: "13px",
                            fontWeight: "400",
                            color: "#FFFFFF",
                          }}
                        >
                          {edu.degree || "Degree"} • {edu.institution || "Institution"}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right Column - Projects (2x2 Grid) */}
                <div
                  style={{
                    flex: 1,
                  }}
                >
                  <div
                    style={{
                      fontSize: "11px",
                      fontWeight: "700",
                      color: "#8E8E93",
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      marginBottom: "12px",
                    }}
                  >
                    Projects
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "12px",
                    }}
                  >
                    {projects.slice(0, 4).map((project, idx) => (
                      <div
                        key={idx}
                        style={{
                          padding: "12px",
                          background: "rgba(255, 255, 255, 0.05)",
                          borderRadius: "8px",
                          border: "1px solid rgba(255, 255, 255, 0.1)",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#FFFFFF",
                            marginBottom: "4px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {project.name || `Project ${idx + 1}`}
                        </div>
                        <div
                          style={{
                            fontSize: "11px",
                            fontWeight: "400",
                            color: "#8E8E93",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {project.role || project.technologies?.[0] || "Project"}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mini Player Bar */}
      <div
        style={{
          height: "72px",
          background: "rgba(45, 45, 50, 0.95)",
          backdropFilter: "blur(40px)",
          WebkitBackdropFilter: "blur(40px)",
          borderTop: "1px solid rgba(255, 255, 255, 0.1)",
          display: "flex",
          alignItems: "center",
          padding: "0 24px",
          gap: "20px",
        }}
      >
        {/* Play Icon */}
        <div
          style={{
            fontSize: "20px",
            color: "#FA243C",
          }}
        >
          ▶
        </div>

        {/* Track Info */}
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <div>
            <div
              style={{
                fontSize: "14px",
                fontWeight: "500",
                color: "#FFFFFF",
              }}
            >
              {currentTitle}
            </div>
            <div
              style={{
                fontSize: "12px",
                fontWeight: "400",
                color: "#8E8E93",
              }}
            >
              {name}
            </div>
          </div>
        </div>

        {/* Progress Bar with Time */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            flex: 1,
          }}
        >
          {/* Current Time */}
          <div
            style={{
              fontSize: "11px",
              color: "#8E8E93",
              minWidth: "40px",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            2:34
          </div>

          {/* Progress Bar */}
          <div
            style={{
              flex: 1,
              height: "4px",
              background: "rgba(255, 255, 255, 0.1)",
              borderRadius: "2px",
              position: "relative",
              cursor: "pointer",
            }}
          >
            <div
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                width: "45%",
                height: "100%",
                background: "#FA243C",
                borderRadius: "2px",
              }}
            />
            {/* Progress Thumb */}
            <div
              style={{
                position: "absolute",
                left: "45%",
                top: "50%",
                transform: "translate(-50%, -50%)",
                width: "12px",
                height: "12px",
                borderRadius: "50%",
                background: "#FFFFFF",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
              }}
            />
          </div>

          {/* Total Time */}
          <div
            style={{
              fontSize: "11px",
              color: "#8E8E93",
              minWidth: "40px",
              textAlign: "right",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            5:42
          </div>
        </div>

        {/* Contact Info */}
        <div
          style={{
            display: "flex",
            gap: "20px",
            alignItems: "center",
          }}
        >
          {email && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "11px",
                color: "#8E8E93",
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#8E8E93"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              {email}
            </div>
          )}
          {phone && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "11px",
                color: "#8E8E93",
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#8E8E93"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              {phone}
            </div>
          )}
          {location && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "11px",
                color: "#8E8E93",
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#8E8E93"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              {location}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Card 7 - Discord Desktop Style Resume Card (16:9 - 1920x1080px)
export const DiscordDesktopCard = ({ resumeData, profilePhotoUrl }) => {
  const name = resumeData?.parsedData?.personalInfo?.name || "Your Name";
  const currentTitle = resumeData?.parsedData?.experience?.[0]?.title || "Professional";
  const currentCompany = resumeData?.parsedData?.experience?.[0]?.company || "";
  const location = resumeData?.parsedData?.personalInfo?.location || "Location";
  const email = resumeData?.parsedData?.personalInfo?.email || "";
  const phone = resumeData?.parsedData?.personalInfo?.phone || "";

  // Get top skills
  const allSkills =
    resumeData?.parsedData?.skills?.list ||
    Object.values(resumeData?.parsedData?.skills?.categorized || {}).flat() ||
    [];
  const topSkills = allSkills.slice(0, 6);

  // Get experiences
  const experiences = resumeData?.parsedData?.experience || [];
  const topExperiences = experiences.slice(0, 3);

  // Summary
  const summaryData = resumeData?.parsedData?.summary;
  const summaryText = typeof summaryData === "string" ? summaryData : summaryData?.content || "";
  const shortSummary = summaryText.length > 200 ? summaryText.substring(0, 200) + "..." : summaryText;

  // Get user initials for avatar
  const getInitials = (name) => {
    const names = name.split(" ");
    return names.length > 1 ? `${names[0][0]}${names[1][0]}`.toUpperCase() : name.substring(0, 2).toUpperCase();
  };

  return (
    <div
      style={{
        width: "1920px",
        height: "1080px",
        background: "#36393F",
        display: "flex",
        fontFamily: '"gg sans", "Helvetica Neue", Helvetica, Arial, sans-serif',
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Server List (Left Bar - 72px) */}
      <div
        style={{
          width: "72px",
          background: "#202225",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "12px 0",
          gap: "8px",
        }}
      >
        {/* Home Button */}
        <div
          style={{
            width: "48px",
            height: "48px",
            background: "#5865F2",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "border-radius 0.2s",
            marginBottom: "8px",
          }}
        >
          <svg width="28" height="28" viewBox="0 0 28 21" fill="white">
            <path d="M23.0212 1.67671C21.3107 0.879656 19.5079 0.318797 17.6584 0C17.4062 0.461742 17.1749 0.934541 16.9708 1.4184C15.003 1.12145 12.9974 1.12145 11.0283 1.4184C10.819 0.934541 10.589 0.461744 10.3368 0C8.48074 0.324393 6.67795 0.885118 4.96746 1.68231C1.56727 6.77853 0.649666 11.7538 1.11108 16.652C3.10102 18.1418 5.3262 19.2743 7.69177 20C8.22338 19.2743 8.69519 18.4993 9.09812 17.691C8.32996 17.3997 7.58522 17.0424 6.87684 16.6135C7.06531 16.4762 7.24726 16.3387 7.42403 16.1847C11.5911 18.1749 16.408 18.1749 20.5763 16.1847C20.7531 16.3332 20.9351 16.4762 21.1171 16.6135C20.41 17.0369 19.6639 17.3997 18.897 17.691C19.3052 18.4993 19.777 19.2689 20.3031 20C22.6712 19.2689 24.8964 18.1364 26.8876 16.652C27.43 10.9731 25.9665 6.04728 23.0212 1.67671ZM9.68041 13.6383C8.39754 13.6383 7.34085 12.4453 7.34085 10.994C7.34085 9.54272 8.37155 8.34973 9.68041 8.34973C10.9893 8.34973 12.0395 9.54272 12.0187 10.994C12.0187 12.4453 10.9828 13.6383 9.68041 13.6383ZM18.3161 13.6383C17.0332 13.6383 15.9765 12.4453 15.9765 10.994C15.9765 9.54272 17.0124 8.34973 18.3161 8.34973C19.6184 8.34973 20.6751 9.54272 20.6543 10.994C20.6543 12.4453 19.6184 13.6383 18.3161 13.6383Z" />
          </svg>
        </div>

        {/* Divider */}
        <div
          style={{
            width: "32px",
            height: "2px",
            background: "#40444B",
            borderRadius: "1px",
          }}
        />

        {/* Server Icons */}
        <div
          style={{
            width: "48px",
            height: "48px",
            background: "#36393F",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            fontSize: "18px",
            fontWeight: "600",
            color: "#5865F2",
            transition: "border-radius 0.2s",
          }}
        >
          {getInitials(name)}
        </div>

        <div
          style={{
            width: "48px",
            height: "48px",
            background: "#36393F",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            fontSize: "24px",
            color: "#3BA55D",
            transition: "border-radius 0.2s",
          }}
        >
          +
        </div>
      </div>

      {/* Channel Sidebar (240px) */}
      <div
        style={{
          width: "240px",
          background: "#2F3136",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Server Name */}
        <div
          style={{
            height: "48px",
            padding: "0 16px",
            borderBottom: "1px solid #202225",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
          }}
        >
          <div
            style={{
              fontSize: "16px",
              fontWeight: "700",
              color: "#FFFFFF",
              letterSpacing: "0.02em",
            }}
          >
            {name.split(" ")[0]}'s Server
          </div>
          <svg width="18" height="18" viewBox="0 0 18 18" fill="#B9BBBE">
            <path d="M9.3 5.3L5.3 9.3 9.3 13.3" transform="rotate(-90 9 9)" />
          </svg>
        </div>

        {/* Channels List */}
        <div
          style={{
            flex: 1,
            padding: "16px 8px",
            overflowY: "auto",
          }}
        >
          {/* Text Channels */}
          <div
            style={{
              marginBottom: "4px",
              padding: "0 8px",
              fontSize: "12px",
              fontWeight: "600",
              color: "#96989D",
              letterSpacing: "0.02em",
              textTransform: "uppercase",
            }}
          >
            TEXT CHANNELS
          </div>

          <div
            style={{
              padding: "6px 8px",
              margin: "1px 0",
              borderRadius: "4px",
              background: "#40444B",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              cursor: "pointer",
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#B9BBBE">
              <path d="M5.88657 21C5.57547 21 5.3399 20.7189 5.39427 20.4126L6.00001 17H2.59511C2.28449 17 2.04905 16.7198 2.10259 16.4138L2.27759 15.4138C2.31946 15.1746 2.52722 15 2.77011 15H6.35001L7.41001 9H4.00511C3.69449 9 3.45905 8.71977 3.51259 8.41381L3.68759 7.41381C3.72946 7.17456 3.93722 7 4.18011 7H7.76001L8.39677 3.41262C8.43914 3.17391 8.64664 3 8.88907 3H9.87344C10.1845 3 10.4201 3.28107 10.3657 3.58738L9.76001 7H15.76L16.3968 3.41262C16.4391 3.17391 16.6466 3 16.8891 3H17.8734C18.1845 3 18.4201 3.28107 18.3657 3.58738L17.76 7H21.1649C21.4755 7 21.711 7.28023 21.6574 7.58619L21.4824 8.58619C21.4406 8.82544 21.2328 9 20.9899 9H17.41L16.35 15H19.7549C20.0655 15 20.301 15.2802 20.2474 15.5862L20.0724 16.5862C20.0306 16.8254 19.8228 17 19.5799 17H16L15.3632 20.5874C15.3209 20.8261 15.1134 21 14.8709 21H13.8866C13.5755 21 13.3399 20.7189 13.3943 20.4126L14 17H8.00001L7.36325 20.5874C7.32088 20.8261 7.11337 21 6.87094 21H5.88657ZM9.41001 9L8.35001 15H14.35L15.41 9H9.41001Z" />
            </svg>
            <span
              style={{
                fontSize: "16px",
                fontWeight: "500",
                color: "#FFFFFF",
              }}
            >
              💼-hire-me
            </span>
          </div>

          <div
            style={{
              padding: "6px 8px",
              margin: "1px 0",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              cursor: "pointer",
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#96989D">
              <path d="M5.88657 21C5.57547 21 5.3399 20.7189 5.39427 20.4126L6.00001 17H2.59511C2.28449 17 2.04905 16.7198 2.10259 16.4138L2.27759 15.4138C2.31946 15.1746 2.52722 15 2.77011 15H6.35001L7.41001 9H4.00511C3.69449 9 3.45905 8.71977 3.51259 8.41381L3.68759 7.41381C3.72946 7.17456 3.93722 7 4.18011 7H7.76001L8.39677 3.41262C8.43914 3.17391 8.64664 3 8.88907 3H9.87344C10.1845 3 10.4201 3.28107 10.3657 3.58738L9.76001 7H15.76L16.3968 3.41262C16.4391 3.17391 16.6466 3 16.8891 3H17.8734C18.1845 3 18.4201 3.28107 18.3657 3.58738L17.76 7H21.1649C21.4755 7 21.711 7.28023 21.6574 7.58619L21.4824 8.58619C21.4406 8.82544 21.2328 9 20.9899 9H17.41L16.35 15H19.7549C20.0655 15 20.301 15.2802 20.2474 15.5862L20.0724 16.5862C20.0306 16.8254 19.8228 17 19.5799 17H16L15.3632 20.5874C15.3209 20.8261 15.1134 21 14.8709 21H13.8866C13.5755 21 13.3399 20.7189 13.3943 20.4126L14 17H8.00001L7.36325 20.5874C7.32088 20.8261 7.11337 21 6.87094 21H5.88657ZM9.41001 9L8.35001 15H14.35L15.41 9H9.41001Z" />
            </svg>
            <span
              style={{
                fontSize: "16px",
                fontWeight: "500",
                color: "#96989D",
              }}
            >
              📊-skills
            </span>
          </div>

          <div
            style={{
              padding: "6px 8px",
              margin: "1px 0",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              cursor: "pointer",
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#96989D">
              <path d="M5.88657 21C5.57547 21 5.3399 20.7189 5.39427 20.4126L6.00001 17H2.59511C2.28449 17 2.04905 16.7198 2.10259 16.4138L2.27759 15.4138C2.31946 15.1746 2.52722 15 2.77011 15H6.35001L7.41001 9H4.00511C3.69449 9 3.45905 8.71977 3.51259 8.41381L3.68759 7.41381C3.72946 7.17456 3.93722 7 4.18011 7H7.76001L8.39677 3.41262C8.43914 3.17391 8.64664 3 8.88907 3H9.87344C10.1845 3 10.4201 3.28107 10.3657 3.58738L9.76001 7H15.76L16.3968 3.41262C16.4391 3.17391 16.6466 3 16.8891 3H17.8734C18.1845 3 18.4201 3.28107 18.3657 3.58738L17.76 7H21.1649C21.4755 7 21.711 7.28023 21.6574 7.58619L21.4824 8.58619C21.4406 8.82544 21.2328 9 20.9899 9H17.41L16.35 15H19.7549C20.0655 15 20.301 15.2802 20.2474 15.5862L20.0724 16.5862C20.0306 16.8254 19.8228 17 19.5799 17H16L15.3632 20.5874C15.3209 20.8261 15.1134 21 14.8709 21H13.8866C13.5755 21 13.3399 20.7189 13.3943 20.4126L14 17H8.00001L7.36325 20.5874C7.32088 20.8261 7.11337 21 6.87094 21H5.88657ZM9.41001 9L8.35001 15H14.35L15.41 9H9.41001Z" />
            </svg>
            <span
              style={{
                fontSize: "16px",
                fontWeight: "500",
                color: "#96989D",
              }}
            >
              💼-experience
            </span>
          </div>

          <div
            style={{
              padding: "6px 8px",
              margin: "1px 0",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              cursor: "pointer",
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#96989D">
              <path d="M5.88657 21C5.57547 21 5.3399 20.7189 5.39427 20.4126L6.00001 17H2.59511C2.28449 17 2.04905 16.7198 2.10259 16.4138L2.27759 15.4138C2.31946 15.1746 2.52722 15 2.77011 15H6.35001L7.41001 9H4.00511C3.69449 9 3.45905 8.71977 3.51259 8.41381L3.68759 7.41381C3.72946 7.17456 3.93722 7 4.18011 7H7.76001L8.39677 3.41262C8.43914 3.17391 8.64664 3 8.88907 3H9.87344C10.1845 3 10.4201 3.28107 10.3657 3.58738L9.76001 7H15.76L16.3968 3.41262C16.4391 3.17391 16.6466 3 16.8891 3H17.8734C18.1845 3 18.4201 3.28107 18.3657 3.58738L17.76 7H21.1649C21.4755 7 21.711 7.28023 21.6574 7.58619L21.4824 8.58619C21.4406 8.82544 21.2328 9 20.9899 9H17.41L16.35 15H19.7549C20.0655 15 20.301 15.2802 20.2474 15.5862L20.0724 16.5862C20.0306 16.8254 19.8228 17 19.5799 17H16L15.3632 20.5874C15.3209 20.8261 15.1134 21 14.8709 21H13.8866C13.5755 21 13.3399 20.7189 13.3943 20.4126L14 17H8.00001L7.36325 20.5874C7.32088 20.8261 7.11337 21 6.87094 21H5.88657ZM9.41001 9L8.35001 15H14.35L15.41 9H9.41001Z" />
            </svg>
            <span
              style={{
                fontSize: "16px",
                fontWeight: "500",
                color: "#96989D",
              }}
            >
              📧-contact
            </span>
          </div>

          {/* Voice Channels */}
          <div
            style={{
              marginTop: "16px",
              marginBottom: "4px",
              padding: "0 8px",
              fontSize: "12px",
              fontWeight: "600",
              color: "#96989D",
              letterSpacing: "0.02em",
              textTransform: "uppercase",
            }}
          >
            VOICE CHANNELS
          </div>

          <div
            style={{
              padding: "6px 8px",
              margin: "1px 0",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              cursor: "pointer",
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#96989D">
              <path d="M11.383 3.07904C11.009 2.92504 10.579 3.01004 10.293 3.29604L6 8.00004H3C2.45 8.00004 2 8.45004 2 9.00004V15C2 15.55 2.45 16 3 16H6L10.293 20.71C10.579 20.996 11.009 21.082 11.383 20.927C11.757 20.772 12 20.407 12 20V4.00004C12 3.59304 11.757 3.22804 11.383 3.07904ZM14 5.00004V7.00004C16.757 7.00004 19 9.24304 19 12C19 14.757 16.757 17 14 17V19C17.86 19 21 15.86 21 12C21 8.14004 17.86 5.00004 14 5.00004ZM14 9.00004V11C15.654 11 17 12.346 17 14C17 15.654 15.654 17 14 17V15C14.551 15 15 14.551 15 14C15 13.449 14.551 13 14 13V11C15.654 11 17 12.346 17 14C17 15.654 15.654 17 14 17V19C16.757 19 19 16.757 19 14C19 11.243 16.757 9.00004 14 9.00004Z" />
            </svg>
            <span
              style={{
                fontSize: "16px",
                fontWeight: "500",
                color: "#96989D",
              }}
            >
              General
            </span>
          </div>

          <div
            style={{
              padding: "6px 8px",
              margin: "1px 0",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              cursor: "pointer",
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#96989D">
              <path d="M11.383 3.07904C11.009 2.92504 10.579 3.01004 10.293 3.29604L6 8.00004H3C2.45 8.00004 2 8.45004 2 9.00004V15C2 15.55 2.45 16 3 16H6L10.293 20.71C10.579 20.996 11.009 21.082 11.383 20.927C11.757 20.772 12 20.407 12 20V4.00004C12 3.59304 11.757 3.22804 11.383 3.07904ZM14 5.00004V7.00004C16.757 7.00004 19 9.24304 19 12C19 14.757 16.757 17 14 17V19C17.86 19 21 15.86 21 12C21 8.14004 17.86 5.00004 14 5.00004ZM14 9.00004V11C15.654 11 17 12.346 17 14C17 15.654 15.654 17 14 17V15C14.551 15 15 14.551 15 14C15 13.449 14.551 13 14 13V11C15.654 11 17 12.346 17 14C17 15.654 15.654 17 14 17V19C16.757 19 19 16.757 19 14C19 11.243 16.757 9.00004 14 9.00004Z" />
            </svg>
            <span
              style={{
                fontSize: "16px",
                fontWeight: "500",
                color: "#96989D",
              }}
            >
              Interview Room
            </span>
          </div>
        </div>

        {/* User Card */}
        <div
          style={{
            height: "52px",
            background: "#292B2F",
            padding: "0 8px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <div
            style={{
              position: "relative",
              width: "32px",
              height: "32px",
            }}
          >
            {profilePhotoUrl ? (
              <img
                src={profilePhotoUrl}
                alt="Profile"
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  background: "#5865F2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#FFFFFF",
                }}
              >
                {getInitials(name)}
              </div>
            )}
            <div
              style={{
                position: "absolute",
                bottom: "-2px",
                right: "-2px",
                width: "14px",
                height: "14px",
                background: "#3BA55D",
                borderRadius: "50%",
                border: "3px solid #292B2F",
              }}
            />
          </div>
          <div
            style={{
              flex: 1,
              minWidth: 0,
            }}
          >
            <div
              style={{
                fontSize: "14px",
                fontWeight: "600",
                color: "#FFFFFF",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {name.split(" ")[0]}
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "#B9BBBE",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              🟢 Online
            </div>
          </div>
        </div>
      </div>

      {/* Main Chat Area (Flexible) */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          background: "#36393F",
        }}
      >
        {/* Channel Header */}
        <div
          style={{
            height: "48px",
            padding: "0 16px",
            borderBottom: "1px solid #202225",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="#96989D">
            <path d="M5.88657 21C5.57547 21 5.3399 20.7189 5.39427 20.4126L6.00001 17H2.59511C2.28449 17 2.04905 16.7198 2.10259 16.4138L2.27759 15.4138C2.31946 15.1746 2.52722 15 2.77011 15H6.35001L7.41001 9H4.00511C3.69449 9 3.45905 8.71977 3.51259 8.41381L3.68759 7.41381C3.72946 7.17456 3.93722 7 4.18011 7H7.76001L8.39677 3.41262C8.43914 3.17391 8.64664 3 8.88907 3H9.87344C10.1845 3 10.4201 3.28107 10.3657 3.58738L9.76001 7H15.76L16.3968 3.41262C16.4391 3.17391 16.6466 3 16.8891 3H17.8734C18.1845 3 18.4201 3.28107 18.3657 3.58738L17.76 7H21.1649C21.4755 7 21.711 7.28023 21.6574 7.58619L21.4824 8.58619C21.4406 8.82544 21.2328 9 20.9899 9H17.41L16.35 15H19.7549C20.0655 15 20.301 15.2802 20.2474 15.5862L20.0724 16.5862C20.0306 16.8254 19.8228 17 19.5799 17H16L15.3632 20.5874C15.3209 20.8261 15.1134 21 14.8709 21H13.8866C13.5755 21 13.3399 20.7189 13.3943 20.4126L14 17H8.00001L7.36325 20.5874C7.32088 20.8261 7.11337 21 6.87094 21H5.88657ZM9.41001 9L8.35001 15H14.35L15.41 9H9.41001Z" />
          </svg>
          <span
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: "#FFFFFF",
            }}
          >
            💼-hire-me
          </span>
          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              gap: "16px",
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="#B9BBBE" style={{ cursor: "pointer" }}>
              <path d="M19.738 10H4.262a1 1 0 0 0-.95 1.316l2.5 8a1 1 0 0 0 .95.684h10.476a1 1 0 0 0 .95-.684l2.5-8a1 1 0 0 0-.95-1.316ZM14 14v2h-4v-2h4Z" />
            </svg>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="#B9BBBE" style={{ cursor: "pointer" }}>
              <path d="M12 2C6.486 2 2 6.486 2 12C2 17.514 6.486 22 12 22C17.514 22 22 17.514 22 12C22 6.486 17.514 2 12 2ZM12 20C7.589 20 4 16.411 4 12C4 7.589 7.589 4 12 4C16.411 4 20 7.589 20 12C20 16.411 16.411 20 12 20Z" />
              <circle cx="12" cy="12" r="1" />
            </svg>
          </div>
        </div>

        {/* Chat Messages */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "16px",
          }}
        >
          {/* Pinned Message */}
          <div
            style={{
              background: "#2F3136",
              padding: "12px 16px",
              borderRadius: "4px",
              borderLeft: "4px solid #5865F2",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                fontSize: "12px",
                fontWeight: "600",
                color: "#5865F2",
                marginBottom: "4px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              📌 PINNED MESSAGE
            </div>
            <div
              style={{
                fontSize: "14px",
                color: "#DCDDDE",
              }}
            >
              "Welcome! Check out my resume and portfolio below"
            </div>
          </div>

          {/* Resume Bot Message */}
          <div
            style={{
              display: "flex",
              gap: "16px",
              marginBottom: "16px",
              padding: "2px 0",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                background: "#5865F2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" />
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "4px",
                }}
              >
                <span
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#FFFFFF",
                  }}
                >
                  {name}
                </span>
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: "600",
                    color: "#FFFFFF",
                    background: "#5865F2",
                    padding: "2px 6px",
                    borderRadius: "3px",
                  }}
                >
                  BOT
                </span>
                <span
                  style={{
                    fontSize: "12px",
                    color: "#96989D",
                  }}
                >
                  Today at 9:00 AM
                </span>
              </div>
              <div
                style={{
                  fontSize: "16px",
                  color: "#DCDDDE",
                  lineHeight: "1.375rem",
                  marginBottom: "8px",
                }}
              >
                Welcome to my Discord Resume! I'm a{" "}
                <span style={{ fontWeight: "600", color: "#5865F2" }}>{currentTitle}</span>{" "}
                {currentCompany && `at ${currentCompany}`}
              </div>
              {shortSummary && (
                <div
                  style={{
                    fontSize: "14px",
                    color: "#DCDDDE",
                    lineHeight: "1.375rem",
                    marginBottom: "12px",
                  }}
                >
                  {shortSummary}
                </div>
              )}

              {/* Embed Card - Skills */}
              <div
                style={{
                  background: "#2F3136",
                  border: "1px solid #202225",
                  borderLeft: "4px solid #5865F2",
                  borderRadius: "4px",
                  padding: "16px",
                  marginBottom: "12px",
                  maxWidth: "520px",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#FFFFFF",
                    marginBottom: "12px",
                  }}
                >
                  🎯 TECHNICAL SKILLS
                </div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                  }}
                >
                  {topSkills.map((skill, idx) => (
                    <div
                      key={idx}
                      style={{
                        background: "#5865F2",
                        color: "#FFFFFF",
                        padding: "4px 10px",
                        borderRadius: "3px",
                        fontSize: "12px",
                        fontWeight: "500",
                      }}
                    >
                      @{skill}
                    </div>
                  ))}
                </div>
              </div>

              {/* Reactions */}
              <div
                style={{
                  display: "flex",
                  gap: "4px",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    background: "#2F3136",
                    border: "1px solid #202225",
                    borderRadius: "4px",
                    padding: "4px 8px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    cursor: "pointer",
                  }}
                >
                  <span style={{ fontSize: "14px" }}>👍</span>
                  <span style={{ fontSize: "12px", color: "#DCDDDE", fontWeight: "500" }}>25</span>
                </div>
                <div
                  style={{
                    background: "#2F3136",
                    border: "1px solid #202225",
                    borderRadius: "4px",
                    padding: "4px 8px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    cursor: "pointer",
                  }}
                >
                  <span style={{ fontSize: "14px" }}>🔥</span>
                  <span style={{ fontSize: "12px", color: "#DCDDDE", fontWeight: "500" }}>15</span>
                </div>
                <div
                  style={{
                    background: "#2F3136",
                    border: "1px solid #202225",
                    borderRadius: "4px",
                    padding: "4px 8px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    cursor: "pointer",
                  }}
                >
                  <span style={{ fontSize: "14px" }}>⭐</span>
                  <span style={{ fontSize: "12px", color: "#DCDDDE", fontWeight: "500" }}>8</span>
                </div>
              </div>
            </div>
          </div>

          {/* Experience Message */}
          {topExperiences.length > 0 && (
            <div
              style={{
                display: "flex",
                gap: "16px",
                marginBottom: "16px",
                padding: "2px 0",
              }}
            >
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  background: "#3BA55D",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  fontSize: "20px",
                }}
              >
                💼
              </div>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "4px",
                  }}
                >
                  <span
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#FFFFFF",
                    }}
                  >
                    Experience Bot
                  </span>
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: "600",
                      color: "#FFFFFF",
                      background: "#3BA55D",
                      padding: "2px 6px",
                      borderRadius: "3px",
                    }}
                  >
                    BOT
                  </span>
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#96989D",
                    }}
                  >
                    Today at 9:05 AM
                  </span>
                </div>

                {/* Experience Embed */}
                <div
                  style={{
                    background: "#2F3136",
                    border: "1px solid #202225",
                    borderLeft: "4px solid #3BA55D",
                    borderRadius: "4px",
                    padding: "16px",
                    maxWidth: "520px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#FFFFFF",
                      marginBottom: "12px",
                    }}
                  >
                    💼 WORK EXPERIENCE
                  </div>
                  {topExperiences.map((exp, idx) => (
                    <div
                      key={idx}
                      style={{
                        marginBottom: idx < topExperiences.length - 1 ? "12px" : "0",
                        paddingBottom: idx < topExperiences.length - 1 ? "12px" : "0",
                        borderBottom: idx < topExperiences.length - 1 ? "1px solid #40444B" : "none",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#FFFFFF",
                          marginBottom: "2px",
                        }}
                      >
                        {exp.title}
                      </div>
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#B9BBBE",
                          marginBottom: "4px",
                        }}
                      >
                        {exp.company} • {exp.startDate} - {exp.endDate || "Present"}
                      </div>
                      {exp.description && (
                        <div
                          style={{
                            fontSize: "13px",
                            color: "#DCDDDE",
                            lineHeight: "1.4",
                          }}
                        >
                          {exp.description.substring(0, 100)}...
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Contact Command Message */}
          <div
            style={{
              display: "flex",
              gap: "16px",
              padding: "2px 0",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                background: "#ED4245",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                fontSize: "20px",
              }}
            >
              📧
            </div>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "4px",
                }}
              >
                <span
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#FFFFFF",
                  }}
                >
                  Contact Bot
                </span>
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: "600",
                    color: "#FFFFFF",
                    background: "#ED4245",
                    padding: "2px 6px",
                    borderRadius: "3px",
                  }}
                >
                  BOT
                </span>
                <span
                  style={{
                    fontSize: "12px",
                    color: "#96989D",
                  }}
                >
                  Today at 9:10 AM
                </span>
              </div>

              {/* Contact Embed */}
              <div
                style={{
                  background: "#2F3136",
                  border: "1px solid #202225",
                  borderLeft: "4px solid #ED4245",
                  borderRadius: "4px",
                  padding: "16px",
                  maxWidth: "520px",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#FFFFFF",
                    marginBottom: "12px",
                  }}
                >
                  📧 CONTACT INFORMATION
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  {email && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        fontSize: "13px",
                        color: "#DCDDDE",
                      }}
                    >
                      <span>📧</span>
                      <span style={{ color: "#00B0F4" }}>{email}</span>
                    </div>
                  )}
                  {phone && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        fontSize: "13px",
                        color: "#DCDDDE",
                      }}
                    >
                      <span>📱</span>
                      <span>{phone}</span>
                    </div>
                  )}
                  {location && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        fontSize: "13px",
                        color: "#DCDDDE",
                      }}
                    >
                      <span>📍</span>
                      <span>{location}</span>
                    </div>
                  )}
                </div>
                <div
                  style={{
                    marginTop: "12px",
                    paddingTop: "12px",
                    borderTop: "1px solid #40444B",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#96989D",
                      marginBottom: "6px",
                    }}
                  >
                    STATUS
                  </div>
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      background: "#3BA55D",
                      padding: "4px 10px",
                      borderRadius: "3px",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#FFFFFF",
                    }}
                  >
                    <div
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        background: "#FFFFFF",
                      }}
                    />
                    Available to hire
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Message Input */}
        <div
          style={{
            padding: "16px",
            background: "#36393F",
          }}
        >
          <div
            style={{
              background: "#40444B",
              borderRadius: "8px",
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="#B9BBBE" style={{ cursor: "pointer" }}>
              <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" />
            </svg>
            <div
              style={{
                flex: 1,
                fontSize: "15px",
                color: "#96989D",
              }}
            >
              Message @{name.split(" ")[0]}
            </div>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="#B9BBBE" style={{ cursor: "pointer" }}>
              <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" />
            </svg>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="#B9BBBE" style={{ cursor: "pointer" }}>
              <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" />
            </svg>
          </div>
        </div>
      </div>

      {/* Members List (Right Sidebar - 240px) */}
      <div
        style={{
          width: "240px",
          background: "#2F3136",
          display: "flex",
          flexDirection: "column",
          padding: "16px 8px",
        }}
      >
        {/* Online Members */}
        <div
          style={{
            marginBottom: "4px",
            padding: "0 8px",
            fontSize: "12px",
            fontWeight: "600",
            color: "#96989D",
            letterSpacing: "0.02em",
            textTransform: "uppercase",
          }}
        >
          ROLES
        </div>

        {/* Founder Role */}
        <div
          style={{
            padding: "8px",
            margin: "1px 0",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            cursor: "pointer",
          }}
        >
          <div
            style={{
              position: "relative",
              width: "32px",
              height: "32px",
            }}
          >
            {profilePhotoUrl ? (
              <img
                src={profilePhotoUrl}
                alt="Profile"
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  background: "#5865F2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#FFFFFF",
                }}
              >
                {getInitials(name)}
              </div>
            )}
            <div
              style={{
                position: "absolute",
                bottom: "-2px",
                right: "-2px",
                width: "12px",
                height: "12px",
                background: "#3BA55D",
                borderRadius: "50%",
                border: "3px solid #2F3136",
              }}
            />
          </div>
          <div
            style={{
              flex: 1,
              minWidth: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                marginBottom: "2px",
              }}
            >
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#FFFFFF",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {name.split(" ")[0]}
              </span>
              <span
                style={{
                  fontSize: "10px",
                  fontWeight: "600",
                  color: "#FFFFFF",
                  background: "#5865F2",
                  padding: "1px 4px",
                  borderRadius: "2px",
                }}
              >
                BOT
              </span>
            </div>
            <div
              style={{
                fontSize: "11px",
                color: "#B9BBBE",
              }}
            >
              👑 Founder
            </div>
          </div>
        </div>

        {/* Divider */}
        <div
          style={{
            height: "1px",
            background: "#40444B",
            margin: "12px 8px",
          }}
        />

        {/* Role Badges Section */}
        <div
          style={{
            marginBottom: "4px",
            padding: "0 8px",
            fontSize: "12px",
            fontWeight: "600",
            color: "#96989D",
            letterSpacing: "0.02em",
            textTransform: "uppercase",
          }}
        >
          BADGES
        </div>

        <div
          style={{
            padding: "8px",
            display: "flex",
            flexDirection: "column",
            gap: "6px",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              background: "#5865F2",
              padding: "4px 8px",
              borderRadius: "3px",
              fontSize: "11px",
              fontWeight: "600",
              color: "#FFFFFF",
            }}
          >
            🚀 Senior Dev
          </div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              background: "#3BA55D",
              padding: "4px 8px",
              borderRadius: "3px",
              fontSize: "11px",
              fontWeight: "600",
              color: "#FFFFFF",
            }}
          >
            💼 Available
          </div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              background: "#ED4245",
              padding: "4px 8px",
              borderRadius: "3px",
              fontSize: "11px",
              fontWeight: "600",
              color: "#FFFFFF",
            }}
          >
            ⚡ Fast Responder
          </div>
        </div>

        {/* Divider */}
        <div
          style={{
            height: "1px",
            background: "#40444B",
            margin: "12px 8px",
          }}
        />

        {/* Server Stats */}
        <div
          style={{
            marginBottom: "4px",
            padding: "0 8px",
            fontSize: "12px",
            fontWeight: "600",
            color: "#96989D",
            letterSpacing: "0.02em",
            textTransform: "uppercase",
          }}
        >
          SERVER INFO
        </div>

        <div
          style={{
            padding: "8px",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          <div
            style={{
              fontSize: "12px",
              color: "#DCDDDE",
            }}
          >
            <span style={{ color: "#96989D" }}>Created:</span>{" "}
            {new Date().toLocaleDateString("en-US", { month: "short", year: "numeric" })}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: "#DCDDDE",
            }}
          >
            <span style={{ color: "#96989D" }}>Response:</span> {"< 24 hours"}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: "#DCDDDE",
            }}
          >
            <span style={{ color: "#96989D" }}>Experience:</span> {experiences.length}+ years
          </div>
        </div>
      </div>
    </div>
  );
};

// Card 7 - GitHub Profile Desktop Style (16:9 - 1920x1080px)
export const GitHubProfileCard = ({ resumeData, profilePhotoUrl }) => {
  const name = resumeData?.parsedData?.personalInfo?.name || "Your Name";
  const username = resumeData?.parsedData?.personalInfo?.email?.split("@")[0] || "username";
  const location = resumeData?.parsedData?.personalInfo?.location || "Location";
  const email = resumeData?.parsedData?.personalInfo?.email || "";
  const linkedin =
    resumeData?.parsedData?.personalInfo?.linkedIn || resumeData?.parsedData?.personalInfo?.linkedin || "";
  const github = resumeData?.parsedData?.personalInfo?.github || "";
  const website = resumeData?.parsedData?.personalInfo?.website || "";

  // Summary/Bio
  const summaryData = resumeData?.parsedData?.summary;
  const summaryText = typeof summaryData === "string" ? summaryData : summaryData?.content || "";
  const bio = summaryText || "Full-Stack Developer | Open Source Enthusiast";

  // Skills for languages and tech stack
  const allSkills =
    resumeData?.parsedData?.skills?.list ||
    Object.values(resumeData?.parsedData?.skills?.categorized || {}).flat() ||
    [];

  // Language colors map
  const languageColors = {
    JavaScript: "#F1E05A",
    TypeScript: "#3178C6",
    Python: "#3572A5",
    HTML: "#E34C26",
    CSS: "#563D7C",
    React: "#61DAFB",
    Vue: "#4FC08D",
    Java: "#B07219",
    "C++": "#F34B7D",
    Ruby: "#701516",
    Go: "#00ADD8",
    Rust: "#DEA584",
    PHP: "#4F5D95",
    Swift: "#FA7343",
    Kotlin: "#A97BFF",
  };

  // Get top 5 skills with percentages
  const topLanguages = allSkills.slice(0, 5).map((skill, idx) => {
    const percentages = [42, 28, 18, 8, 4]; // Decreasing percentages
    return {
      name: skill,
      percentage: percentages[idx] || 2,
      color: languageColors[skill] || "#8B949E",
    };
  });

  // Experience for projects/repos
  const experiences = resumeData?.parsedData?.experience || [];
  const projects = resumeData?.parsedData?.projects || [];

  // Create pinned repos from experience and projects
  const pinnedRepos = [];

  if (experiences.length > 0) {
    experiences.slice(0, 3).forEach((exp, idx) => {
      pinnedRepos.push({
        name: exp.company?.toLowerCase().replace(/\s+/g, "-") || `project-${idx + 1}`,
        description: exp.title || "Professional Experience",
        language: topLanguages[idx % topLanguages.length]?.name || "JavaScript",
        stars: Math.floor(Math.random() * 500) + 50,
        forks: Math.floor(Math.random() * 100) + 10,
      });
    });
  }

  if (projects.length > 0 && pinnedRepos.length < 6) {
    projects.slice(0, 6 - pinnedRepos.length).forEach((proj, idx) => {
      pinnedRepos.push({
        name: proj.name?.toLowerCase().replace(/\s+/g, "-") || `repo-${idx + 1}`,
        description: proj.description || "Project Repository",
        language: topLanguages[(pinnedRepos.length + idx) % topLanguages.length]?.name || "TypeScript",
        stars: Math.floor(Math.random() * 300) + 20,
        forks: Math.floor(Math.random() * 80) + 5,
      });
    });
  }

  // Fill remaining slots if needed
  while (pinnedRepos.length < 6) {
    pinnedRepos.push({
      name: `repository-${pinnedRepos.length + 1}`,
      description: "Open source project",
      language: topLanguages[pinnedRepos.length % topLanguages.length]?.name || "JavaScript",
      stars: Math.floor(Math.random() * 200) + 10,
      forks: Math.floor(Math.random() * 50) + 5,
    });
  }

  // Mock contribution data (52 weeks x 7 days)
  const generateContributions = () => {
    const contributions = [];
    for (let week = 0; week < 52; week++) {
      for (let day = 0; day < 7; day++) {
        const count = Math.floor(Math.random() * 15);
        contributions.push({
          week,
          day,
          count,
          level: count === 0 ? 0 : count < 4 ? 1 : count < 7 ? 2 : count < 10 ? 3 : 4,
        });
      }
    }
    return contributions;
  };

  const contributions = generateContributions();
  const totalContributions = contributions.reduce((sum, c) => sum + c.count, 0);

  // Contribution level colors
  const contributionColors = {
    0: "#161B22",
    1: "#0E4429",
    2: "#006D32",
    3: "#26A641",
    4: "#39D353",
  };

  return (
    <div
      style={{
        width: "1920px",
        height: "1080px",
        background: "#0D1117",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        position: "relative",
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Noto Sans", Helvetica, Arial, sans-serif',
        overflow: "hidden",
        color: "#F0F6FC",
      }}
    >
      {/* GitHub Header */}
      <div
        style={{
          background: "#161B22",
          borderBottom: "1px solid #30363D",
          padding: "16px 40px",
          display: "flex",
          alignItems: "center",
          gap: "24px",
        }}
      >
        {/* GitHub Logo and Name */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
          }}
        >
          {/* GitHub Icon */}
          <svg height="32" viewBox="0 0 16 16" width="32" fill="#F0F6FC">
            <path d="M8 0c4.42 0 8 3.58 8 8a8.013 8.013 0 0 1-5.45 7.59c-.4.08-.55-.17-.55-.38 0-.27.01-1.13.01-2.2 0-.75-.25-1.23-.54-1.48 1.78-.2 3.65-.88 3.65-3.95 0-.88-.31-1.59-.82-2.15.08-.2.36-1.02-.08-2.12 0 0-.67-.22-2.2.82-.64-.18-1.32-.27-2-.27-.68 0-1.36.09-2 .27-1.53-1.03-2.2-.82-2.2-.82-.44 1.1-.16 1.92-.08 2.12-.51.56-.82 1.28-.82 2.15 0 3.06 1.86 3.75 3.64 3.95-.23.2-.44.55-.51 1.07-.46.21-1.61.55-2.33-.66-.15-.24-.6-.83-1.23-.82-.67.01-.27.38.01.53.34.19.73.9.82 1.13.16.45.68 1.31 2.69.94 0 .67.01 1.3.01 1.49 0 .21-.15.45-.55.38A7.995 7.995 0 0 1 0 8c0-4.42 3.58-8 8-8Z"></path>
          </svg>
          <span
            style={{
              fontSize: "24px",
              fontWeight: "600",
              color: "#F0F6FC",
            }}
          >
            GitHub
          </span>
        </div>

        {/* Navigation Tabs */}
        <div
          style={{
            display: "flex",
            gap: "4px",
            flex: 1,
          }}
        >
          {/* Overview Tab - Active */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "600",
              color: "#F0F6FC",
              cursor: "pointer",
            }}
          >
            <svg height="16" width="16" viewBox="0 0 16 16" fill="#F0F6FC">
              <path d="M0 1.75A.75.75 0 0 1 .75 1h4.253c1.227 0 2.317.59 3 1.501A3.743 3.743 0 0 1 11.006 1h4.245a.75.75 0 0 1 .75.75v10.5a.75.75 0 0 1-.75.75h-4.507a2.25 2.25 0 0 0-1.591.659l-.622.621a.75.75 0 0 1-1.06 0l-.622-.621A2.25 2.25 0 0 0 5.258 13H.75a.75.75 0 0 1-.75-.75Zm7.251 10.324.004-5.073-.002-2.253A2.25 2.25 0 0 0 5.003 2.5H1.5v9h3.757a3.75 3.75 0 0 1 1.994.574ZM8.755 4.75l-.004 7.322a3.752 3.752 0 0 1 1.992-.572H14.5v-9h-3.495a2.25 2.25 0 0 0-2.25 2.25Z"></path>
            </svg>
            <span>Overview</span>
          </div>

          {/* Repositories Tab */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "400",
              color: "#8B949E",
              cursor: "pointer",
            }}
          >
            <svg height="16" width="16" viewBox="0 0 16 16" fill="#8B949E">
              <path d="M2 2.5A2.5 2.5 0 0 1 4.5 0h8.75a.75.75 0 0 1 .75.75v12.5a.75.75 0 0 1-.75.75h-2.5a.75.75 0 0 1 0-1.5h1.75v-2h-8a1 1 0 0 0-.714 1.7.75.75 0 1 1-1.072 1.05A2.495 2.495 0 0 1 2 11.5Zm10.5-1h-8a1 1 0 0 0-1 1v6.708A2.486 2.486 0 0 1 4.5 9h8ZM5 12.25a.25.25 0 0 1 .25-.25h3.5a.25.25 0 0 1 .25.25v3.25a.25.25 0 0 1-.4.2l-1.45-1.087a.249.249 0 0 0-.3 0L5.4 15.7a.25.25 0 0 1-.4-.2Z"></path>
            </svg>
            <span>Repositories</span>
            <span
              style={{
                backgroundColor: "#30363D",
                color: "#F0F6FC",
                fontSize: "12px",
                fontWeight: "600",
                padding: "2px 8px",
                borderRadius: "2em",
              }}
            >
              {pinnedRepos.length}
            </span>
          </div>

          {/* Projects Tab */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "400",
              color: "#8B949E",
              cursor: "pointer",
            }}
          >
            <svg height="16" width="16" viewBox="0 0 16 16" fill="#8B949E">
              <path d="M1.75 0h12.5C15.216 0 16 .784 16 1.75v12.5A1.75 1.75 0 0 1 14.25 16H1.75A1.75 1.75 0 0 1 0 14.25V1.75C0 .784.784 0 1.75 0ZM1.5 1.75v12.5c0 .138.112.25.25.25h12.5a.25.25 0 0 0 .25-.25V1.75a.25.25 0 0 0-.25-.25H1.75a.25.25 0 0 0-.25.25ZM11.75 3a.75.75 0 0 1 .75.75v7.5a.75.75 0 0 1-1.5 0v-7.5a.75.75 0 0 1 .75-.75Zm-8.25.75a.75.75 0 0 1 1.5 0v7.5a.75.75 0 0 1-1.5 0ZM8 3a.75.75 0 0 1 .75.75v7.5a.75.75 0 0 1-1.5 0v-7.5A.75.75 0 0 1 8 3Z"></path>
            </svg>
            <span>Projects</span>
          </div>

          {/* Packages Tab */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "400",
              color: "#8B949E",
              cursor: "pointer",
            }}
          >
            <svg height="16" width="16" viewBox="0 0 16 16" fill="#8B949E">
              <path d="m8.878.392 5.25 3.045c.54.314.872.89.872 1.514v6.098a1.75 1.75 0 0 1-.872 1.514l-5.25 3.045a1.75 1.75 0 0 1-1.756 0l-5.25-3.045A1.75 1.75 0 0 1 1 11.049V4.951c0-.624.332-1.201.872-1.514L7.122.392a1.75 1.75 0 0 1 1.756 0ZM7.875 1.69l-4.63 2.685L8 7.133l4.755-2.758-4.63-2.685a.248.248 0 0 0-.25 0ZM2.5 5.677v5.372c0 .09.047.171.125.216l4.625 2.683V8.432Zm6.25 8.271 4.625-2.683a.25.25 0 0 0 .125-.216V5.677L8.75 8.432Z"></path>
            </svg>
            <span>Packages</span>
          </div>

          {/* Stars Tab */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "400",
              color: "#8B949E",
              cursor: "pointer",
            }}
          >
            <svg height="16" width="16" viewBox="0 0 16 16" fill="#8B949E">
              <path d="M8 .25a.75.75 0 0 1 .673.418l1.882 3.815 4.21.612a.75.75 0 0 1 .416 1.279l-3.046 2.97.719 4.192a.751.751 0 0 1-1.088.791L8 12.347l-3.766 1.98a.75.75 0 0 1-1.088-.79l.72-4.194L.818 6.374a.75.75 0 0 1 .416-1.28l4.21-.611L7.327.668A.75.75 0 0 1 8 .25Z"></path>
            </svg>
            <span>Stars</span>
          </div>
        </div>

        {/* Search Bar */}
        <div
          style={{
            position: "relative",
            width: "300px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              backgroundColor: "#0D1117",
              border: "1px solid #30363D",
              borderRadius: "6px",
              padding: "6px 12px",
              color: "#8B949E",
              fontSize: "14px",
            }}
          >
            <svg height="16" width="16" viewBox="0 0 16 16" fill="#8B949E">
              <path d="M10.68 11.74a6 6 0 0 1-7.922-8.982 6 6 0 0 1 8.982 7.922l3.04 3.04a.749.749 0 0 1-.326 1.275.749.749 0 0 1-.734-.215ZM11.5 7a4.5 4.5 0 1 0-8.997.004A4.5 4.5 0 0 0 11.5 7Z"></path>
            </svg>
            <span>
              Type{" "}
              <kbd
                style={{
                  backgroundColor: "#161B22",
                  border: "1px solid #30363D",
                  borderRadius: "4px",
                  padding: "2px 6px",
                  fontSize: "12px",
                  fontFamily: "monospace",
                }}
              >
                /
              </kbd>{" "}
              to search
            </span>
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div
        style={{
          display: "flex",
          padding: "40px",
          gap: "32px",
          flex: 1,
          overflow: "hidden",
        }}
      >
        {/* Left Sidebar */}
        <div
          style={{
            width: "296px",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          {/* Avatar */}
          <div
            style={{
              width: "296px",
              height: "296px",
              borderRadius: "50%",
              border: "1px solid #30363D",
              overflow: "hidden",
              background: "#161B22",
            }}
          >
            {profilePhotoUrl ? (
              <img
                src={profilePhotoUrl}
                alt={name}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "linear-gradient(135deg, #58A6FF 0%, #3FB950 100%)",
                  fontSize: "120px",
                  color: "#FFFFFF",
                  fontWeight: "600",
                }}
              >
                {name?.charAt(0)?.toUpperCase() || "U"}
              </div>
            )}
          </div>

          {/* Name and Username */}
          <div>
            <div
              style={{
                fontSize: "26px",
                fontWeight: "600",
                color: "#F0F6FC",
                marginBottom: "4px",
              }}
            >
              {name}
            </div>
            <div
              style={{
                fontSize: "20px",
                fontWeight: "300",
                color: "#8B949E",
              }}
            >
              @{username}
            </div>
          </div>

          {/* Follow Button */}
          <button
            style={{
              background: "#21262D",
              border: "1px solid #30363D",
              borderRadius: "6px",
              padding: "8px 16px",
              color: "#F0F6FC",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              width: "100%",
            }}
          >
            Follow
          </button>

          {/* Bio */}
          {bio && (
            <div
              style={{
                fontSize: "14px",
                color: "#8B949E",
                lineHeight: "1.5",
                marginTop: "8px",
              }}
            >
              {bio.slice(0, 150)}
              {bio.length > 150 ? "..." : ""}
            </div>
          )}

          {/* Location and Links */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              marginTop: "8px",
            }}
          >
            {location && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "14px",
                  color: "#8B949E",
                }}
              >
                <span>📍</span>
                <span>{location}</span>
              </div>
            )}
            {website && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "14px",
                  color: "#58A6FF",
                }}
              >
                <span>🔗</span>
                <span>{website.replace(/^https?:\/\//, "")}</span>
              </div>
            )}
            {email && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "14px",
                  color: "#8B949E",
                }}
              >
                <span>✉️</span>
                <span>{email}</span>
              </div>
            )}
          </div>

          {/* Stats */}
          <div
            style={{
              marginTop: "16px",
              paddingTop: "16px",
              borderTop: "1px solid #30363D",
            }}
          >
            <div
              style={{
                fontSize: "14px",
                color: "#8B949E",
                display: "flex",
                gap: "4px",
              }}
            >
              <span style={{ color: "#F0F6FC", fontWeight: "600" }}>500+</span>
              <span>followers</span>
              <span>·</span>
              <span style={{ color: "#F0F6FC", fontWeight: "600" }}>234</span>
              <span>following</span>
            </div>
          </div>

          {/* Achievements */}
          <div
            style={{
              marginTop: "16px",
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
            }}
          >
            <div
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "50%",
                background: "#161B22",
                border: "1px solid #30363D",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "24px",
              }}
            >
              🏆
            </div>
            <div
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "50%",
                background: "#161B22",
                border: "1px solid #30363D",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "24px",
              }}
            >
              ⭐
            </div>
            <div
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "50%",
                background: "#161B22",
                border: "1px solid #30363D",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "24px",
              }}
            >
              🔥
            </div>
          </div>
        </div>

        {/* Right Content Area */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: "60px",
            overflow: "hidden",
          }}
        >
          {/* Popular Repositories */}
          <div>
            <div
              style={{
                fontSize: "19px",
                fontWeight: "400",
                color: "#F0F6FC",
                marginBottom: "12px",
              }}
            >
              Popular Repositories
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "16px",
              }}
            >
              {pinnedRepos.slice(0, 6).map((repo, idx) => (
                <div
                  key={idx}
                  style={{
                    background: "#161B22",
                    border: "1px solid #30363D",
                    borderRadius: "6px",
                    padding: "16px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  {/* Repo Name */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <svg height="18" width="18" viewBox="0 0 16 16" fill="#8B949E">
                      <path d="M2 2.5A2.5 2.5 0 0 1 4.5 0h8.75a.75.75 0 0 1 .75.75v12.5a.75.75 0 0 1-.75.75h-2.5a.75.75 0 0 1 0-1.5h1.75v-2h-8a1 1 0 0 0-.714 1.7.75.75 0 1 1-1.072 1.05A2.495 2.495 0 0 1 2 11.5Zm10.5-1h-8a1 1 0 0 0-1 1v6.708A2.486 2.486 0 0 1 4.5 9h8ZM5 12.25a.25.25 0 0 1 .25-.25h3.5a.25.25 0 0 1 .25.25v3.25a.25.25 0 0 1-.4.2l-1.45-1.087a.249.249 0 0 0-.3 0L5.4 15.7a.25.25 0 0 1-.4-.2Z"></path>
                    </svg>
                    <span
                      style={{
                        fontSize: "16px",
                        fontWeight: "600",
                        color: "#58A6FF",
                      }}
                    >
                      {repo.name}
                    </span>
                  </div>

                  {/* Description */}
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#8B949E",
                      lineHeight: "1.5",
                      flex: 1,
                    }}
                  >
                    {repo.description}
                  </div>

                  {/* Stats */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                      fontSize: "14px",
                      color: "#8B949E",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <div
                        style={{
                          width: "12px",
                          height: "12px",
                          borderRadius: "50%",
                          background: languageColors[repo.language] || "#8B949E",
                        }}
                      />
                      <span>{repo.language}</span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <span>⭐</span>
                      <span>{repo.stars}</span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <svg height="12" width="12" viewBox="0 0 16 16" fill="#8B949E">
                        <path d="M5 5.372v.878c0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75v-.878a2.25 2.25 0 1 1 1.5 0v.878a2.25 2.25 0 0 1-2.25 2.25h-1.5v2.128a2.251 2.251 0 1 1-1.5 0V8.5h-1.5A2.25 2.25 0 0 1 3.5 6.25v-.878a2.25 2.25 0 1 1 1.5 0ZM5 3.25a.75.75 0 1 0-1.5 0 .75.75 0 0 0 1.5 0Zm6.75.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm-3 8.75a.75.75 0 1 0-1.5 0 .75.75 0 0 0 1.5 0Z"></path>
                      </svg>
                      <span>{repo.forks}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contribution Section */}
          <div>
            {/* Title */}
            <div
              style={{
                fontSize: "17px",
                color: "#8B949E",
                marginBottom: "12px",
              }}
            >
              {totalContributions} contributions in the last year
            </div>

            {/* Contribution Graph Box with Year Selector */}
            <div
              style={{
                display: "flex",
                gap: "50px",
                alignItems: "flex-start",
              }}
            >
              <div
                style={{
                  background: "#161B22",
                  border: "1px solid #30363D",
                  borderRadius: "6px",
                  padding: "16px",
                  width: "fit-content",
                }}
              >
                {/* Month Labels */}
                <div
                  style={{
                    display: "flex",
                    gap: "5px",
                    marginBottom: "6px",
                    marginLeft: "40px",
                  }}
                >
                  {["Oct", "Nov", "Dec", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep"].map(
                    (month, idx) => (
                      <div
                        key={month}
                        style={{
                          fontSize: "11px",
                          color: "#8B949E",
                          width: `${4.33 * 16 + 4.33 * 4}px`,
                          textAlign: "left",
                        }}
                      >
                        {month}
                      </div>
                    )
                  )}
                </div>

                {/* Days Grid with Labels */}
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                  }}
                >
                  {/* Day Labels */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "5px",
                      paddingTop: "0px",
                    }}
                  >
                    <div style={{ fontSize: "11px", color: "#8B949E", height: "16px", lineHeight: "16px" }}>
                      Mon
                    </div>
                    <div style={{ fontSize: "11px", color: "transparent", height: "16px", lineHeight: "16px" }}>
                      Tue
                    </div>
                    <div style={{ fontSize: "11px", color: "#8B949E", height: "16px", lineHeight: "16px" }}>
                      Wed
                    </div>
                    <div style={{ fontSize: "11px", color: "transparent", height: "16px", lineHeight: "16px" }}>
                      Thu
                    </div>
                    <div style={{ fontSize: "11px", color: "#8B949E", height: "16px", lineHeight: "16px" }}>
                      Fri
                    </div>
                    <div style={{ fontSize: "11px", color: "transparent", height: "16px", lineHeight: "16px" }}>
                      Sat
                    </div>
                    <div style={{ fontSize: "11px", color: "transparent", height: "16px", lineHeight: "16px" }}>
                      Sun
                    </div>
                  </div>

                  {/* Contribution Grid - Much Wider */}
                  <div
                    style={{
                      display: "flex",
                      gap: "5px",
                    }}
                  >
                    {Array.from({ length: 52 }).map((_, weekIdx) => (
                      <div
                        key={weekIdx}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "5px",
                        }}
                      >
                        {Array.from({ length: 7 }).map((_, dayIdx) => {
                          const contrib = contributions.find((c) => c.week === weekIdx && c.day === dayIdx);
                          return (
                            <div
                              key={dayIdx}
                              style={{
                                width: "16px",
                                height: "16px",
                                borderRadius: "3px",
                                background: contributionColors[contrib?.level || 0],
                                border: contrib?.level === 0 ? "1px solid #30363D" : "none",
                              }}
                            />
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Legend and Learn More */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: "14px",
                    marginLeft: "40px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#58A6FF",
                      cursor: "pointer",
                    }}
                  >
                    Learn how we count contributions
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      fontSize: "12px",
                      color: "#8B949E",
                    }}
                  >
                    <span>Less</span>
                    {[0, 1, 2, 3, 4].map((level) => (
                      <div
                        key={level}
                        style={{
                          width: "16px",
                          height: "16px",
                          borderRadius: "3px",
                          background: contributionColors[level],
                          border: level === 0 ? "1px solid #30363D" : "none",
                        }}
                      />
                    ))}
                    <span>More</span>
                  </div>
                </div>
              </div>

              {/* Year Selector - Column Format */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  paddingTop: "16px",
                }}
              >
                <div
                  style={{
                    backgroundColor: "#1890ff",
                    color: "#FFFFFF",
                    fontSize: "14px",
                    fontWeight: "600",
                    padding: "8px 20px",
                    borderRadius: "6px",
                    textAlign: "center",
                    cursor: "pointer",
                    minWidth: "80px",
                  }}
                >
                  2025
                </div>
                {["2024", "2023", "2022"].map((year) => (
                  <div
                    key={year}
                    style={{
                      color: "#8B949E",
                      fontSize: "14px",
                      fontWeight: "400",
                      padding: "8px 20px",
                      textAlign: "center",
                      cursor: "pointer",
                      minWidth: "80px",
                    }}
                  >
                    {year}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* GitHub Footer */}
      <div
        style={{
          borderTop: "1px solid #30363D",
          padding: "24px 40px",
          display: "flex",
          alignItems: "center",
          gap: "20px",
          fontSize: "12px",
          color: "#8B949E",
        }}
      >
        {/* GitHub Logo */}
        <svg height="24" viewBox="0 0 16 16" width="24" fill="#8B949E">
          <path d="M8 0c4.42 0 8 3.58 8 8a8.013 8.013 0 0 1-5.45 7.59c-.4.08-.55-.17-.55-.38 0-.27.01-1.13.01-2.2 0-.75-.25-1.23-.54-1.48 1.78-.2 3.65-.88 3.65-3.95 0-.88-.31-1.59-.82-2.15.08-.2.36-1.02-.08-2.12 0 0-.67-.22-2.2.82-.64-.18-1.32-.27-2-.27-.68 0-1.36.09-2 .27-1.53-1.03-2.2-.82-2.2-.82-.44 1.1-.16 1.92-.08 2.12-.51.56-.82 1.28-.82 2.15 0 3.06 1.86 3.75 3.64 3.95-.23.2-.44.55-.51 1.07-.46.21-1.61.55-2.33-.66-.15-.24-.6-.83-1.23-.82-.67.01-.27.38.01.53.34.19.73.9.82 1.13.16.45.68 1.31 2.69.94 0 .67.01 1.3.01 1.49 0 .21-.15.45-.55.38A7.995 7.995 0 0 1 0 8c0-4.42 3.58-8 8-8Z"></path>
        </svg>

        {/* Copyright */}
        <div>© 2025 GitHub, Inc.</div>

        {/* Footer Links */}
        <div
          style={{
            display: "flex",
            gap: "20px",
            flex: 1,
          }}
        >
          <a href="#" style={{ color: "#58A6FF", textDecoration: "none" }}>
            Terms
          </a>
          <a href="#" style={{ color: "#58A6FF", textDecoration: "none" }}>
            Privacy
          </a>
          <a href="#" style={{ color: "#58A6FF", textDecoration: "none" }}>
            Security
          </a>
          <a href="#" style={{ color: "#58A6FF", textDecoration: "none" }}>
            Status
          </a>
          <a href="#" style={{ color: "#58A6FF", textDecoration: "none" }}>
            Docs
          </a>
          <a href="#" style={{ color: "#58A6FF", textDecoration: "none" }}>
            Contact
          </a>
          <a href="#" style={{ color: "#58A6FF", textDecoration: "none" }}>
            Manage cookies
          </a>
          <a href="#" style={{ color: "#58A6FF", textDecoration: "none" }}>
            Do not share my personal information
          </a>
        </div>

        {/* Made with upresume.ai */}
        <div
          style={{
            marginLeft: "auto",
            color: "#8B949E",
          }}
        >
          Made with{" "}
          <a
            href="https://upresume.ai"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#58A6FF", textDecoration: "none" }}
          >
            upresume.ai
          </a>
        </div>
      </div>
    </div>
  );
};

// Spotify Podcasts Desktop Card (16:9 - 1920 x 1080px)
export const SpotifyPodcastsDesktopCard = ({ resumeData, profilePhotoUrl }) => {
  const name = resumeData?.parsedData?.personalInfo?.name || "Your Name";
  const currentTitle = resumeData?.parsedData?.experience?.[0]?.title || "Professional";

  // Get experiences as podcast episodes
  const experiences = resumeData?.parsedData?.experience || [];
  const topExperiences = experiences.slice(0, 5);

  // Summary
  const summaryData = resumeData?.parsedData?.summary;
  const summaryText = typeof summaryData === "string" ? summaryData : summaryData?.content || "";
  const shortSummary = summaryText.length > 150 ? summaryText.substring(0, 150) + "..." : summaryText;

  // Calculate duration from dates
  const calculateDuration = (startDate, endDate) => {
    if (!startDate) return "Ongoing";

    const start = new Date(startDate);
    const end = endDate && endDate.toLowerCase() !== "present" ? new Date(endDate) : new Date();

    const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;

    if (years > 0 && remainingMonths > 0) {
      return `${years}y ${remainingMonths}mo`;
    } else if (years > 0) {
      return `${years}y`;
    } else {
      return `${remainingMonths}mo`;
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr || dateStr.toLowerCase() === "present") return "Present";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
  };

  return (
    <div
      style={{
        width: "1920px",
        height: "1080px",
        background: "#121212",
        display: "flex",
        fontFamily: '"Circular", "Montserrat", "Helvetica Neue", Helvetica, Arial, sans-serif',
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Sidebar Navigation (230px) */}
      <div
        style={{
          width: "230px",
          background: "#000000",
          display: "flex",
          flexDirection: "column",
          padding: "24px 12px",
        }}
      >
        {/* Spotify Logo */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "32px",
            padding: "0 12px",
          }}
        >
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
            <circle cx="16" cy="16" r="16" fill="#1DB954" />
            <path
              d="M22.5 13.5C18.5 11.5 11.5 11.5 8.5 13C8.2 13.2 7.8 13.1 7.6 12.8C7.4 12.5 7.5 12.1 7.8 11.9C11.2 10.2 18.8 10.2 23.2 12.1C23.5 12.3 23.6 12.7 23.4 13C23.2 13.3 22.8 13.4 22.5 13.5ZM22.4 16.5C22.2 16.8 21.9 16.9 21.6 16.7C18.3 14.9 13.5 14.4 9.8 15.5C9.5 15.6 9.1 15.4 9 15.1C8.9 14.8 9.1 14.4 9.4 14.3C13.5 13.1 18.7 13.7 22.3 15.7C22.6 15.8 22.6 16.2 22.4 16.5ZM21.5 19.4C21.3 19.6 21.1 19.7 20.9 19.6C18 17.9 14.3 17.6 9.9 18.6C9.7 18.6 9.4 18.5 9.4 18.3C9.3 18.1 9.4 17.8 9.6 17.8C14.3 16.7 18.3 17.1 21.4 18.9C21.6 19 21.7 19.3 21.5 19.4Z"
              fill="white"
            />
          </svg>
          <span
            style={{
              fontSize: "24px",
              fontWeight: "700",
              color: "#FFFFFF",
              letterSpacing: "-0.5px",
            }}
          >
            Podcasts
          </span>
        </div>

        {/* Navigation Menu */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            marginBottom: "24px",
          }}
        >
          <div
            style={{
              padding: "8px 12px",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              gap: "16px",
              cursor: "pointer",
              transition: "background 0.2s",
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="#B3B3B3">
              <path d="M9 2L9 22L7 22L7 2L9 2ZM15 2L15 22L17 22L17 2L15 2Z" />
            </svg>
            <span style={{ fontSize: "14px", fontWeight: "500", color: "#B3B3B3" }}>Home</span>
          </div>

          <div
            style={{
              padding: "8px 12px",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              gap: "16px",
              cursor: "pointer",
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="#B3B3B3">
              <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
            </svg>
            <span style={{ fontSize: "14px", fontWeight: "500", color: "#B3B3B3" }}>Search</span>
          </div>

          <div
            style={{
              padding: "8px 12px",
              borderRadius: "4px",
              background: "#282828",
              display: "flex",
              alignItems: "center",
              gap: "16px",
              cursor: "pointer",
              borderLeft: "4px solid #1DB954",
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="#FFFFFF">
              <path d="M20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM18 7h-2.5V4H18v3zM13.5 7V4H16v3h-2.5zM13.5 9V12H16V9h-2.5zM11 4h2.5v3H11V4zM9 4v3H8V4h1zm-1 5h2.5v3H8V9zm0 5v-2h2.5v2H8zm7.5 0v-2H18v2h-2.5zM4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6z" />
            </svg>
            <span style={{ fontSize: "14px", fontWeight: "700", color: "#FFFFFF" }}>Your Library</span>
          </div>
        </div>

        {/* Your Podcasts */}
        <div
          style={{
            marginTop: "8px",
          }}
        >
          <div
            style={{
              padding: "8px 12px",
              fontSize: "11px",
              fontWeight: "700",
              color: "#B3B3B3",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              marginBottom: "8px",
            }}
          >
            YOUR PODCASTS
          </div>

          <div
            style={{
              padding: "8px 12px",
              borderRadius: "4px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                background: "#8B39F5",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "20px",
              }}
            >
              🎙️
            </div>
            <span style={{ fontSize: "14px", fontWeight: "500", color: "#B3B3B3" }}>Dev Journey</span>
          </div>
        </div>

        {/* Recent Episodes */}
        <div
          style={{
            marginTop: "auto",
            paddingTop: "16px",
          }}
        >
          <div
            style={{
              padding: "8px 12px",
              fontSize: "11px",
              fontWeight: "700",
              color: "#B3B3B3",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              marginBottom: "8px",
            }}
          >
            RECENT
          </div>
          {topExperiences.slice(0, 2).map((exp, idx) => (
            <div
              key={idx}
              style={{
                padding: "6px 12px",
                fontSize: "13px",
                color: "#B3B3B3",
                cursor: "pointer",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              Ep {idx + 1}: {exp.title?.substring(0, 20)}...
            </div>
          ))}
        </div>
      </div>

      {/* Main Content Area */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Show Header */}
        <div
          style={{
            padding: "32px 40px",
            background: "linear-gradient(180deg, #282828 0%, #121212 100%)",
            display: "flex",
            gap: "32px",
            alignItems: "flex-start",
          }}
        >
          {/* Cover Art */}
          <div
            style={{
              width: "232px",
              height: "232px",
              background: profilePhotoUrl
                ? `url(${profilePhotoUrl})`
                : "linear-gradient(135deg, #8B39F5 0%, #1DB954 100%)",
              backgroundSize: "cover",
              backgroundPosition: "center",
              borderRadius: "8px",
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.5)",
              flexShrink: 0,
            }}
          />

          {/* Show Info */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
              paddingTop: "32px",
            }}
          >
            <div
              style={{
                fontSize: "12px",
                fontWeight: "700",
                color: "#FFFFFF",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                marginBottom: "8px",
              }}
            >
              PODCAST
            </div>

            <div
              style={{
                fontSize: "48px",
                fontWeight: "900",
                color: "#FFFFFF",
                letterSpacing: "-1px",
                marginBottom: "16px",
                lineHeight: "1.2",
              }}
            >
              The {name.split(" ")[0]} Journey
            </div>

            <div
              style={{
                fontSize: "16px",
                color: "#B3B3B3",
                marginBottom: "20px",
                lineHeight: "1.6",
              }}
            >
              {shortSummary ||
                `Chronicling the professional journey of ${name} from ${
                  topExperiences[topExperiences.length - 1]?.title
                } to ${currentTitle}`}
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "20px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg key={star} width="16" height="16" viewBox="0 0 24 24" fill="#1DB954">
                    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                  </svg>
                ))}
              </div>
              <span style={{ fontSize: "14px", color: "#B3B3B3" }}>5.0 ({topExperiences.length} episodes)</span>
            </div>

            <div
              style={{
                display: "flex",
                gap: "16px",
                alignItems: "center",
              }}
            >
              <button
                style={{
                  background: "#1DB954",
                  color: "#FFFFFF",
                  border: "none",
                  padding: "14px 40px",
                  borderRadius: "500px",
                  fontSize: "16px",
                  fontWeight: "700",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  transition: "transform 0.2s",
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z" />
                </svg>
                PLAY LATEST
              </button>

              <button
                style={{
                  background: "transparent",
                  color: "#FFFFFF",
                  border: "2px solid #FFFFFF",
                  padding: "12px 32px",
                  borderRadius: "500px",
                  fontSize: "14px",
                  fontWeight: "700",
                  cursor: "pointer",
                  transition: "transform 0.2s",
                }}
              >
                + FOLLOW
              </button>

              <div
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  border: "2px solid #B3B3B3",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="#B3B3B3">
                  <circle cx="12" cy="6" r="1.5" />
                  <circle cx="12" cy="12" r="1.5" />
                  <circle cx="12" cy="18" r="1.5" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Episodes List */}
        <div
          style={{
            flex: 1,
            padding: "24px 40px",
            overflowY: "auto",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <h2
              style={{
                fontSize: "24px",
                fontWeight: "700",
                color: "#FFFFFF",
                margin: 0,
              }}
            >
              All Episodes
            </h2>
            <div
              style={{
                fontSize: "14px",
                color: "#B3B3B3",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              Latest
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M4 6L8 10L12 6" />
              </svg>
            </div>
          </div>

          {/* Episode Cards */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            {topExperiences.map((exp, idx) => {
              const isNew = idx === 0;
              const isPlaying = idx === 0;
              const duration = calculateDuration(exp.startDate, exp.endDate);
              const startDate = formatDate(exp.startDate);

              return (
                <div
                  key={idx}
                  style={{
                    background: isPlaying ? "#282828" : "transparent",
                    borderRadius: "4px",
                    padding: "12px 16px",
                    display: "flex",
                    gap: "16px",
                    alignItems: "center",
                    cursor: "pointer",
                    transition: "background 0.2s",
                    borderLeft: isPlaying ? "4px solid #1DB954" : "4px solid transparent",
                  }}
                >
                  {/* Play Button */}
                  <div
                    style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "50%",
                      background: isPlaying ? "#1DB954" : "#404040",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      flexShrink: 0,
                      transition: "background 0.2s",
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="#FFFFFF">
                      {isPlaying ? <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" /> : <path d="M8 5v14l11-7z" />}
                    </svg>
                  </div>

                  {/* Episode Info */}
                  <div
                    style={{
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "4px",
                      }}
                    >
                      {isNew && (
                        <div
                          style={{
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            background: "#2E77D0",
                            flexShrink: 0,
                          }}
                        />
                      )}
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: "500",
                          color: "#FFFFFF",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        Ep {topExperiences.length - idx}: {exp.title} {exp.company && `at ${exp.company}`}
                      </div>
                    </div>

                    <div
                      style={{
                        fontSize: "14px",
                        color: "#B3B3B3",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        marginBottom: "4px",
                      }}
                    >
                      {exp.description?.substring(0, 80)}...
                    </div>

                    <div
                      style={{
                        fontSize: "12px",
                        color: "#6A6A6A",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <span>{startDate}</span>
                      <span>•</span>
                      <span>{duration}</span>
                      {isPlaying && (
                        <>
                          <span>•</span>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <div
                              style={{
                                width: "60px",
                                height: "4px",
                                background: "#535353",
                                borderRadius: "2px",
                                position: "relative",
                                overflow: "hidden",
                              }}
                            >
                              <div
                                style={{
                                  position: "absolute",
                                  left: 0,
                                  top: 0,
                                  height: "100%",
                                  width: "45%",
                                  background: "#1DB954",
                                  borderRadius: "2px",
                                }}
                              />
                            </div>
                            <span style={{ color: "#1DB954" }}>45% played</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Episode Duration Badge */}
                  <div
                    style={{
                      padding: "4px 12px",
                      borderRadius: "4px",
                      background: "#404040",
                      fontSize: "12px",
                      color: "#B3B3B3",
                      fontWeight: "500",
                      flexShrink: 0,
                    }}
                  >
                    {duration}
                  </div>

                  {/* More Options */}
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="#B3B3B3">
                      <circle cx="12" cy="6" r="1.5" />
                      <circle cx="12" cy="12" r="1.5" />
                      <circle cx="12" cy="18" r="1.5" />
                    </svg>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Now Playing Bar */}
        <div
          style={{
            height: "90px",
            background: "#181818",
            borderTop: "1px solid #282828",
            display: "flex",
            alignItems: "center",
            padding: "0 16px",
            gap: "16px",
            boxShadow: "0 -2px 10px rgba(0, 0, 0, 0.3)",
          }}
        >
          {/* Current Episode Info */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              width: "300px",
            }}
          >
            <div
              style={{
                width: "56px",
                height: "56px",
                background: "linear-gradient(135deg, #8B39F5 0%, #1DB954 100%)",
                borderRadius: "4px",
                flexShrink: 0,
              }}
            />
            <div
              style={{
                flex: 1,
                minWidth: 0,
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#FFFFFF",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {topExperiences[0]?.title}
              </div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#B3B3B3",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                The {name.split(" ")[0]} Journey
              </div>
            </div>
          </div>

          {/* Player Controls */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              alignItems: "center",
            }}
          >
            {/* Control Buttons */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "24px",
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#B3B3B3" style={{ cursor: "pointer" }}>
                <path d="M7 6v12l-5-6 5-6zm10 0v12l-5-6 5-6z" />
              </svg>
              <div
                style={{
                  background: "#B3B3B3",
                  borderRadius: "4px",
                  padding: "6px 12px",
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#000000",
                  cursor: "pointer",
                }}
              >
                -15
              </div>
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  background: "#FFFFFF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "transform 0.2s",
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#000000">
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                </svg>
              </div>
              <div
                style={{
                  background: "#B3B3B3",
                  borderRadius: "4px",
                  padding: "6px 12px",
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#000000",
                  cursor: "pointer",
                }}
              >
                +15
              </div>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#B3B3B3" style={{ cursor: "pointer" }}>
                <path d="M7 6v12l5-6-5-6zm10 0v12l5-6-5-6z" />
              </svg>
            </div>

            {/* Progress Bar */}
            <div
              style={{
                width: "100%",
                maxWidth: "600px",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <span style={{ fontSize: "12px", color: "#B3B3B3", minWidth: "45px", textAlign: "right" }}>
                15:32
              </span>
              <div
                style={{
                  flex: 1,
                  height: "4px",
                  background: "#535353",
                  borderRadius: "2px",
                  position: "relative",
                  cursor: "pointer",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    height: "100%",
                    width: "45%",
                    background: "#1DB954",
                    borderRadius: "2px",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    left: "45%",
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "12px",
                    height: "12px",
                    background: "#FFFFFF",
                    borderRadius: "50%",
                    opacity: 0,
                  }}
                />
              </div>
              <span style={{ fontSize: "12px", color: "#B3B3B3", minWidth: "45px" }}>45:00</span>
            </div>
          </div>

          {/* Additional Controls */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              width: "200px",
              justifyContent: "flex-end",
            }}
          >
            <div
              style={{
                padding: "6px 12px",
                borderRadius: "4px",
                border: "1px solid #B3B3B3",
                fontSize: "12px",
                fontWeight: "600",
                color: "#B3B3B3",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              1.0x
              <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                <path d="M4 6L8 10L12 6" />
              </svg>
            </div>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#B3B3B3" style={{ cursor: "pointer" }}>
              <path d="M15 6l-6 6 6 6V6z" />
              <path d="M4 18h2V6H4v12z" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

// Card 15 - IMDB Desktop Style Resume Card (16:9 - 1920x1080px)
export const IMDBDesktopCard = ({ resumeData, profilePhotoUrl }) => {
  const name = resumeData?.parsedData?.personalInfo?.name || "Your Name";
  const currentTitle = resumeData?.parsedData?.experience?.[0]?.title || "Professional";
  const currentCompany = resumeData?.parsedData?.experience?.[0]?.company || "";
  const location = resumeData?.parsedData?.personalInfo?.location || "Location";
  const email = resumeData?.parsedData?.personalInfo?.email || "";
  const phone = resumeData?.parsedData?.personalInfo?.phone || "";

  // Calculate overall rating based on experience
  const experiences = resumeData?.parsedData?.experience || [];
  const totalYears = experiences.length;
  const rating = Math.min(9.5, 7.0 + totalYears * 0.3).toFixed(1);
  const reviewCount = Math.floor(Math.random() * 50) + 15; // 15-65 reviews

  // Get top skills
  const allSkills =
    resumeData?.parsedData?.skills?.list ||
    Object.values(resumeData?.parsedData?.skills?.categorized || {}).flat() ||
    [];
  const topSkills = allSkills.slice(0, 6);

  // Get education
  const education = resumeData?.parsedData?.education || [];
  const latestEducation = education[0];

  // Get certifications/awards
  const certifications = resumeData?.parsedData?.certifications || [];
  const awards = certifications.slice(0, 3);

  // Known For Projects - top 4 experiences
  const knownForProjects = experiences.slice(0, 4);

  // Summary
  const summaryData = resumeData?.parsedData?.summary;
  const summaryText = typeof summaryData === "string" ? summaryData : summaryData?.content || "";
  let plotSummary =
    summaryText ||
    `${currentTitle} with ${totalYears}+ years of experience in modern software development. Known for delivering high-impact solutions and leading technical teams to success.`;
  if (plotSummary.length > 200) {
    plotSummary = plotSummary.substring(0, 200) + "...";
  }

  // Metascore calculation (performance score)
  const metascore = Math.floor(parseFloat(rating) * 10);
  const metascoreColor = metascore >= 80 ? "#61C74F" : metascore >= 60 ? "#FFCC33" : "#FF0000";

  return (
    <div
      style={{
        width: "1920px",
        height: "1080px",
        background: "#1F1F1F",
        display: "flex",
        flexDirection: "column",
        fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
        color: "#FFFFFF",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* IMDb Header */}
      <div
        style={{
          background: "#000000",
          borderBottom: "1px solid #3F3F3F",
          padding: "12px 40px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* IMDb Logo */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <div
            style={{
              background: "#F5C518",
              padding: "8px 16px",
              borderRadius: "4px",
              fontWeight: "bold",
              fontSize: "32px",
              color: "#000000",
              letterSpacing: "-1px",
            }}
          >
            IMDb
          </div>
          <div
            style={{
              fontSize: "16px",
              color: "#AAAAAA",
              fontWeight: "500",
            }}
          >
            Professional Database
          </div>
        </div>

        {/* Right Actions */}
        <div
          style={{
            display: "flex",
            gap: "24px",
            alignItems: "center",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              color: "#5799EF",
              cursor: "pointer",
            }}
          >
            Add to Watchlist
          </div>
          <div
            style={{
              background: "#F5C518",
              color: "#000000",
              padding: "8px 20px",
              borderRadius: "4px",
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            Pro
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div
        style={{
          padding: "40px",
          display: "flex",
          gap: "30px",
          flex: 1,
          overflow: "hidden",
        }}
      >
        {/* Left Column - Profile Image */}
        <div
          style={{
            width: "300px",
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            gap: "20px",
          }}
        >
          {/* Profile Photo - Smaller */}
          <div
            style={{
              width: "300px",
              height: "300px",
              background: "#2A2A2A",
              borderRadius: "4px",
              overflow: "hidden",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
            }}
          >
            {profilePhotoUrl ? (
              <img
                src={profilePhotoUrl}
                alt={name}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "100px",
                  color: "#787878",
                }}
              >
                👤
              </div>
            )}
          </div>

          {/* Rating Box - Compact */}
          <div
            style={{
              background: "#2A2A2A",
              padding: "20px 15px",
              borderRadius: "4px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "center",
                gap: "6px",
                marginBottom: "10px",
              }}
            >
              <span
                style={{
                  fontSize: "48px",
                  fontWeight: "bold",
                  color: "#F5C518",
                  lineHeight: "1",
                }}
              >
                ⭐ {rating}
              </span>
              <span
                style={{
                  fontSize: "24px",
                  color: "#F5C518",
                  fontWeight: "600",
                  lineHeight: "1",
                }}
              >
                /10
              </span>
            </div>
            <div
              style={{
                fontSize: "14px",
                color: "#AAAAAA",
              }}
            >
              {reviewCount} reviews
            </div>
          </div>

          {/* Top Skills Section */}
          <div
            style={{
              background: "#2A2A2A",
              padding: "15px",
              borderRadius: "4px",
              border: "1px solid #3F3F3F",
            }}
          >
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "bold",
                margin: "0 0 12px 0",
                color: "#FFFFFF",
              }}
            >
              Top Skills
            </h3>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              {topSkills.slice(0, 6).map((skill, idx) => (
                <div
                  key={idx}
                  style={{
                    background: "#1A1A1A",
                    padding: "8px 12px",
                    borderRadius: "3px",
                    fontSize: "13px",
                    color: "#F5C518",
                    border: "1px solid #3F3F3F",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <span style={{ color: "#F5C518", fontSize: "16px" }}>⚡</span>
                  <span style={{ flex: 1 }}>{skill}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column - Information */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: "25px",
            overflow: "auto",
          }}
        >
          {/* Title Section */}
          <div>
            <h1
              style={{
                fontSize: "42px",
                fontWeight: "600",
                margin: "0 0 12px 0",
                lineHeight: "1.2",
                color: "#FFFFFF",
              }}
            >
              {name}
            </h1>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                fontSize: "16px",
                color: "#AAAAAA",
                marginBottom: "12px",
              }}
            >
              <span>{currentTitle}</span>
              <span>|</span>
              <span>{totalYears}+ years</span>
              <span>|</span>
              <span>{location}</span>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "20px",
                marginTop: "15px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span style={{ fontSize: "20px", color: "#F5C518" }}>⭐</span>
                <span style={{ fontSize: "20px", fontWeight: "bold" }}>{rating}</span>
              </div>

              <div
                style={{
                  width: "40px",
                  height: "40px",
                  background: metascoreColor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "18px",
                  fontWeight: "bold",
                  color: "#FFFFFF",
                  borderRadius: "2px",
                }}
              >
                {metascore}
              </div>

              {awards.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    color: "#F5C518",
                  }}
                >
                  <span style={{ fontSize: "18px" }}>🏆</span>
                  <span style={{ fontSize: "14px" }}>{awards.length} Awards</span>
                </div>
              )}
            </div>
          </div>

          {/* Plot Summary */}
          <div>
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "bold",
                margin: "0 0 12px 0",
                color: "#FFFFFF",
              }}
            >
              Professional Summary
            </h3>
            <p
              style={{
                fontSize: "16px",
                lineHeight: "1.5",
                color: "#FFFFFF",
                margin: 0,
              }}
            >
              {plotSummary}
            </p>
          </div>

          {/* Known For */}
          {knownForProjects.length > 0 && (
            <div>
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "bold",
                  margin: "0 0 15px 0",
                  color: "#FFFFFF",
                }}
              >
                Known For
              </h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: "15px",
                }}
              >
                {knownForProjects.map((proj, idx) => {
                  const projRating = (7.5 + Math.random() * 2).toFixed(1);
                  return (
                    <div
                      key={idx}
                      style={{
                        background: "#2A2A2A",
                        borderRadius: "4px",
                        overflow: "hidden",
                        border: "1px solid #3F3F3F",
                      }}
                    >
                      <div
                        style={{
                          width: "100%",
                          height: "120px",
                          background: "#1A1A1A",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "40px",
                        }}
                      >
                        💼
                      </div>
                      <div
                        style={{
                          padding: "10px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "13px",
                            fontWeight: "500",
                            color: "#FFFFFF",
                            marginBottom: "4px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {proj.company || "Project"}
                        </div>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#AAAAAA",
                            marginBottom: "6px",
                          }}
                        >
                          {proj.startDate?.split(" ")[0] || "Recent"}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            fontSize: "12px",
                          }}
                        >
                          <span style={{ color: "#F5C518" }}>⭐</span>
                          <span style={{ color: "#FFFFFF", fontWeight: "500" }}>{projRating}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Filmography/Experience */}
          <div>
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "bold",
                margin: "0 0 15px 0",
                color: "#FFFFFF",
              }}
            >
              Filmography (Experience)
            </h3>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
              }}
            >
              {experiences.slice(0, 4).map((exp, idx) => {
                const expRating = (7.0 + Math.random() * 2.5).toFixed(1);
                return (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "12px 15px",
                      background: idx % 2 === 0 ? "#2A2A2A" : "transparent",
                      borderRadius: "4px",
                    }}
                  >
                    <div
                      style={{
                        width: "80px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#AAAAAA",
                      }}
                    >
                      {exp.startDate?.split(" ")[0] || "2024"}
                    </div>
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "15px",
                          fontWeight: "500",
                          color: "#FFFFFF",
                        }}
                      >
                        {exp.title}
                      </div>
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#787878",
                        }}
                      >
                        {exp.company}
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        fontSize: "14px",
                      }}
                    >
                      <span style={{ color: "#F5C518" }}>⭐</span>
                      <span style={{ color: "#FFFFFF", fontWeight: "500" }}>{expRating}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            {experiences.length > 4 && (
              <div
                style={{
                  marginTop: "10px",
                  fontSize: "14px",
                  color: "#5799EF",
                  cursor: "pointer",
                }}
              >
                See all {experiences.length} credits →
              </div>
            )}
          </div>

          {/* Technical Details */}
          <div
            style={{
              background: "#2A2A2A",
              padding: "20px",
              borderRadius: "4px",
              border: "1px solid #3F3F3F",
            }}
          >
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "bold",
                margin: "0 0 15px 0",
                color: "#FFFFFF",
              }}
            >
              Technical Specs
            </h3>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "12px",
                fontSize: "14px",
              }}
            >
              <div>
                <span style={{ color: "#AAAAAA" }}>Role:</span>
                <span style={{ color: "#FFFFFF", marginLeft: "8px" }}>{currentTitle}</span>
              </div>
              {currentCompany && (
                <div>
                  <span style={{ color: "#AAAAAA" }}>Company:</span>
                  <span style={{ color: "#FFFFFF", marginLeft: "8px" }}>{currentCompany}</span>
                </div>
              )}
              {latestEducation && (
                <div>
                  <span style={{ color: "#AAAAAA" }}>Education:</span>
                  <span style={{ color: "#FFFFFF", marginLeft: "8px" }}>
                    {latestEducation.degree} - {latestEducation.institution}
                  </span>
                </div>
              )}
              <div>
                <span style={{ color: "#AAAAAA" }}>Experience:</span>
                <span style={{ color: "#FFFFFF", marginLeft: "8px" }}>{totalYears}+ years</span>
              </div>
            </div>

            {topSkills.length > 0 && (
              <div style={{ marginTop: "15px" }}>
                <div style={{ color: "#AAAAAA", fontSize: "14px", marginBottom: "8px" }}>Skills:</div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                  }}
                >
                  {topSkills.map((skill, idx) => (
                    <span
                      key={idx}
                      style={{
                        background: "#1A1A1A",
                        color: "#F5C518",
                        padding: "4px 12px",
                        borderRadius: "3px",
                        fontSize: "12px",
                        border: "1px solid #3F3F3F",
                      }}
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          background: "#000000",
          borderTop: "1px solid #3F3F3F",
          padding: "20px 40px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "20px",
            fontSize: "12px",
            color: "#787878",
            alignItems: "center",
          }}
        >
          {email && <span>✉️ {email}</span>}
          {phone && <span>📞 {phone}</span>}
        </div>
        <div
          style={{
            fontSize: "12px",
            color: "#787878",
          }}
        >
          Made with <span style={{ color: "#F5C518", fontWeight: "600" }}>upresume.ai</span>
        </div>
      </div>
    </div>
  );
};

// Card 8 - YouTube Studio Desktop Style (16:9 - 1920x1080px)
export const YouTubeStudioCard = ({ resumeData, profilePhotoUrl }) => {
  const name = resumeData?.parsedData?.personalInfo?.name || "Your Name";
  const currentTitle = resumeData?.parsedData?.experience?.[0]?.title || "Professional";
  const email = resumeData?.parsedData?.personalInfo?.email || "";
  const phone = resumeData?.parsedData?.personalInfo?.phone || "";

  // Calculate career metrics
  const calculateYears = () => {
    if (!resumeData?.parsedData?.experience || resumeData.parsedData.experience.length === 0) {
      return 0;
    }
    return resumeData.parsedData.experience.length;
  };

  const yearsOfExperience = calculateYears();
  const experiences = resumeData?.parsedData?.experience || [];
  const allSkills =
    resumeData?.parsedData?.skills?.list ||
    Object.values(resumeData?.parsedData?.skills?.categorized || {}).flat() ||
    [];
  const topSkills = allSkills.slice(0, 6);

  // Mock analytics data
  const totalProjects = experiences.length * 3;
  const skillCount = allSkills.length;
  const projectGrowth = 156;
  const skillGrowth = 45;
  const impactValue = "5M+";
  const impactGrowth = 380;

  return (
    <div
      style={{
        width: "1920px",
        height: "1080px",
        background: "#0F0F0F",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        fontFamily:
          '"YouTube Sans", "Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Top Navigation */}
      <div
        style={{
          background: "#0F0F0F",
          borderBottom: "1px solid #3F3F3F",
          padding: "16px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <svg width="40" height="28" viewBox="0 0 40 28" fill="none">
            <path
              d="M39.15 4.37C38.69 2.61 37.32 1.24 35.56 0.78C32.44 0 20 0 20 0C20 0 7.56 0 4.44 0.78C2.68 1.24 1.31 2.61 0.85 4.37C0.07 7.49 0.07 14 0.07 14C0.07 14 0.07 20.51 0.85 23.63C1.31 25.39 2.68 26.76 4.44 27.22C7.56 28 20 28 20 28C20 28 32.44 28 35.56 27.22C37.32 26.76 38.69 25.39 39.15 23.63C39.93 20.51 39.93 14 39.93 14C39.93 14 39.93 7.49 39.15 4.37Z"
              fill="#FF0000"
            />
            <path d="M16 20V8L26 14L16 20Z" fill="white" />
          </svg>
          <span
            style={{
              fontSize: "20px",
              fontWeight: "500",
              color: "#FFFFFF",
            }}
          >
            Studio
          </span>
          <span
            style={{
              fontSize: "14px",
              color: "#AAAAAA",
              marginLeft: "8px",
            }}
          >
            Developer Portfolio
          </span>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <div
            style={{
              padding: "8px 16px",
              background: "#065FD4",
              borderRadius: "18px",
              fontSize: "14px",
              fontWeight: "500",
              color: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <span
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                background: "#2BA640",
              }}
            ></span>
            AVAILABLE FOR HIRE
          </div>
          <div
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              background: profilePhotoUrl ? `url(${profilePhotoUrl})` : "#FF0000",
              backgroundSize: "cover",
              backgroundPosition: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#FFFFFF",
              fontSize: "16px",
              fontWeight: "600",
            }}
          >
            {!profilePhotoUrl && name.charAt(0).toUpperCase()}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div
        style={{
          display: "flex",
          flex: 1,
          overflow: "hidden",
        }}
      >
        {/* Sidebar Navigation */}
        <div
          style={{
            width: "240px",
            background: "#0F0F0F",
            borderRight: "1px solid #3F3F3F",
            padding: "12px 0",
            display: "flex",
            flexDirection: "column",
            gap: "2px",
          }}
        >
          <div
            style={{
              padding: "10px 24px",
              background: "#272727",
              borderLeft: "4px solid #FF0000",
              fontSize: "14px",
              color: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              gap: "16px",
            }}
          >
            <span style={{ fontSize: "20px" }}>📊</span>
            Dashboard
          </div>
          <div
            style={{
              padding: "10px 24px",
              fontSize: "14px",
              color: "#AAAAAA",
              display: "flex",
              alignItems: "center",
              gap: "16px",
            }}
          >
            <span style={{ fontSize: "20px" }}>💼</span>
            Projects
          </div>
          <div
            style={{
              padding: "10px 24px",
              fontSize: "14px",
              color: "#AAAAAA",
              display: "flex",
              alignItems: "center",
              gap: "16px",
            }}
          >
            <span style={{ fontSize: "20px" }}>📈</span>
            Analytics
          </div>
          <div
            style={{
              padding: "10px 24px",
              fontSize: "14px",
              color: "#AAAAAA",
              display: "flex",
              alignItems: "center",
              gap: "16px",
            }}
          >
            <span style={{ fontSize: "20px" }}>💬</span>
            Testimonials
          </div>
          <div
            style={{
              padding: "10px 24px",
              fontSize: "14px",
              color: "#AAAAAA",
              display: "flex",
              alignItems: "center",
              gap: "16px",
            }}
          >
            <span style={{ fontSize: "20px" }}>💰</span>
            Value
          </div>
          <div style={{ margin: "12px 0", borderTop: "1px solid #3F3F3F" }}></div>
          <div
            style={{
              padding: "10px 24px",
              fontSize: "14px",
              color: "#AAAAAA",
              display: "flex",
              alignItems: "center",
              gap: "16px",
            }}
          >
            <span style={{ fontSize: "20px" }}>⚙️</span>
            Settings
          </div>
        </div>

        {/* Dashboard Content */}
        <div
          style={{
            flex: 1,
            background: "#0F0F0F",
            padding: "24px",
            overflowY: "auto",
          }}
        >
          {/* Header */}
          <div
            style={{
              marginBottom: "24px",
            }}
          >
            <h1
              style={{
                fontSize: "24px",
                fontWeight: "500",
                color: "#FFFFFF",
                margin: "0 0 4px 0",
              }}
            >
              Channel Dashboard
            </h1>
            <div
              style={{
                fontSize: "14px",
                color: "#AAAAAA",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <span>Career Analytics - Last {yearsOfExperience}+ years</span>
              <span
                style={{
                  padding: "2px 8px",
                  background: "#272727",
                  borderRadius: "4px",
                  fontSize: "12px",
                }}
              >
                Professional
              </span>
            </div>
          </div>

          {/* Current Status Banner */}
          <div
            style={{
              background: "#181818",
              border: "1px solid #3F3F3F",
              borderRadius: "8px",
              padding: "16px 20px",
              marginBottom: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "14px",
                  color: "#AAAAAA",
                  marginBottom: "4px",
                }}
              >
                Current Status
              </div>
              <div
                style={{
                  fontSize: "32px",
                  fontWeight: "400",
                  color: "#FFFFFF",
                }}
              >
                {name}
              </div>
              <div
                style={{
                  fontSize: "14px",
                  color: "#AAAAAA",
                  marginTop: "4px",
                }}
              >
                {currentTitle} • {yearsOfExperience}+ years experience
              </div>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "12px 20px",
                background: "#2BA640",
                borderRadius: "4px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#FFFFFF",
              }}
            >
              ↑ Available for opportunities
            </div>
          </div>

          {/* Analytics Cards Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "16px",
              marginBottom: "24px",
            }}
          >
            {/* Projects Card */}
            <div
              style={{
                background: "#181818",
                border: "1px solid #3F3F3F",
                borderRadius: "8px",
                padding: "20px",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  color: "#AAAAAA",
                  marginBottom: "8px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Projects
              </div>
              <div
                style={{
                  fontSize: "32px",
                  fontWeight: "400",
                  color: "#FFFFFF",
                  marginBottom: "8px",
                }}
              >
                {totalProjects}
              </div>
              <div
                style={{
                  fontSize: "14px",
                  color: "#2BA640",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  marginBottom: "12px",
                }}
              >
                <span>↑ {projectGrowth}%</span>
                <span style={{ color: "#AAAAAA", fontWeight: "400" }}>vs career start</span>
              </div>
              <div
                style={{
                  height: "32px",
                  display: "flex",
                  alignItems: "flex-end",
                  gap: "2px",
                }}
              >
                {[20, 35, 45, 60, 75, 85, 100].map((height, idx) => (
                  <div
                    key={idx}
                    style={{
                      flex: 1,
                      height: `${height}%`,
                      background: "rgba(6, 95, 212, 0.3)",
                      borderRadius: "2px 2px 0 0",
                    }}
                  ></div>
                ))}
              </div>
            </div>

            {/* Skills Card */}
            <div
              style={{
                background: "#181818",
                border: "1px solid #3F3F3F",
                borderRadius: "8px",
                padding: "20px",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  color: "#AAAAAA",
                  marginBottom: "8px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Skills
              </div>
              <div
                style={{
                  fontSize: "32px",
                  fontWeight: "400",
                  color: "#FFFFFF",
                  marginBottom: "8px",
                }}
              >
                {skillCount}+
              </div>
              <div
                style={{
                  fontSize: "14px",
                  color: "#2BA640",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  marginBottom: "12px",
                }}
              >
                <span>↑ {skillGrowth}%</span>
                <span style={{ color: "#AAAAAA", fontWeight: "400" }}>mastered</span>
              </div>
              <div
                style={{
                  height: "32px",
                  display: "flex",
                  alignItems: "flex-end",
                  gap: "2px",
                }}
              >
                {[40, 55, 70, 85, 90, 95, 100].map((height, idx) => (
                  <div
                    key={idx}
                    style={{
                      flex: 1,
                      height: `${height}%`,
                      background: "rgba(145, 71, 255, 0.3)",
                      borderRadius: "2px 2px 0 0",
                    }}
                  ></div>
                ))}
              </div>
            </div>

            {/* Impact Card */}
            <div
              style={{
                background: "#181818",
                border: "1px solid #3F3F3F",
                borderRadius: "8px",
                padding: "20px",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  color: "#AAAAAA",
                  marginBottom: "8px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Impact
              </div>
              <div
                style={{
                  fontSize: "32px",
                  fontWeight: "400",
                  color: "#FFFFFF",
                  marginBottom: "8px",
                }}
              >
                ${impactValue}
              </div>
              <div
                style={{
                  fontSize: "14px",
                  color: "#2BA640",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  marginBottom: "12px",
                }}
              >
                <span>↑ {impactGrowth}%</span>
                <span style={{ color: "#AAAAAA", fontWeight: "400" }}>value generated</span>
              </div>
              <div
                style={{
                  height: "32px",
                  display: "flex",
                  alignItems: "flex-end",
                  gap: "2px",
                }}
              >
                {[30, 50, 65, 80, 88, 93, 100].map((height, idx) => (
                  <div
                    key={idx}
                    style={{
                      flex: 1,
                      height: `${height}%`,
                      background: "rgba(43, 166, 64, 0.3)",
                      borderRadius: "2px 2px 0 0",
                    }}
                  ></div>
                ))}
              </div>
            </div>
          </div>

          {/* Top Skills Performance */}
          <div
            style={{
              background: "#181818",
              border: "1px solid #3F3F3F",
              borderRadius: "8px",
              padding: "20px",
              marginBottom: "24px",
            }}
          >
            <div
              style={{
                fontSize: "16px",
                fontWeight: "500",
                color: "#FFFFFF",
                marginBottom: "16px",
              }}
            >
              Top Performing Skills
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "12px",
              }}
            >
              {topSkills.slice(0, 3).map((skill, idx) => {
                const hours = Math.floor(Math.random() * 3000) + 1000;
                const percentage = Math.floor(Math.random() * 40) + 60;
                return (
                  <div
                    key={idx}
                    style={{
                      padding: "12px",
                      background: "#212121",
                      borderRadius: "4px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "8px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "14px",
                          fontWeight: "500",
                          color: "#FFFFFF",
                        }}
                      >
                        {skill}
                      </span>
                      <span
                        style={{
                          fontSize: "12px",
                          color: "#AAAAAA",
                        }}
                      >
                        {hours.toLocaleString()}h
                      </span>
                    </div>
                    <div
                      style={{
                        width: "100%",
                        height: "4px",
                        background: "#3F3F3F",
                        borderRadius: "2px",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: `${percentage}%`,
                          height: "100%",
                          background: "#065FD4",
                          borderRadius: "2px",
                        }}
                      ></div>
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#2BA640",
                        marginTop: "4px",
                      }}
                    >
                      ↑ {percentage}%
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Recent Projects Table */}
          <div
            style={{
              background: "#181818",
              border: "1px solid #3F3F3F",
              borderRadius: "8px",
              padding: "20px",
            }}
          >
            <div
              style={{
                fontSize: "16px",
                fontWeight: "500",
                color: "#FFFFFF",
                marginBottom: "16px",
              }}
            >
              Recent Projects
            </div>
            <div
              style={{
                borderTop: "1px solid #3F3F3F",
              }}
            >
              {experiences.slice(0, 3).map((exp, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: "16px 0",
                    borderBottom: "1px solid #3F3F3F",
                    display: "flex",
                    gap: "16px",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      width: "48px",
                      height: "48px",
                      background: "#272727",
                      borderRadius: "4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "20px",
                    }}
                  >
                    📊
                  </div>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#FFFFFF",
                        marginBottom: "4px",
                      }}
                    >
                      {exp.title || "Project"}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#AAAAAA",
                      }}
                    >
                      {exp.company || "Company"} • {exp.duration || "Duration"}
                    </div>
                  </div>
                  <div
                    style={{
                      textAlign: "right",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "14px",
                        color: "#FFFFFF",
                        marginBottom: "4px",
                      }}
                    >
                      Excellent
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#2BA640",
                      }}
                    >
                      ↑ High Impact
                    </div>
                  </div>
                  <div
                    style={{
                      padding: "6px 12px",
                      background: "#272727",
                      borderRadius: "4px",
                      fontSize: "12px",
                      color: "#AAAAAA",
                    }}
                  >
                    View
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Footer Branding */}
      <div
        style={{
          position: "absolute",
          bottom: "16px",
          right: "24px",
          fontSize: "12px",
          color: "#606060",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        Made with <span style={{ color: "#FF0000", fontWeight: "600" }}>upresume.ai</span>
      </div>
    </div>
  );
};

// Card 15 - Twitter/X Desktop Style Resume Card (16:9 - 1920 x 1080px)
export const TwitterMobileCard = ({ resumeData, profilePhotoUrl }) => {
  const name = resumeData?.parsedData?.personalInfo?.name || "Your Name";
  const currentTitle = resumeData?.parsedData?.experience?.[0]?.title || "Professional";
  const currentCompany = resumeData?.parsedData?.experience?.[0]?.company || "";
  const username = name.toLowerCase().replace(/\s+/g, "");

  // Calculate years of experience
  const calculateYears = () => {
    if (!resumeData?.parsedData?.experience || resumeData.parsedData.experience.length === 0) {
      return 0;
    }
    return resumeData.parsedData.experience.length;
  };
  const yearsOfExperience = calculateYears();

  // Get top skills
  const allSkills =
    resumeData?.parsedData?.skills?.list ||
    Object.values(resumeData?.parsedData?.skills?.categorized || {}).flat() ||
    [];
  const topSkills = allSkills.slice(0, 6);

  // Get experience and education
  const experiences = resumeData?.parsedData?.experience || [];
  const latestExperiences = experiences.slice(0, 2);
  const education = resumeData?.parsedData?.education || [];
  const latestEducation = education.slice(0, 2);

  // Bio/Tweet content
  const summaryData = resumeData?.parsedData?.summary;
  const summaryText = typeof summaryData === "string" ? summaryData : summaryData?.content || "";
  let tweetContent =
    summaryText ||
    `🚨 BREAKING: Area ${currentTitle.toLowerCase()} reportedly has ALL the skills your company needs.

Witnesses report:
• ${yearsOfExperience}+ years experience ✓
• ${topSkills[0] || "Expert"} / ${topSkills[1] || "Professional"} skills ✓
• Ships clean code ✓
• Available immediately ✓

More at 11. RT to save a recruiter's day.`;

  // Trim tweet to ~280 characters if too long
  if (tweetContent.length > 280) {
    const sentences = tweetContent.split(/[.!?]+/).filter((s) => s.trim());
    let result = "";
    for (const sentence of sentences) {
      if ((result + sentence).length <= 260) {
        result += (result ? ". " : "") + sentence.trim();
      } else {
        break;
      }
    }
    tweetContent = result + (result ? "..." : "");
  }

  // Format timestamp
  const getTimestamp = () => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const displayHour = hours % 12 || 12;
    const month = now.toLocaleDateString("en-US", { month: "short" });
    const day = now.getDate();
    const year = now.getFullYear();
    return {
      time: `${displayHour}:${minutes} ${ampm}`,
      date: `${month} ${day}, ${year}`,
    };
  };
  const timestamp = getTimestamp();

  // Engagement numbers (mock)
  const retweets = Math.floor(42000 + Math.random() * 10000);
  const quotes = Math.floor(8000 + Math.random() * 2000);
  const likes = Math.floor(420000 + Math.random() * 50000);
  const views = Math.floor(1200000 + Math.random() * 300000);

  return (
    <div
      style={{
        width: "1920px",
        height: "1080px",
        background: "#000000",
        display: "flex",
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        color: "#E7E9EA",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Left Sidebar */}
      <div
        style={{
          width: "275px",
          background: "#000000",
          borderRight: "1px solid #2F3336",
          display: "flex",
          flexDirection: "column",
          padding: "20px",
        }}
      >
        {/* Twitter/X Logo */}
        <div
          style={{
            marginBottom: "40px",
            padding: "12px",
          }}
        >
          <svg width="30" height="30" viewBox="0 0 24 24" fill="#E7E9EA">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
          </svg>
        </div>

        {/* Navigation */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            marginBottom: "auto",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "20px",
              padding: "12px 16px",
              borderRadius: "28px",
              fontSize: "20px",
              fontWeight: "700",
              color: "#E7E9EA",
              background: "#E7E9EA10",
            }}
          >
            <svg width="26" height="26" viewBox="0 0 24 24" fill="#E7E9EA">
              <path d="M21.591 7.146L12.52 1.157c-.316-.21-.724-.21-1.04 0l-9.071 5.99c-.26.173-.409.456-.409.757v13.183c0 .502.418.913.929.913H9.14c.51 0 .929-.41.929-.913v-7.075h3.909v7.075c0 .502.417.913.928.913h6.165c.511 0 .929-.41.929-.913V7.904c0-.301-.158-.584-.408-.758z" />
            </svg>
            <span>Home</span>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "20px",
              padding: "12px 16px",
              borderRadius: "28px",
              fontSize: "20px",
              color: "#E7E9EA",
            }}
          >
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#E7E9EA" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <span>Explore</span>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "20px",
              padding: "12px 16px",
              borderRadius: "28px",
              fontSize: "20px",
              color: "#E7E9EA",
            }}
          >
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#E7E9EA" strokeWidth="2">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
            </svg>
            <span>Notifications</span>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "20px",
              padding: "12px 16px",
              borderRadius: "28px",
              fontSize: "20px",
              color: "#E7E9EA",
            }}
          >
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#E7E9EA" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
            <span>Messages</span>
          </div>
        </div>

        {/* Profile Section */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            padding: "12px",
            borderRadius: "28px",
            cursor: "pointer",
            transition: "background 0.2s",
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              backgroundColor: "#2F3336",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            {profilePhotoUrl ? (
              <img
                src={profilePhotoUrl}
                alt="Profile"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <div
                style={{
                  fontSize: "20px",
                  color: "#71767B",
                }}
              >
                👤
              </div>
            )}
          </div>
          <div
            style={{
              flex: 1,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                fontSize: "15px",
                fontWeight: "700",
                color: "#E7E9EA",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {name}
            </div>
            <div
              style={{
                fontSize: "15px",
                color: "#71767B",
              }}
            >
              @{username}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Tweet Header */}
        <div
          style={{
            padding: "16px 24px",
            borderBottom: "1px solid #2F3336",
            display: "flex",
            alignItems: "center",
            gap: "20px",
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#E7E9EA" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          <div
            style={{
              fontSize: "20px",
              fontWeight: "700",
              color: "#E7E9EA",
            }}
          >
            Tweet
          </div>
        </div>

        {/* Tweet Content */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "16px 24px",
          }}
        >
          {/* User Info */}
          <div
            style={{
              display: "flex",
              gap: "12px",
              marginBottom: "12px",
            }}
          >
            <div
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "50%",
                backgroundColor: "#2F3336",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              {profilePhotoUrl ? (
                <img
                  src={profilePhotoUrl}
                  alt="Profile"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <div
                  style={{
                    fontSize: "24px",
                    color: "#71767B",
                  }}
                >
                  👤
                </div>
              )}
            </div>

            <div
              style={{
                flex: 1,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <div
                  style={{
                    fontSize: "17px",
                    fontWeight: "700",
                    color: "#E7E9EA",
                  }}
                >
                  {name}
                </div>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="#1D9BF0">
                  <path d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.71-3.998-3.818-3.998-.47 0-.92.084-1.336.25C14.818 2.415 13.51 1.5 12 1.5s-2.816.917-3.437 2.25c-.415-.165-.866-.25-1.336-.25-2.11 0-3.818 1.79-3.818 4 0 .494.083.964.237 1.4-1.272.65-2.147 2.018-2.147 3.6 0 1.495.782 2.798 1.942 3.486-.02.17-.032.34-.032.514 0 2.21 1.708 4 3.818 4 .47 0 .92-.086 1.335-.25.62 1.334 1.926 2.25 3.437 2.25 1.512 0 2.818-.916 3.437-2.25.415.163.865.248 1.336.248 2.11 0 3.818-1.79 3.818-4 0-.174-.012-.344-.033-.513 1.158-.687 1.943-1.99 1.943-3.484zm-6.616-3.334l-4.334 6.5c-.145.217-.382.334-.625.334-.143 0-.288-.04-.416-.126l-.115-.094-2.415-2.415c-.293-.293-.293-.768 0-1.06s.768-.294 1.06 0l1.77 1.767 3.825-5.74c.23-.345.696-.436 1.04-.207.346.23.44.696.21 1.04z" />
                </svg>
                <span
                  style={{
                    fontSize: "15px",
                    color: "#71767B",
                  }}
                >
                  @{username}
                </span>
                <span
                  style={{
                    fontSize: "15px",
                    color: "#71767B",
                  }}
                >
                  · 2m
                </span>
              </div>
            </div>
          </div>

          {/* Tweet Text */}
          <div
            style={{
              fontSize: "23px",
              lineHeight: "1.3",
              color: "#E7E9EA",
              marginBottom: "16px",
              whiteSpace: "pre-wrap",
            }}
          >
            {tweetContent}
          </div>

          {/* Skills and Info Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "16px",
              marginBottom: "16px",
            }}
          >
            {/* Skills Progress */}
            {topSkills.length > 0 && (
              <div
                style={{
                  padding: "16px",
                  background: "#16181C",
                  borderRadius: "16px",
                  border: "1px solid #2F3336",
                }}
              >
                <div
                  style={{
                    fontSize: "15px",
                    fontWeight: "700",
                    color: "#E7E9EA",
                    marginBottom: "12px",
                  }}
                >
                  Top Skills
                </div>
                {topSkills.slice(0, 3).map((skill, idx) => {
                  const percentage = 100 - idx * 5;
                  return (
                    <div
                      key={idx}
                      style={{
                        marginBottom: idx < 2 ? "12px" : "0",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "6px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "14px",
                            color: "#E7E9EA",
                          }}
                        >
                          {skill}
                        </div>
                        <div
                          style={{
                            fontSize: "14px",
                            color: "#1D9BF0",
                            fontWeight: "700",
                          }}
                        >
                          {percentage}%
                        </div>
                      </div>
                      <div
                        style={{
                          width: "100%",
                          height: "8px",
                          background: "#2F3336",
                          borderRadius: "4px",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: `${percentage}%`,
                            height: "100%",
                            background: "#1D9BF0",
                            borderRadius: "4px",
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Experience */}
            {latestExperiences.length > 0 && (
              <div
                style={{
                  padding: "16px",
                  background: "#16181C",
                  borderRadius: "16px",
                  border: "1px solid #2F3336",
                }}
              >
                <div
                  style={{
                    fontSize: "15px",
                    fontWeight: "700",
                    color: "#E7E9EA",
                    marginBottom: "12px",
                  }}
                >
                  💼 Experience
                </div>
                {latestExperiences.slice(0, 2).map((exp, idx) => (
                  <div
                    key={idx}
                    style={{
                      marginBottom: idx < 1 ? "12px" : "0",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#E7E9EA",
                      }}
                    >
                      {exp.title}
                    </div>
                    <div
                      style={{
                        fontSize: "13px",
                        color: "#71767B",
                      }}
                    >
                      {exp.company} · {exp.startDate} - {exp.endDate || "Present"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Education */}
          {latestEducation.length > 0 && (
            <div
              style={{
                padding: "16px",
                background: "#16181C",
                borderRadius: "16px",
                border: "1px solid #2F3336",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  fontSize: "15px",
                  fontWeight: "700",
                  color: "#E7E9EA",
                  marginBottom: "12px",
                }}
              >
                🎓 Education
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "12px",
                }}
              >
                {latestEducation.map((edu, idx) => (
                  <div key={idx}>
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#E7E9EA",
                      }}
                    >
                      {edu.degree || "Degree"}
                    </div>
                    <div
                      style={{
                        fontSize: "13px",
                        color: "#71767B",
                      }}
                    >
                      {edu.school}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Timestamp */}
          <div
            style={{
              fontSize: "15px",
              color: "#71767B",
              marginBottom: "16px",
              paddingBottom: "16px",
              borderBottom: "1px solid #2F3336",
            }}
          >
            {timestamp.time} · {timestamp.date} ·{" "}
            <span style={{ fontWeight: "700", color: "#E7E9EA" }}>{(views / 1000000).toFixed(1)}M</span> Views
          </div>

          {/* Engagement Stats */}
          <div
            style={{
              display: "flex",
              gap: "24px",
              paddingBottom: "16px",
              borderBottom: "1px solid #2F3336",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: "6px",
                alignItems: "baseline",
              }}
            >
              <div
                style={{
                  fontSize: "15px",
                  fontWeight: "700",
                  color: "#E7E9EA",
                }}
              >
                {(retweets / 1000).toFixed(1)}K
              </div>
              <div
                style={{
                  fontSize: "15px",
                  color: "#71767B",
                }}
              >
                Retweets
              </div>
            </div>
            <div
              style={{
                display: "flex",
                gap: "6px",
                alignItems: "baseline",
              }}
            >
              <div
                style={{
                  fontSize: "15px",
                  fontWeight: "700",
                  color: "#E7E9EA",
                }}
              >
                {(quotes / 1000).toFixed(1)}K
              </div>
              <div
                style={{
                  fontSize: "15px",
                  color: "#71767B",
                }}
              >
                Quotes
              </div>
            </div>
            <div
              style={{
                display: "flex",
                gap: "6px",
                alignItems: "baseline",
              }}
            >
              <div
                style={{
                  fontSize: "15px",
                  fontWeight: "700",
                  color: "#E7E9EA",
                }}
              >
                {(likes / 1000).toFixed(1)}K
              </div>
              <div
                style={{
                  fontSize: "15px",
                  color: "#71767B",
                }}
              >
                Likes
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-around",
              paddingTop: "8px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                color: "#71767B",
                cursor: "pointer",
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
              </svg>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                color: "#71767B",
                cursor: "pointer",
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="17 1 21 5 17 9" />
                <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                <polyline points="7 23 3 19 7 15" />
                <path d="M21 13v2a4 4 0 0 1-4 4H3" />
              </svg>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                color: "#71767B",
                cursor: "pointer",
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                color: "#71767B",
                cursor: "pointer",
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                <polyline points="16 6 12 2 8 6" />
                <line x1="12" y1="2" x2="12" y2="15" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Right Sidebar - Trends */}
      <div
        style={{
          width: "350px",
          background: "#000000",
          borderLeft: "1px solid #2F3336",
          padding: "20px",
          overflowY: "auto",
        }}
      >
        <div
          style={{
            background: "#16181C",
            borderRadius: "16px",
            padding: "16px",
          }}
        >
          <div
            style={{
              fontSize: "20px",
              fontWeight: "700",
              color: "#E7E9EA",
              marginBottom: "16px",
            }}
          >
            What's happening
          </div>

          <div
            style={{
              marginBottom: "16px",
              paddingBottom: "16px",
              borderBottom: "1px solid #2F3336",
            }}
          >
            <div
              style={{
                fontSize: "13px",
                color: "#71767B",
                marginBottom: "2px",
              }}
            >
              Trending in Tech
            </div>
            <div
              style={{
                fontSize: "15px",
                fontWeight: "700",
                color: "#E7E9EA",
                marginBottom: "2px",
              }}
            >
              #{topSkills[0] || "Technology"}
            </div>
            <div
              style={{
                fontSize: "13px",
                color: "#71767B",
              }}
            >
              {Math.floor(Math.random() * 50 + 10)}K posts
            </div>
          </div>

          <div
            style={{
              marginBottom: "16px",
              paddingBottom: "16px",
              borderBottom: "1px solid #2F3336",
            }}
          >
            <div
              style={{
                fontSize: "13px",
                color: "#71767B",
                marginBottom: "2px",
              }}
            >
              Technology · Trending
            </div>
            <div
              style={{
                fontSize: "15px",
                fontWeight: "700",
                color: "#E7E9EA",
                marginBottom: "2px",
              }}
            >
              {currentTitle}
            </div>
            <div
              style={{
                fontSize: "13px",
                color: "#71767B",
              }}
            >
              {Math.floor(Math.random() * 30 + 5)}K posts
            </div>
          </div>

          <div>
            <div
              style={{
                fontSize: "13px",
                color: "#71767B",
                marginBottom: "2px",
              }}
            >
              Careers · Trending
            </div>
            <div
              style={{
                fontSize: "15px",
                fontWeight: "700",
                color: "#E7E9EA",
                marginBottom: "2px",
              }}
            >
              Now Hiring
            </div>
            <div
              style={{
                fontSize: "13px",
                color: "#71767B",
              }}
            >
              {Math.floor(Math.random() * 100 + 50)}K posts
            </div>
          </div>
        </div>

        <div
          style={{
            marginTop: "16px",
            fontSize: "13px",
            color: "#71767B",
            textAlign: "center",
          }}
        >
          Made with <span style={{ color: "#1D9BF0", fontWeight: "700" }}>upresume.ai</span>
        </div>
      </div>
    </div>
  );
};

// Export function to download card as image
export const downloadCardAsImage = async (cardRef, cardTitle) => {
  if (!cardRef.current) {
    console.error("Card reference not found");
    return;
  }

  try {
    const canvas = await html2canvas(cardRef.current, {
      backgroundColor: null,
      scale: 2,
      logging: false,
      useCORS: true,
      allowTaint: true,
    });

    // Convert to blob and download
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${cardTitle}_Upresume.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, "image/png");
  } catch (error) {
    console.error("Error generating card image:", error);
    throw error;
  }
};
